<template>
  <div class="linha-map-editor d-flex flex-column fill-height">
    <v-progress-linear v-if="loading || fenceDragRecalculating" indeterminate color="primary" class="mb-1 flex-grow-0" />

    <v-alert v-if="!loading && mensagemVazia" type="info" dense outlined class="flex-grow-0 mb-1 mx-1">
      {{ mensagemVazia }}
    </v-alert>

    <div class="linha-map-editor__body d-flex flex-grow-1">
      <v-navigation-drawer
        v-if="isLinhasContext"
        v-model="drawerRotasOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="360"
        class="linha-map-editor__drawer linha-map-editor__drawer--list"
      >
        <div class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
            <v-text-field
              v-model="searchDrawerRotas"
              append-icon="mdi-magnify"
              :label="$t('crudLinhas.search')"
              single-line
              hide-details
              dense
              clearable
            />
            <v-btn icon small class="ml-1" @click="drawerRotasOpen = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="selecaoTotal > 1"
            class="linha-map-editor__drawer-pager d-flex align-center flex-grow-0 px-2 py-1"
          >
            <v-btn
              icon
              small
              :disabled="selecaoPage <= 0"
              :title="$t('crudLinhas.detail.parameters.previous')"
              @click="$emit('update:selecaoPage', selecaoPage - 1)"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="flex-grow-1 text-center px-2 text-truncate">
              <span class="caption font-weight-medium">
                {{ $t('crudLinhas.detail.parameters.pager', { current: selecaoPage + 1, total: selecaoTotal }) }}
              </span>
              <span v-if="selecaoLabel" class="caption grey--text"> — {{ selecaoLabel }}</span>
            </div>
            <v-btn
              icon
              small
              :disabled="selecaoPage >= selecaoTotal - 1"
              :title="$t('crudLinhas.detail.parameters.next')"
              @click="$emit('update:selecaoPage', selecaoPage + 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="linha-map-editor__drawer-list flex-grow-1">
            <v-list dense>
              <v-list-item
                v-for="r in rotasDrawerFiltradas"
                :key="'rl-drawer-' + r.id_rota + '-' + r.id_sentido"
                :class="{ 'primary--text': isRouteHighlightedOnMap(r.id_rota) }"
                @click="onDrawerRotaClick(r.id_rota)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ r.nome_rota || r.cod_rota || r.id_rota }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ r.cod_rota || '—' }} · {{ r.descricao_tipo_rota || r.id_tipo_rota }} · {{ r.descricao_sentido || r.id_sentido }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="canEdit">
                  <v-btn icon small :loading="savingRotas" @click.stop="removeRotaAssociacao(r)">
                    <v-icon small color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!rotasDrawerFiltradas.length">
                <v-list-item-content>
                  <v-list-item-title class="text--secondary text-body-2">
                    {{ $t('crudLinhas.map.noGeometry') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-if="showGenerateReturnFromOutbound" class="linha-map-editor__drawer-footer flex-grow-0 px-2 pb-2 pt-1">
            <v-btn
              color="primary"
              small
              block
              outlined
              :disabled="savingCercas || roteirizandoPorCercas"
              :loading="savingCercas || roteirizandoPorCercas"
              @click="gerarCercasVolta"
            >
              <v-icon small left>mdi-swap-horizontal</v-icon>
              {{ $t('crudLinhas.map.generateReturnFromOutbound') }}
            </v-btn>
          </div>
        </div>
      </v-navigation-drawer>

      <v-navigation-drawer
        v-if="isLinhasContext"
        v-model="drawerCercasOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="360"
        class="linha-map-editor__drawer linha-map-editor__drawer--list"
      >
        <div ref="fencesSidePanel" class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
            <v-text-field
              v-model="searchDrawerCercas"
              append-icon="mdi-magnify"
              :label="$t('crudLinhas.search')"
              single-line
              hide-details
              dense
              clearable
            />
            <v-btn icon small class="ml-1" @click="drawerCercasOpen = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="selecaoTotal > 1"
            class="linha-map-editor__drawer-pager d-flex align-center flex-grow-0 px-2 py-1"
          >
            <v-btn
              icon
              small
              :disabled="selecaoPage <= 0"
              :title="$t('crudLinhas.detail.parameters.previous')"
              @click="$emit('update:selecaoPage', selecaoPage - 1)"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="flex-grow-1 text-center px-2 text-truncate">
              <span class="caption font-weight-medium">
                {{ $t('crudLinhas.detail.parameters.pager', { current: selecaoPage + 1, total: selecaoTotal }) }}
              </span>
              <span v-if="selecaoLabel" class="caption grey--text"> — {{ selecaoLabel }}</span>
            </div>
            <v-btn
              icon
              small
              :disabled="selecaoPage >= selecaoTotal - 1"
              :title="$t('crudLinhas.detail.parameters.next')"
              @click="$emit('update:selecaoPage', selecaoPage + 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="linha-map-editor__drawer-list flex-grow-1">
            <div
              v-if="canEdit && activeFormatTargetCerca"
              class="linha-map-editor__fence-format-panel pa-3"
            >
              <div class="subtitle-2 mb-1 text-truncate">
                {{ activeFormatTargetLabel || $t('geofence.format') }}
              </div>
              <div class="caption grey--text mb-1">{{ $t('geofence.format') }}</div>
              <v-radio-group
                :value="fenceEditBarFormato === 'circular' ? 1 : 0"
                row
                dense
                class="mt-0 mb-0"
                hide-details
                :disabled="fenceDragRecalculating"
                @change="onFenceDrawerFormatoRadio"
              >
                <v-radio :label="$t('geofence.formatCircular')" :value="1" />
                <v-radio :label="$t('geofence.formatPolygon')" :value="0" />
              </v-radio-group>
              <div class="caption grey--text mt-2">
                {{ fenceEditBarHint }}
              </div>
            </div>
            <v-alert
              v-else-if="canEdit && editMode === 'editFences'"
              dense
              text
              type="info"
              class="ma-2 caption"
            >
              {{ $t('crudLinhas.map.fenceFormatSelectHint') }}
            </v-alert>
            <merus-data-table
              v-if="reorderFencesMode"
              :headers="headersCercasReorder"
              :items="cercasReorderItems"
              item-key="_reorderKey"
              disable-pagination
              hide-default-footer
              dense
              hide-columns-button
              hide-configure-columns-button
              hide-search
              class="elevation-7 linha-map-editor__cercas-reorder-table"
              :locale="$i18n.locale"
              style="border-radius: 10px; margin: 10px 8px"
              @click:row="onCercaReorderRowClick"
            >
              <template v-slot:toolbar>
                <div class="subtitle-2 pa-2">{{ $t('crudLinhas.map.controls.changeFenceOrder') }}</div>
              </template>
              <template v-slot:item.nome_cerca="{ item }">
                {{ item.nome_cerca || item.cod_cerca || item.id_cerca }}
              </template>
              <template v-slot:item.order="{ item }">
                <v-btn
                  icon
                  small
                  color="green"
                  :disabled="!canMoveCercaUpInReorder(item)"
                  @click.stop="ordenarCerca(item, 'up')"
                >
                  <v-icon>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="red"
                  :disabled="!canMoveCercaDownInReorder(item)"
                  @click.stop="ordenarCerca(item, 'down')"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
            </merus-data-table>
            <v-list v-else dense>
              <v-list-item
                v-for="c in cercasDrawerFiltradas"
                :key="'cl-drawer-' + c.id_cerca + '-' + c.id_sentido"
                :class="{
                  'linha-map-editor__fence-item--selected':
                    editingCercaId != null && Number(editingCercaId) === Number(c.id_cerca),
                }"
                @click="onCercaDrawerItemClick(c)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ c.nome_cerca || c.cod_cerca || c.id_cerca }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t('crudLinhas.form.fences.order') }}: {{ c.ordem_parada != null ? c.ordem_parada : '—' }}
                    · {{ c.descricao_sentido || c.id_sentido }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="d-flex align-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" x-small class="mr-1">mdi-clock-outline</v-icon>
                      </template>
                      <span>{{ $t('crudLinhas.headers.fences.arrivalTime') }}</span>
                    </v-tooltip>
                    {{ c.tempo_chegada_minutos != null ? c.tempo_chegada_minutos : '—' }} min
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="canEdit">
                  <v-btn icon small :loading="savingCercas" @click.stop="removeCercaAssociacao(c)">
                    <v-icon small color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!cercasDrawerFiltradas.length">
                <v-list-item-content>
                  <v-list-item-title class="text--secondary text-body-2">
                    {{ $t('crudLinhas.map.noGeometry') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </v-navigation-drawer>

      <v-navigation-drawer
        v-if="isUnidadesPreview"
        v-model="drawerLinhasOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="360"
        class="linha-map-editor__drawer linha-map-editor__drawer--list"
      >
        <div class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
            <v-toolbar-title class="subtitle-2">
              {{ $t('crudUnidades.lines.title') }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="drawerLinhasOpen = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="drawerLinhasUnits.length > 1"
            class="linha-map-editor__drawer-pager d-flex align-center flex-grow-0 px-2 py-1"
          >
            <v-btn
              icon
              small
              :disabled="drawerLinhasPage <= 0"
              :title="$t('crudLinhas.detail.parameters.previous')"
              @click="setDrawerLinhasPage(drawerLinhasPage - 1)"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="flex-grow-1 text-center px-2 text-truncate">
              <span class="caption font-weight-medium">
                {{ $t('crudLinhas.detail.parameters.pager', {
                  current: drawerLinhasPage + 1,
                  total: drawerLinhasUnits.length,
                }) }}
              </span>
              <span v-if="drawerLinhasUnitLabel" class="caption grey--text">
                — {{ drawerLinhasUnitLabel }}
              </span>
            </div>
            <v-btn
              icon
              small
              :disabled="drawerLinhasPage >= drawerLinhasUnits.length - 1"
              :title="$t('crudLinhas.detail.parameters.next')"
              @click="setDrawerLinhasPage(drawerLinhasPage + 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="linha-map-editor__drawer-list flex-grow-1 d-flex flex-column">
            <unidade-linhas-table
              ref="unidadeLinhasDrawer"
              :unidade="drawerLinhasUnidadeAtual"
              :token="token"
              :can-edit="canEdit"
              @changed="onUnidadeLinhasDrawerChanged"
              @select-line="onUnidadeLinhaSelect"
            />
          </div>
        </div>
      </v-navigation-drawer>

      <v-navigation-drawer
        v-if="isCercasPreview"
        v-model="drawerCercaLinhasOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="360"
        class="linha-map-editor__drawer linha-map-editor__drawer--list"
      >
        <div class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
            <v-toolbar-title class="subtitle-2">
              {{ $t('crudLinhas.map.controls.cercaLinesDrawer') }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="drawerCercaLinhasOpen = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="drawerCercaLinhasItems.length > 1"
            class="linha-map-editor__drawer-pager d-flex align-center flex-grow-0 px-2 py-1"
          >
            <v-btn
              icon
              small
              :disabled="drawerCercaLinhasPage <= 0"
              :title="$t('crudLinhas.detail.parameters.previous')"
              @click="setDrawerCercaLinhasPage(drawerCercaLinhasPage - 1)"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="flex-grow-1 text-center px-2 text-truncate">
              <span class="caption font-weight-medium">
                {{ $t('crudLinhas.detail.parameters.pager', {
                  current: drawerCercaLinhasPage + 1,
                  total: drawerCercaLinhasItems.length,
                }) }}
              </span>
              <span v-if="drawerCercaLinhasLabel" class="caption grey--text">
                — {{ drawerCercaLinhasLabel }}
              </span>
            </div>
            <v-btn
              icon
              small
              :disabled="drawerCercaLinhasPage >= drawerCercaLinhasItems.length - 1"
              :title="$t('crudLinhas.detail.parameters.next')"
              @click="setDrawerCercaLinhasPage(drawerCercaLinhasPage + 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="linha-map-editor__drawer-list flex-grow-1 d-flex flex-column">
            <cerca-linhas-table
              :cerca="drawerCercaLinhasAtual"
              :token="token"
              v-model="cercaLinhasSelecionadas"
            />
          </div>
        </div>
      </v-navigation-drawer>

      <v-navigation-drawer
        v-if="showCreateEntityDrawer"
        v-model="drawerCreateOpen"
        absolute
        left
        temporary
        hide-overlay
        stateless
        width="400"
        class="linha-map-editor__drawer linha-map-editor__drawer--list"
      >
        <div class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
          <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
            <v-toolbar-title class="subtitle-2">
              {{ createDrawerTitle }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="closeCreateDrawer">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="isLinhasContext && selecaoTotal > 1"
            class="linha-map-editor__drawer-pager d-flex align-center flex-grow-0 px-2 py-1"
          >
            <v-btn
              icon
              small
              :disabled="selecaoPage <= 0"
              :title="$t('crudLinhas.detail.parameters.previous')"
              @click="$emit('update:selecaoPage', selecaoPage - 1)"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="flex-grow-1 text-center px-2 text-truncate">
              <span class="caption font-weight-medium">
                {{ $t('crudLinhas.detail.parameters.pager', { current: selecaoPage + 1, total: selecaoTotal }) }}
              </span>
              <span v-if="selecaoLabel" class="caption grey--text"> — {{ selecaoLabel }}</span>
            </div>
            <v-btn
              icon
              small
              :disabled="selecaoPage >= selecaoTotal - 1"
              :title="$t('crudLinhas.detail.parameters.next')"
              @click="$emit('update:selecaoPage', selecaoPage + 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div class="linha-map-editor__drawer-list flex-grow-1">
            <slot name="create-entity" />
          </div>
        </div>
      </v-navigation-drawer>

      <div
        class="linha-map-editor__map flex-grow-1"
        :class="{ 'linha-map-editor__map--picking': pickLocationMode || editMode === 'drawFence' || editMode === 'drawRoute' }"
      >
        <l-map
          ref="mapRef"
          :key="mapKey"
          :zoom="zoom"
          :center="currentCenter"
          :options="mapOptions"
          style="height: 100%; width: 100%; border-radius: 8px"
          @ready="onMapReady"
        >
          <l-tile-layer :url="url" :attribution="attribution" />
          <l-control v-if="!isEntityPreview" :position="'topright'" class="text-overlay">
            <v-row>
              <v-col cols="12" sm="3" md="3">
                <v-checkbox v-model="rotasVisible" :label="$t('crudLinhas.map.showRoutes')" />
              </v-col>
              <v-col cols="12" sm="3" md="4">
                <v-checkbox v-model="cercasVisible" :label="$t('crudLinhas.map.showFences')" />
              </v-col>
              <v-col cols="12" sm="3" md="5">
                <v-checkbox v-model="passageirosVisible" :label="$t('crudLinhas.map.showPassengers')" />
              </v-col>
            </v-row>
          </l-control>
          <l-control v-if="isUnidadesPreview" :position="'topright'" class="text-overlay">
            <v-checkbox
              v-model="linhasAssociadasVisible"
              :label="$t('crudLinhas.map.controls.showAssociatedLines')"
              class="ma-0"
              hide-details
              dense
            />
          </l-control>
          <l-geo-json
            v-for="(rota, i) in mapRotas"
            :key="`rota-${rota.id_rota}-${i}-${rota._rev || 0}`"
            :geojson="rota.geojson"
            :options-style="rota.style"
            :options="rota.options"
            :visible="mapRotasVisible"
          />
          <l-geo-json
            v-for="(rota, i) in mapCercaLinhasSelecionadasRotas"
            :key="`cerca-linha-rota-${rota.id_rota}-${i}`"
            :geojson="rota.geojson"
            :options-style="rota.style"
            :options="rota.options"
            :visible="true"
          />
          <l-geo-json
            v-if="previewRotaGeojson"
            :key="`preview-rota-${previewRotaGeojson.id_rota}`"
            :geojson="previewRotaGeojson.geojson"
            :options-style="previewRotaGeojson.style"
            :options="previewRotaGeojson.options"
            :visible="true"
          />
          <l-geo-json
            v-for="(cerca, w) in mapCercasGeojson"
            :key="`cerca-${cerca.id_cerca}-${w}-${previewLayerRev}`"
            :geojson="cerca.geojson"
            :options="cerca.options"
            :options-style="cerca.style"
            :visible="cercasVisible"
          />
          <l-circle
            v-for="(cerca, w) in mapCercasCirculares"
            :key="`cerca-circ-${cerca.id_cerca}-${w}-${previewLayerRev}`"
            :lat-lng="latLng(cerca.lat_centro, cerca.lng_centro)"
            :radius="Number(cerca.raio_mts) || 0"
            :color="cerca._strokeColor"
            :fill-color="cerca._fillColor"
            :fill-opacity="0.25"
            :weight="2"
            :visible="cercasVisible"
            @ready="onCircularCercaLayerReady(cerca, $event)"
          />
          <!-- Prévia da cerca selecionada / anexada localmente (ainda não salva na linha) -->
          <l-geo-json
            v-for="(cerca, w) in previewCercasGeojson"
            :key="`preview-cerca-${cerca.id_cerca}-${w}`"
            :geojson="cerca.geojson"
            :options="cerca.options"
            :options-style="cerca.style"
            :visible="true"
          />
          <l-circle
            v-for="(cerca, w) in previewCercasCirculares"
            :key="`preview-cerca-circ-${cerca.id_cerca}-${w}`"
            :lat-lng="latLng(cerca.lat_centro, cerca.lng_centro)"
            :radius="Number(cerca.raio_mts) || 0"
            :color="cerca._strokeColor"
            :fill-color="cerca._fillColor"
            :fill-opacity="0.2"
            :weight="2"
            :dash-array="'8 6'"
            :visible="true"
            @ready="onPreviewCircularCercaLayerReady(cerca, $event)"
          />
          <l-marker
            v-for="(pin, p) in previewCercaMarkers"
            :key="`preview-cerca-pin-${pin.id_cerca}-${p}`"
            :lat-lng="latLng(pin.lat, pin.lng)"
            :icon="iconPinCerca(pin.color)"
            :visible="true"
            :z-index-offset="30"
          >
            <l-tooltip v-if="pin.nome" :content="pin.nome" :options="{ direction: 'top', offset: [0, -28] }" />
          </l-marker>
          <l-marker
            v-for="(pin, p) in mapCercaMarkers"
            :key="`cerca-pin-${pin.id_cerca}-${p}-${editMode}`"
            :lat-lng="latLng(pin.lat, pin.lng)"
            :icon="iconPinCerca(pin.color)"
            :visible="cercasVisible"
            :draggable="isCercaPinDraggable(pin)"
            :z-index-offset="10"
            @click="onCercaPinClick(pin)"
            @dragstart="onCercaPinDragStart(pin)"
            @dragend="onCercaPinDragend(pin, $event)"
          >
            <l-tooltip v-if="pin.nome" :content="pin.nome" :options="{ direction: 'top', offset: [0, -28] }" />
          </l-marker>
          <l-marker
            v-for="h in fenceEditHandles"
            :key="`fence-handle-${h.id_cerca}-${h.kind}-${h.index}`"
            :lat-lng="latLng(h.lat, h.lng)"
            :icon="iconFenceHandle(h.kind)"
            :draggable="true"
            :visible="true"
            :z-index-offset="40"
            @dragstart="selectFenceForFormatEdit(h.id_cerca)"
            @drag="onFenceHandleDrag(h, $event)"
            @dragend="onFenceHandleDragend(h, $event)"
          >
            <l-tooltip
              :content="h.kind === 'radius' ? $t('crudLinhas.map.fenceRadiusTooltip') : $t('crudLinhas.map.fenceVertexTooltip')"
              :options="{ direction: 'top', offset: [0, -10] }"
            />
          </l-marker>
          <l-marker
            v-for="h in editRouteVertexHandles"
            :key="`route-vertex-${activeEditRouteId}-${h.index}`"
            :lat-lng="latLng(h.lat, h.lng)"
            :icon="iconRouteVertexHandle(h.isEnd)"
            :draggable="canEdit && !editRecalculating"
            :visible="true"
            :z-index-offset="46"
            @dragstart="onRouteVertexDragStart(h)"
            @dragend="onRouteVertexDragend(h, $event)"
          >
            <l-tooltip
              :content="$t('crudLinhas.map.routeVertexTooltip')"
              :options="{ direction: 'top', offset: [0, -10] }"
            />
          </l-marker>
          <!-- Sobreposição somente leitura das demais linhas marcadas na tabela -->
          <l-geo-json
            v-for="(rota, oi) in outrasLinhasMapRotas"
            :key="`other-linha-rota-${rota.id_rota}-${oi}`"
            :geojson="rota.geojson"
            :options-style="rota.style"
            :options="rota.options"
            :visible="rotasVisible"
          />
          <l-geo-json
            v-for="(cerca, ow) in outrasLinhasMapCercasGeojson"
            :key="`other-linha-cerca-${cerca.id_cerca}-${ow}`"
            :geojson="cerca.geojson"
            :options="cerca.options"
            :options-style="cerca.style"
            :visible="cercasVisible"
          />
          <l-circle
            v-for="(cerca, ow) in outrasLinhasMapCercasCirculares"
            :key="`other-linha-cerca-circ-${cerca.id_cerca}-${ow}`"
            :lat-lng="latLng(cerca.lat_centro, cerca.lng_centro)"
            :radius="Number(cerca.raio_mts) || 0"
            :color="cerca._strokeColor"
            :fill-color="cerca._fillColor"
            :fill-opacity="0.18"
            :weight="2"
            :dash-array="'6 4'"
            :visible="cercasVisible"
          />
          <l-marker
            v-for="(pin, op) in outrasLinhasMapCercaMarkers"
            :key="`other-linha-cerca-pin-${pin.id_cerca}-${op}`"
            :lat-lng="latLng(pin.lat, pin.lng)"
            :icon="iconPinCerca(pin.color)"
            :visible="cercasVisible"
            :z-index-offset="5"
          >
            <l-tooltip v-if="pin.nome" :content="pin.nome" :options="{ direction: 'top', offset: [0, -28] }" />
          </l-marker>
          <l-polyline
            v-for="link in passengerWalkConnectionLayers"
            :key="link._linkId"
            :lat-lngs="link.latLngs"
            :color="link.style.color"
            :weight="link.style.weight"
            :opacity="link.style.opacity"
            :dash-array="link.style.dashArray"
          />
          <l-marker
            v-for="(pax, pi) in mapPassageiros"
            :key="`pax-${pax.id_pessoa}-${pi}`"
            :lat-lng="latLng(pax.lat, pax.lng)"
            :icon="iconPinPassageiro(pax)"
            :visible="passageirosVisible"
            :z-index-offset="20"
            @click="onPassageiroMarkerClick(pax)"
          >
            <l-tooltip :content="pax.nome_completo || ''" :options="{ direction: 'top', offset: [0, -24] }" />
          </l-marker>
        </l-map>
        <recursos-operacao-map-controls
          v-if="showMapControls"
          :items="mapControlItems"
          :menu-items="mapControlMenuItems"
          :actions-label="$t('crudLinhas.map.controls.actions')"
          @select="onMapControlSelect"
        />
        <!-- Barra de edição in-place nos previews de rota/cerca (não há drawer aqui) -->
        <div
          v-if="previewEditBar"
          class="linha-map-editor__preview-editbar d-flex align-center flex-wrap"
          :style="previewEditBarStyle"
        >
          <template v-if="previewEditBar === 'route'">
            <v-chip
              x-small
              class="mr-2"
              color="warning"
              text-color="white"
              v-if="activeEditRouteId != null && (editWaypoints.length || editRouteGeometryDirty)"
            >
              <template v-if="editWaypoints.length">
                {{ $t('crudLinhas.map.editViaCount', { count: editWaypoints.length }) }}
              </template>
              <template v-if="editWaypoints.length && editRouteGeometryDirty"> · </template>
              <template v-if="editRouteGeometryDirty">
                {{ $t('crudLinhas.map.editRoutePending') }}
              </template>
            </v-chip>
            <span class="caption mr-2">
              <template v-if="activeEditRouteId == null">
                {{ $t('routePlanning.actions.editRouteSelectFirst') }}
              </template>
              <template v-else-if="editRouteRedrawing">
                {{ $t('crudLinhas.map.redrawRouteEditHint') }}
              </template>
              <template v-else>
                {{ $t('crudLinhas.map.previewRouteEditHint') }}
              </template>
            </span>
            <v-btn
              x-small
              color="warning"
              class="mr-1"
              :loading="editRecalculating"
              :disabled="
                activeEditRouteId == null ||
                editRecalculating ||
                (!editWaypoints.length && !editRouteGeometryDirty) ||
                (editRouteRedrawing && !(editRouteVertexHandles && editRouteVertexHandles.length >= 2))
              "
              @click="applyEditRoute"
            >{{ $t('crudLinhas.map.applyRouteEdit') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="
                editRecalculating ||
                (!editRouteRedrawing && !editWaypoints.length) ||
                (editRouteRedrawing && !(editRouteVertexHandles && editRouteVertexHandles.length))
              "
              @click="editRouteRedrawing ? undoLastRedrawRouteVertex() : undoLastVia()"
            >{{ $t('crudLinhas.map.undoVia') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="(!editWaypoints.length && !editRouteGeometryDirty) || editRecalculating"
              @click="resetEditRouteDraft"
            >{{ $t('button.clear') }}</v-btn>
            <v-btn
              x-small
              text
              color="error"
              class="mr-1"
              :disabled="activeEditRouteId == null || editRecalculating || !hasEditRouteTrace"
              @click="removeEditRouteTrace"
            >{{ $t('crudLinhas.map.removeRouteTrace') }}</v-btn>
          </template>
          <template v-else-if="previewEditBar === 'drawFence'">
            <v-chip x-small class="mr-2" color="warning" text-color="white">
              {{ $t('crudLinhas.map.drawFenceVertexCount', { count: drawFenceVertices.length }) }}
            </v-chip>
            <span class="caption mr-2">{{ $t('crudLinhas.map.drawFenceClickHint') }}</span>
            <v-btn
              x-small
              color="warning"
              class="mr-1"
              :disabled="drawFenceVertices.length < 3"
              @click="finalizeDrawFence"
            >{{ $t('crudLinhas.map.drawFenceFinish') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="!drawFenceVertices.length"
              @click="undoLastDrawFenceVertex"
            >{{ $t('crudLinhas.map.drawFenceUndo') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="!drawFenceVertices.length"
              @click="resetDrawFenceVertices"
            >{{ $t('button.clear') }}</v-btn>
          </template>
          <template v-else-if="previewEditBar === 'drawRoute'">
            <v-chip x-small class="mr-2" color="warning" text-color="white">
              {{ $t('crudLinhas.map.drawRouteVertexCount', { count: drawRouteVertices.length }) }}
            </v-chip>
            <span class="caption mr-2">{{ $t('crudLinhas.map.drawRouteClickHint') }}</span>
            <v-btn
              x-small
              color="warning"
              class="mr-1"
              :disabled="drawRouteVertices.length < 2"
              @click="finalizeDrawRoute"
            >{{ $t('crudLinhas.map.drawRouteFinish') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="!drawRouteVertices.length"
              @click="undoLastDrawRouteVertex"
            >{{ $t('crudLinhas.map.drawRouteUndo') }}</v-btn>
            <v-btn
              x-small
              text
              class="mr-1"
              :disabled="!drawRouteVertices.length"
              @click="resetDrawRouteVertices"
            >{{ $t('button.clear') }}</v-btn>
          </template>
          <template v-else>
            <v-progress-circular
              v-if="fenceDragRecalculating"
              indeterminate
              size="14"
              width="2"
              color="warning"
              class="mr-2"
            />
            <v-select
              v-if="fenceFormatSelectItems.length > 1"
              :value="editingCercaId"
              :items="fenceFormatSelectItems"
              item-text="text"
              item-value="value"
              dense
              hide-details
              outlined
              :label="$t('crudLinhas.form.fences.fence')"
              class="mr-2 linha-map-editor__fence-format-select"
              :disabled="fenceDragRecalculating"
              @change="onFenceFormatTargetSelect"
            />
            <span v-else-if="activeFormatTargetCerca" class="caption mr-2 font-weight-medium text-truncate" style="max-width: 160px;">
              {{ activeFormatTargetLabel }}
            </span>
            <span class="caption mr-2">{{ $t('geofence.format') }}:</span>
            <v-btn-toggle
              :value="fenceEditBarFormato"
              dense
              mandatory
              class="mr-2"
              color="warning"
              @change="onFenceEditBarFormatoChange"
            >
              <v-btn value="circular" x-small :disabled="fenceDragRecalculating || !canChangeFenceFormat">
                {{ $t('geofence.formatCircular') }}
              </v-btn>
              <v-btn value="polygon" x-small :disabled="fenceDragRecalculating || !canChangeFenceFormat">
                {{ $t('geofence.formatPolygon') }}
              </v-btn>
            </v-btn-toggle>
            <span class="caption mr-2 grey--text">{{ fenceEditBarHint }}</span>
          </template>
          <v-btn x-small text @click="editMode = 'view'">
            {{ $t('crudLinhas.map.exitEditMode') }}
          </v-btn>
        </div>
        <div
          v-if="cercaCreateSelectRoute"
          class="linha-map-editor__preview-editbar d-flex align-center flex-wrap"
          :style="previewEditBarStyle"
        >
          <v-icon small class="mr-2" color="primary">mdi-vector-polyline</v-icon>
          <span class="caption mr-2">{{ $t('crudLinhas.map.createFenceSelectRouteHint') }}</span>
        </div>
        <div
          v-if="pickLocationMode"
          class="linha-map-editor__preview-editbar d-flex align-center flex-wrap"
          :style="previewEditBarStyle"
        >
          <v-icon small class="mr-2" color="warning">mdi-map-marker-plus</v-icon>
          <span class="caption mr-2">{{ $t('crudLinhas.map.pickLocationHint') }}</span>
          <v-btn x-small text @click="setPickLocationMode(false)">
            {{ $t('crudLinhas.map.exitEditMode') }}
          </v-btn>
        </div>
      </div>
    </div>

    <v-navigation-drawer
      v-if="isLinhasContext"
      v-model="drawerMoveFenceOpen"
      absolute
      right
      temporary
      hide-overlay
      stateless
      width="380"
      class="linha-map-editor__drawer linha-map-editor__drawer--move-fence"
    >
      <div class="linha-map-editor__drawer-inner d-flex flex-column fill-height">
        <v-toolbar flat dense class="flex-grow-0 linha-map-editor__drawer-toolbar">
          <v-toolbar-title class="subtitle-2">
            {{ $t('crudLinhas.map.moveFenceTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon small :disabled="movendoCerca" @click="closeMoveFenceDrawer">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-stepper
          v-model="moveFenceStep"
          flat
          class="linha-map-editor__move-fence-stepper flex-grow-1 d-flex flex-column"
        >
          <v-stepper-header class="flex-grow-0 px-2">
            <v-stepper-step :complete="moveFenceStep > 1" step="1" editable>
              {{ $t('crudLinhas.map.moveFenceStepFence') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="moveFenceStep > 2" step="2" editable>
              {{ $t('crudLinhas.map.moveFenceStepDestination') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="3" editable>
              {{ $t('crudLinhas.map.moveFenceStepConfirm') }}
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items class="linha-map-editor__move-fence-stepper-items flex-grow-1">
            <v-stepper-content step="1" class="pa-3">
              <p class="text-body-2 mb-2">
                <span class="caption grey--text">{{ $t('crudLinhas.map.moveFenceSourceLine') }}:</span>
                {{ moveFenceOriginLabel || '—' }}
              </p>
              <p class="text-body-2 mb-3">{{ $t('crudLinhas.map.moveFenceStepFenceHint') }}</p>
              <v-progress-linear v-if="moveFenceLoadingSource" indeterminate color="primary" class="mb-2" />
              <v-list v-else dense class="linha-map-editor__move-fence-list py-0">
                <v-list-item
                  v-for="cerca in moveFenceCercasElegiveis"
                  :key="'move-fence-' + cerca.id_cerca + '-' + cerca.id_sentido"
                  :class="{ 'primary--text': isMoveFenceSourceSelected(cerca) }"
                  @click="selectMoveFenceCerca(cerca)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="orange darken-2">mdi-map-marker-radius</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ cerca._moveLabel }}</v-list-item-title>
                    <v-list-item-subtitle v-if="cerca.descricao_tipo_cerca">
                      {{ cerca.descricao_tipo_cerca }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="isMoveFenceSourceSelected(cerca)">
                    <v-icon small color="primary">mdi-check-circle</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="!moveFenceCercasElegiveis.length && !moveFenceLoadingSource">
                  <v-list-item-content>
                    <v-list-item-title class="text--secondary text-body-2">
                      {{ $t('crudLinhas.map.moveFenceNoEligibleFences') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-stepper-content>

            <v-stepper-content step="2" class="pa-3">
              <p class="text-body-2 mb-3">{{ $t('crudLinhas.map.moveFenceStepDestinationHint') }}</p>
              <v-list dense class="linha-map-editor__move-fence-list py-0">
                <v-list-item
                  v-for="linhaDest in moveFenceLinhasDestinoOpcoes"
                  :key="'move-fence-dest-' + linhaDest.id_linha"
                  :class="{ 'primary--text': Number(moveFenceTargetLinha) === Number(linhaDest.id_linha) }"
                  @click="selectMoveFenceDestLinha(linhaDest.id_linha, linhaDest._moveLabel)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="primary">mdi-routes</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ linhaDest._moveLabel }}</v-list-item-title>
                    <v-list-item-subtitle v-if="linhaDest.cod_linha && linhaDest.nome_linha">
                      {{ linhaDest.cod_linha }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="Number(moveFenceTargetLinha) === Number(linhaDest.id_linha)">
                    <v-icon small color="primary">mdi-check-circle</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="!moveFenceLinhasDestinoOpcoes.length">
                  <v-list-item-content>
                    <v-list-item-title class="text--secondary text-body-2">
                      {{ $t('crudLinhas.map.moveFenceNeedTwoLinesOnMap') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-stepper-content>

            <v-stepper-content step="3" class="pa-3">
              <v-alert type="info" dense outlined class="mb-3 text-body-2">
                {{ $t('crudLinhas.map.moveFenceStepConfirmHint') }}
              </v-alert>
              <v-card outlined class="mb-2">
                <v-card-text class="py-2 px-3">
                  <div class="caption grey--text mb-1">{{ $t('crudLinhas.map.moveFenceSourceLine') }}</div>
                  <div class="body-2">{{ moveFenceOriginLabel || '—' }}</div>
                </v-card-text>
              </v-card>
              <v-card outlined class="mb-2">
                <v-card-text class="py-2 px-3">
                  <div class="caption grey--text mb-1">{{ $t('crudLinhas.map.moveFenceSourceFence') }}</div>
                  <div class="body-2">{{ moveFenceSourceLabel || '—' }}</div>
                </v-card-text>
              </v-card>
              <v-card outlined class="mb-2">
                <v-card-text class="py-2 px-3">
                  <div class="caption grey--text mb-1">{{ $t('crudLinhas.map.moveFenceTargetLine') }}</div>
                  <div class="body-2">{{ moveFenceDestLabel || '—' }}</div>
                </v-card-text>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <div class="linha-map-editor__drawer-footer flex-grow-0 pa-3 d-flex align-center">
          <v-btn
            text
            small
            :disabled="movendoCerca || moveFenceStep <= 1"
            @click="moveFenceGoBack"
          >
            {{ $t('button.back') }}
          </v-btn>
          <v-spacer />
          <v-btn text small :disabled="movendoCerca" @click="closeMoveFenceDrawer">
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            v-if="moveFenceStep < 3"
            color="primary"
            small
            class="ml-2 white--text"
            :disabled="!canAdvanceMoveFenceStep"
            @click="moveFenceGoNext"
          >
            {{ $t('button.next') }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            small
            class="ml-2 white--text"
            :loading="movendoCerca"
            :disabled="movendoCerca || !canConfirmMoveFence"
            @click="confirmMoveFence"
          >
            {{ $t('crudLinhas.map.moveFenceConfirm') }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="dialogTerminusOrder" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">
          <span style="color: #d60000">{{ $t('crudLinhas.messages.systemMessage') }}</span>
        </v-card-title>
        <v-card-text>
          <span style="color: #d60000">{{ $t('crudLinhas.map.dialogTerminusOrderBody') }}</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red accent-4" class="white--text" @click="dialogTerminusOrder = false">
            {{ $t('button.confirmOk') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogSemRota" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1">{{ $t('crudLinhas.map.dialogNoRouteTitle') }}</v-card-title>
        <v-card-text>{{ $t('crudLinhas.map.dialogNoRouteBody') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="dialogSemRota = false">{{ $t('button.confirmOk') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCercaForaRota" max-width="480" persistent>
      <v-card>
        <v-card-title class="subtitle-1">{{ $t('crudLinhas.map.dialogFenceOutsideTitle') }}</v-card-title>
        <v-card-text>{{ dialogCercaForaRotaMensagem }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelCercaForaRota">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" text @click="confirmCercaForaRota">
            {{ $t('crudLinhas.map.associateAnyway') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteCercaDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="headline red--text text--accent-4">
          {{ $t('crudLinhas.messages.systemMessage') }}
        </v-card-title>
        <v-card-text class="red--text text--accent-4">
          {{ confirmDeleteCercaMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red accent-4" class="white--text" @click="cancelDeleteCerca">
            {{ $t('crudLinhas.messages.no') }}
          </v-btn>
          <v-btn color="red accent-4" class="white--text" @click="confirmDeleteCerca">
            {{ $t('crudLinhas.messages.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogSentidoRoteirizacao" max-width="460" persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left color="primary">mdi-swap-horizontal</v-icon>
          {{ $t('crudLinhas.map.routeByFencesDirectionTitle') }}
        </v-card-title>
        <v-card-text>
          <div class="caption mb-3">{{ $t('crudLinhas.map.routeByFencesDirectionBody') }}</div>
          <v-select
            v-model="sentidoRoteirizacao"
            :items="sentidosRoteirizacaoItems"
            :label="$t('crudLinhas.map.direction')"
            dense
            outlined
            hide-details
          />
          <v-alert v-if="sentidoRoteirizacaoEhVolta" type="info" dense text class="mt-3 mb-0 caption">
            {{ $t('crudLinhas.map.routeByFencesDirectionReturnHint') }}
          </v-alert>

          <template v-if="sentidoRoteirizacao != null && sentidoRoteirizacao !== ''">
            <v-divider class="my-3" />
            <div class="caption font-weight-medium mb-1">
              {{ $t('crudLinhas.map.routeByFencesDirectionFences', { count: cercasDoSentidoRoteirizacao.length }) }}
            </div>
            <v-alert v-if="!cercasDoSentidoRoteirizacao.length" type="warning" dense text class="mb-0 caption">
              {{ $t('crudLinhas.map.routeByFencesDirectionNoFences') }}
            </v-alert>
            <div v-if="cercasDoSentidoRoteirizacao.length" class="linha-map-editor__direction-fences mt-2">
              <div
                v-for="cerca in cercasDoSentidoRoteirizacao"
                :key="`${cerca.id_cerca}-${cerca.id_sentido}`"
                class="caption d-flex justify-space-between"
              >
                <span class="text-truncate mr-2">
                  {{ cerca.ordem_parada }}. {{ cerca.nome_cerca || cerca.cod_cerca || cerca.id_cerca }}
                </span>
                <span class="grey--text flex-shrink-0">{{ descricaoTipoCerca(cerca.id_tipo_cerca) }}</span>
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogSentidoRoteirizacao = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :disabled="
              sentidoRoteirizacao == null ||
                sentidoRoteirizacao === '' ||
                !cercasDoSentidoRoteirizacao.length
            "
            @click="confirmarSentidoRoteirizacao"
          >
            {{ $t('crudLinhas.map.routeByFences') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogRoteirizarPorCercas"
      fullscreen
      persistent
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card
        v-if="previewRoteirizacao && roteirizacaoComparacao"
        class="d-flex flex-column linha-map-editor__preview-dialog-card"
      >
        <v-toolbar dark color="primary" flat class="flex-grow-0">
          <v-btn icon dark :disabled="salvandoPreviewRoteirizacao" @click="descartarPreviewRoteirizacao">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon left>mdi-compare-horizontal</v-icon>
            {{ $t('crudLinhas.map.routeByFencesPreviewTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn dark text :loading="salvandoPreviewRoteirizacao" @click="salvarPreviewRoteirizacao">
              <v-icon left small>mdi-content-save</v-icon>
              {{ $t('crudLinhas.messages.save') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pa-0 linha-map-editor__preview-dialog-body">
          <div class="d-flex flex-wrap fill-height">
            <div class="linha-map-editor__preview-dialog-map">
              <l-map
                ref="previewMapRef"
                :key="`preview-map-${previewRoteirizacaoRev}`"
                :zoom="previewZoom"
                :center="previewCenter"
                :options="mapOptions"
                style="height: 100%; width: 100%"
                @ready="onPreviewMapReady"
              >
                <l-tile-layer :url="url" :attribution="attribution" />
                <!-- A sugerida é a base sólida; a atual vai tracejada por cima,
                     para que os trechos em comum mostrem as duas cores. -->
                <l-geo-json
                  v-if="roteirizacaoPreviewLayer"
                  :key="`preview-rota-nova-${previewRoteirizacaoRev}`"
                  :geojson="roteirizacaoPreviewLayer.geojson"
                  :options-style="roteirizacaoPreviewLayer.style"
                  :visible="mostrarRotaNovaPreview"
                />
                <l-geo-json
                  v-if="rotaAtualPreviewLayer"
                  ref="previewRotaAtualRef"
                  :key="`preview-rota-atual-${previewRoteirizacaoRev}`"
                  :geojson="rotaAtualPreviewLayer.geojson"
                  :options-style="rotaAtualPreviewLayer.style"
                  :visible="mostrarRotaAtualPreview"
                />
                <l-marker
                  v-for="pin in previewSequenciaMarkers"
                  :key="`preview-seq-${pin.id_cerca}`"
                  :lat-lng="latLng(pin.lat, pin.lng)"
                  :icon="iconPinOrdem(pin)"
                  :z-index-offset="40"
                >
                  <l-tooltip :content="pin.tooltip" :options="{ direction: 'top', offset: [0, -14] }" />
                </l-marker>
              </l-map>
            </div>

            <div class="linha-map-editor__preview-dialog-side pa-3">
              <v-alert
                v-if="!roteirizacaoComparacao.ordem_alterada && !roteirizacaoComparacao.tipos_alterados_flag"
                type="info"
                dense
                text
                class="mb-2 caption"
              >
                {{ $t('crudLinhas.map.routeByFencesPreviewNoOrderChange') }}
              </v-alert>

              <v-switch v-model="mostrarRotaAtualPreview" dense hide-details class="mt-0 mb-1" color="primary">
                <template v-slot:label>
                  <span class="caption">
                    <span class="linha-map-editor__legend linha-map-editor__legend--atual"></span>
                    {{ $t('crudLinhas.map.routeByFencesPreviewCurrent') }}
                  </span>
                </template>
              </v-switch>
              <v-switch v-model="mostrarRotaNovaPreview" dense hide-details class="mt-0 mb-2" color="success">
                <template v-slot:label>
                  <span class="caption">
                    <span class="linha-map-editor__legend linha-map-editor__legend--nova"></span>
                    {{ $t('crudLinhas.map.routeByFencesPreviewNew') }}
                  </span>
                </template>
              </v-switch>

              <v-divider class="mb-2" />
              <!-- Na volta o veículo obrigatoriamente sai do ponto inicial do sentido:
                   não há o que escolher, então o interruptor dá lugar ao aviso. -->
              <div v-if="previewEhVolta" class="caption grey--text mb-2">
                {{ $t('crudLinhas.map.routeByFencesPinReturnFixed') }}
              </div>
              <template v-else>
                <v-switch
                  v-model="fixarPontoInicialRoteirizacao"
                  dense
                  hide-details
                  class="mt-0"
                  color="primary"
                  :disabled="gerandoPreviewRoteirizacao || salvandoPreviewRoteirizacao"
                >
                  <template v-slot:label>
                    <span class="caption">{{ $t('crudLinhas.map.routeByFencesPinStart') }}</span>
                  </template>
                </v-switch>
                <div class="caption grey--text mb-2">
                  {{ $t('crudLinhas.map.routeByFencesPinStartHint') }}
                </div>
              </template>

              <div class="d-flex flex-wrap linha-map-editor__preview-cards mb-2">
              <v-simple-table dense class="linha-map-editor__preview-summary linha-map-editor__preview-card">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewDistance') }}</td>
                      <td class="caption text-right">
                        {{ formatDistanciaKm(roteirizacaoComparacao.distancia_antes) }}
                        <v-icon x-small>mdi-arrow-right</v-icon>
                        <strong>{{ formatDistanciaKm(roteirizacaoComparacao.distancia_depois) }}</strong>
                        <span
                          v-if="roteirizacaoComparacao.diferenca_distancia != null"
                          :class="roteirizacaoComparacao.diferenca_distancia > 0 ? 'red--text' : 'green--text'"
                        >
                          ({{ formatDiferencaKm(roteirizacaoComparacao.diferenca_distancia) }})
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewDuration') }}</td>
                      <td class="caption text-right">
                        {{ formatDuracao(roteirizacaoComparacao.duracao_depois) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewStops') }}</td>
                      <td class="caption text-right">{{ roteirizacaoComparacao.total_paradas }}</td>
                    </tr>
                    <tr>
                      <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewReordered') }}</td>
                      <td class="caption text-right">{{ roteirizacaoComparacao.posicoes_alteradas }}</td>
                    </tr>
                    <tr>
                      <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewRetyped') }}</td>
                      <td class="caption text-right">{{ roteirizacaoComparacao.tipos_alterados }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-simple-table dense class="linha-map-editor__preview-summary linha-map-editor__preview-card">
                <template v-slot:default>
                  <tbody>
                    <tr v-if="carregandoCustoRoteirizacao">
                      <td class="caption" colspan="2">
                        <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
                        {{ $t('crudLinhas.map.routeByFencesPreviewCostLoading') }}
                      </td>
                    </tr>
                    <tr v-else-if="!roteirizacaoCustoComparacao">
                      <td class="caption" colspan="2">
                        {{ $t('crudLinhas.map.routeByFencesPreviewCostUnavailable') }}
                      </td>
                    </tr>
                    <template v-else>
                      <tr>
                        <td class="caption font-weight-medium">
                          {{ $t('crudLinhas.map.routeByFencesPreviewCostTotal') }}
                        </td>
                        <td class="caption text-right">
                          {{ formatMoeda(roteirizacaoCustoComparacao.total_antes) }}
                          <v-icon x-small>mdi-arrow-right</v-icon>
                          <strong>{{ formatMoeda(roteirizacaoCustoComparacao.total_depois) }}</strong>
                          <span
                            v-if="roteirizacaoCustoComparacao.diferenca_total != null"
                            :class="roteirizacaoCustoComparacao.diferenca_total > 0 ? 'red--text' : 'green--text'"
                          >
                            ({{ formatDiferencaMoeda(roteirizacaoCustoComparacao.diferenca_total) }})
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewCostVariable') }}</td>
                        <td class="caption text-right">
                          {{ formatMoeda(roteirizacaoCustoComparacao.variavel_antes) }}
                          <v-icon x-small>mdi-arrow-right</v-icon>
                          {{ formatMoeda(roteirizacaoCustoComparacao.variavel_depois) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="caption">{{ $t('routePlanning.fields.fixedCost') }}</td>
                        <td class="caption text-right">
                          {{ formatMoeda(roteirizacaoCustoComparacao.custo_fixo) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="caption">{{ $t('routePlanning.fields.laborCost') }}</td>
                        <td class="caption text-right">
                          {{ formatMoeda(roteirizacaoCustoComparacao.custo_mao_obra) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewCostSource') }}</td>
                        <td class="caption text-right">{{ custoFonteLabel }}</td>
                      </tr>
                    </template>
                  </tbody>
                </template>
              </v-simple-table>
              </div>

              <v-simple-table dense class="linha-map-editor__preview-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="caption">{{ $t('crudLinhas.map.routeByFencesPreviewOrder') }}</th>
                      <th class="caption">{{ $t('crudLinhas.form.fences.fence') }}</th>
                      <th class="caption">{{ $t('crudLinhas.form.fences.fenceType') }}</th>
                      <th class="caption text-right">{{ $t('crudLinhas.map.routeByFencesPreviewEta') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in roteirizacaoComparacao.linhas"
                      :key="`preview-row-${row.id_cerca}`"
                      :class="row.posicao_alterada || row.tipo_alterada ? 'linha-map-editor__preview-row--changed' : ''"
                    >
                      <td class="caption">
                        <span v-if="row.posicao_antes != null && row.posicao_alterada">
                          {{ row.posicao_antes }} <v-icon x-small>mdi-arrow-right</v-icon>
                        </span>
                        <strong>{{ row.posicao_depois }}</strong>
                      </td>
                      <td class="caption">{{ row.nome_cerca }}</td>
                      <td class="caption">
                        <span v-if="row.tipo_alterada">
                          {{ descricaoTipoCerca(row.tipo_antes) }} <v-icon x-small>mdi-arrow-right</v-icon>
                        </span>
                        {{ descricaoTipoCerca(row.tipo_depois) }}
                      </td>
                      <td class="caption text-right">{{ formatDuracao(row.eta_segundos) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiController from "@/utils/apiController";
import { isValidado } from "@/utils/situacoesValidacao";
import L from "leaflet";
import EventBus from "@/utils/eventBus";
import translateDbField from "@/utils/translateDbField";
import RecursosOperacaoMapControls from "./RecursosOperacaoMapControls.vue";
import UnidadeLinhasTable from "./UnidadeLinhasTable.vue";
import CercaLinhasTable from "./CercaLinhasTable.vue";
import MerusDataTable from "../Tables/MerusDataTable.vue";
import {
  LMap,
  LTileLayer,
  LGeoJson,
  LCircle,
  LMarker,
  LTooltip,
  LPolyline,
  LControl,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import "polyline-encoded";
import {
  buildVersoPlanPayload,
  decodePlanGeometry,
  lngLatFromGeojson,
  sortViasForPlan,
} from "@/utils/routeTraceVroom";
import { cercaIntersectsRotas } from "@/utils/cercaRotaIntersection";
import { haversineMeters, bearingDegrees, destinationPointMeters } from "@/utils/haversine";
import {
  buildEditadaCrudPayload,
  computeRouteCost,
  metersToKm,
  nowSqlDatetime,
  snapshotTracadoRota,
} from "@/utils/historicoAlteracoesRotas";
import { resolveBaselineCrud } from "@/utils/historicoAlteracoesRotasCrud";
import {
  FENCE_TYPE,
  aplicarRoteirizacaoPorCercas,
  buildRoteirizacaoComparison,
  collectFenceTypeChanges,
  enrichCercasWithCoords,
  fenceOrderKey,
  fenceSnapshotForHistory,
  hasTerminusOrderViolation,
  insertFencePreservingTerminusOrder,
  isPinnedTerminusFence,
  isSentidoVolta,
  lineHasDisplacement,
  persistLineFenceSequence,
  planOrderedFencesRoute,
  previewRoteirizacaoPorCercas,
  resolveDirectionContext,
  roteirizarLinhaPorCercas,
  selectMainRouteForSentido,
  solveReorderLineWithPcd,
  updateMainRouteGeometry,
  wouldBreakTerminusOrder,
} from "@/utils/allocationFenceCreation";
import {
  persistHistoricoRoteirizacaoCrudEvent,
  TIPO_EVENTO_HISTORICO,
} from "@/utils/historicoRoteirizacaoSnapshot";

function translateLngLatTree(coords, dLng, dLat) {
  if (!Array.isArray(coords) || !coords.length) return coords;
  if (typeof coords[0] === "number" && typeof coords[1] === "number") {
    return [Number(coords[0]) + dLng, Number(coords[1]) + dLat, ...coords.slice(2)];
  }
  return coords.map((c) => translateLngLatTree(c, dLng, dLat));
}

function translateGeojson(raw, dLat, dLng) {
  if (raw == null || raw === "") return raw;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch (e) {
      return raw;
    }
  }
  const walk = (g) => {
    if (!g || typeof g !== "object") return g;
    if (g.type === "Feature") return { ...g, geometry: walk(g.geometry) };
    if (g.type === "FeatureCollection") {
      return { ...g, features: (g.features || []).map(walk) };
    }
    if (Array.isArray(g.coordinates)) {
      return { ...g, coordinates: translateLngLatTree(g.coordinates, dLng, dLat) };
    }
    return g;
  };
  const next = walk(obj);
  return typeof raw === "string" ? JSON.stringify(next) : next;
}

function translateShape(raw, dLat, dLng) {
  if (raw == null || raw === "") return raw;
  let arr = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch (e) {
      return raw;
    }
  }
  if (!Array.isArray(arr)) return raw;
  const next = arr.map((p) => {
    if (!p || typeof p !== "object") return p;
    const lat = p.lt != null ? Number(p.lt) : Number(p.lat);
    const lng = p.lg != null ? Number(p.lg) : Number(p.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return p;
    if (p.lt != null || p.lg != null) {
      return { ...p, lt: lat + dLat, lg: lng + dLng };
    }
    return { ...p, lat: lat + dLat, lng: lng + dLng };
  });
  return typeof raw === "string" ? JSON.stringify(next) : next;
}

function stringifyMaybe(value) {
  if (value == null) return value;
  return typeof value === "string" ? value : JSON.stringify(value);
}

const DRAFT_CERCA_ID = "draft";
const MIN_FENCE_RADIUS_M = 5;

function isDraftCerca(x) {
  if (!x) return false;
  return x.id_cerca === DRAFT_CERCA_ID || x._createPreview === true || x.id_cerca == null;
}

/** Anel [[lat,lng], …] sem vértice de fecho, a partir de shape ou geojson Polygon. */
function ringFromCerca(cerca) {
  if (!cerca) return [];
  let shape = cerca.shape;
  if (typeof shape === "string" && shape.trim()) {
    try {
      shape = JSON.parse(shape);
    } catch (e) {
      shape = null;
    }
  }
  if (Array.isArray(shape) && shape.length) {
    const ring = shape
      .map((p) => {
        if (!p || typeof p !== "object") return null;
        const lat = p.lt != null ? Number(p.lt) : Number(p.lat);
        const lng = p.lg != null ? Number(p.lg) : Number(p.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return [lat, lng];
      })
      .filter(Boolean);
    if (ring.length >= 2) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (
        Math.abs(first[0] - last[0]) < 1e-9 &&
        Math.abs(first[1] - last[1]) < 1e-9
      ) {
        ring.pop();
      }
    }
    return ring;
  }
  let gj = cerca.geojson;
  if (typeof gj === "string" && gj.trim()) {
    try {
      gj = JSON.parse(gj);
    } catch (e) {
      return [];
    }
  }
  if (!gj || typeof gj !== "object") return [];
  const geom = gj.type === "Feature" ? gj.geometry : gj;
  if (!geom || geom.type !== "Polygon" || !Array.isArray(geom.coordinates)) return [];
  const coords = geom.coordinates[0] || [];
  const ring = coords
    .map((c) => {
      if (!Array.isArray(c) || c.length < 2) return null;
      const lng = Number(c[0]);
      const lat = Number(c[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lat, lng];
    })
    .filter(Boolean);
  if (ring.length >= 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (
      Math.abs(first[0] - last[0]) < 1e-9 &&
      Math.abs(first[1] - last[1]) < 1e-9
    ) {
      ring.pop();
    }
  }
  return ring;
}

/** Reconstrói shape/geojson/centro a partir de anel sem fecho (mesmo contrato do saveCoords legado). */
function buildFenceFromRing(ring) {
  const pts = (Array.isArray(ring) ? ring : [])
    .map((p) => {
      const lat = Number(p && p[0]);
      const lng = Number(p && p[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lat, lng];
    })
    .filter(Boolean);
  if (pts.length < 3) {
    return {
      shape: null,
      geojson: null,
      lat_centro: null,
      lng_centro: null,
      is_circular: 0,
      raio_mts: 0,
    };
  }
  const shapePts = pts.map(([lat, lng]) => ({ lt: lat, lg: lng }));
  shapePts.push({ lt: pts[0][0], lg: pts[0][1] });
  let latSum = 0;
  let lngSum = 0;
  pts.forEach(([lat, lng]) => {
    latSum += lat;
    lngSum += lng;
  });
  const lat_centro = latSum / pts.length;
  const lng_centro = lngSum / pts.length;
  const coordinates = shapePts.map((p) => [p.lg, p.lt]);
  return {
    shape: shapePts,
    geojson: {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [coordinates],
      },
    },
    lat_centro,
    lng_centro,
    is_circular: 0,
    raio_mts: 0,
  };
}

/** LineString + shape Merus a partir de vértices [{lat,lng}, …] (mín. 2). */
function buildRouteFromVertices(vertices) {
  const pts = (Array.isArray(vertices) ? vertices : [])
    .map((v) => {
      const lat = Number(v && v.lat);
      const lng = Number(v && v.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
  if (pts.length < 2) {
    return { shape: null, geojson: null, distancia: null };
  }
  let meters = 0;
  for (let i = 1; i < pts.length; i += 1) {
    meters += haversineMeters(pts[i - 1].lat, pts[i - 1].lng, pts[i].lat, pts[i].lng);
  }
  const shapePts = pts.map((p) => ({ lt: p.lat, lg: p.lng }));
  const coordinates = pts.map((p) => [p.lng, p.lat]);
  return {
    shape: shapePts,
    geojson: {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates,
      },
    },
    distancia: metersToKm(meters),
  };
}

const ROUTE_COLORS = ["#1565C0", "#2E7D32", "#EF6C00", "#6A1B9A", "#C62828"];
/** Preview avulso de cercas (módulo Cercas); no mapa de linhas usa a mesma paleta das rotas. */
const CERCA_COLORS = ROUTE_COLORS;
const PREVIEW_CERCA_COLOR = "#FF6F00";
const PREVIEW_ROTA_COLOR = "#ff9800";
const PRIMARY_LINE_COLOR = ROUTE_COLORS[0];
/** Cor do traçado sugerido pela roteirização por cercas na prévia. */
const PREVIEW_ROTEIRIZACAO_COLOR = "#2E7D32";
const PIN_START_STORAGE_KEY = "linhaMapEditor.roteirizacaoFixarPontoInicial";
const EDIT_ROUTE_COLOR = "#ff9800";
/** Limite de marcadores de vértice ao editar traçado (polilinhas densas do planner). */
const EDIT_ROUTE_VERTEX_MARKER_MAX = 200;
const DRAFT_ROTA_ID = "draft-rota";

export default {
  name: "RecursosOperacaoMapEditor",
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LCircle,
    LMarker,
    LTooltip,
    LPolyline,
    LControl,
    RecursosOperacaoMapControls,
    UnidadeLinhasTable,
    CercaLinhasTable,
    MerusDataTable,
  },
  props: {
    linha: { type: Object, default: null },
    sentidos: { type: Array, default: () => [] },
    sentidoSelecionado: { type: [Number, String], default: null },
    token: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
    canInsert: { type: Boolean, default: false },
    passageiros: { type: Array, default: () => [] },
    tiposCerca: { type: Array, default: () => [] },
    tiposRota: { type: Array, default: () => [] },
    historicoSessaoId: { type: String, default: null },
    historicoSessaoOrigemId: { type: String, default: null },
    entityPreviewType: { type: String, default: null },
    entityPreviewItem: { type: Object, default: null },
    /** Quando há mais de uma linha/rota/cerca/unidade marcada na tabela, todas vêm aqui (inclui o item primário). */
    entityPreviewItems: { type: Array, default: () => [] },
    entityPreviewRotas: { type: Array, default: () => [] },
    entityPreviewCerca: { type: Object, default: null },
    /** Associações cerca-linha pré-cadastradas no drawer de criação de linha. */
    entityPreviewCercasLinha: { type: Array, default: () => [] },
    /** Centraliza a cerca (ex.: unidade de negócio) em vez do traçado das rotas no preview de linha. */
    entityPreviewFitPreferCerca: { type: Boolean, default: false },
    /** Grupos {cerca, rotas} por unidade marcada, quando há seleção múltipla de unidades. */
    entityPreviewUnitGroups: { type: Array, default: () => [] },
    /** Outras linhas marcadas na tabela (além de `linha`), exibidas em sobreposição somente leitura. */
    additionalLinhas: { type: Array, default: () => [] },
    /** Paginador de linhas marcadas na tabela — mesmo cursor da aba Parâmetros. */
    selecaoTotal: { type: Number, default: 0 },
    selecaoPage: { type: Number, default: 0 },
    selecaoLabel: { type: String, default: null },
    mapContext: { type: String, default: "linhas" },
    /** Passo inicial ao criar cerca na linha: exige seleção de rota antes do formulário. */
    cercaCreateSelectRoute: { type: Boolean, default: false },
    cercaCreateRouteId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loadedLinhaId: null,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      loading: false,
      rotasVisible: true,
      cercasVisible: true,
      passageirosVisible: true,
      /** Preview de unidades: exibe no mapa as rotas das linhas associadas. */
      linhasAssociadasVisible: true,
      editMode: "view",
      rawRotas: [],
      rawCercas: [],
      cercasLinhaLocal: [],
      rotasLinhaLocal: [],
      mapRotas: [],
      mapCercasGeojson: [],
      mapCercasCirculares: [],
      mapCercaMarkers: [],
      /** Sobreposição somente leitura das demais linhas marcadas na tabela (contexto "linhas"). */
      outrasLinhasMapRotas: [],
      outrasLinhasMapCercasGeojson: [],
      outrasLinhasMapCercasCirculares: [],
      outrasLinhasMapCercaMarkers: [],
      additionalLinhasRequestSeq: 0,
      carregarDadosRequestSeq: 0,
      mapKey: 0,
      zoom: 14,
      currentCenter: [-22.88746, -43.29185],
      mapOptions: { zoomSnap: 0.5, scrollWheelZoom: true },
      map: null,
      mensagemVazia: "",
      activeEditRouteId: null,
      editWaypoints: [],
      /** Vias já aplicadas nesta sessão de edição (preview sem cercas); preservam o traçado entre applies. */
      appliedEditVias: [],
      editRecalculating: false,
      mapClickHandler: null,
      pickLocationMode: false,
      pickLocationHandler: null,
      createDraftCerca: null,
      createDraftRota: null,
      /** Pré-visualização no drawer de criação de linha (rotas + cerca da unidade). */
      linhaCreatePreviewRotas: [],
      linhaCreatePreviewCerca: null,
      linhaCreatePreviewCercasLinha: [],
      fenceEditHandles: [],
      /** Handles arrastáveis dos vértices da rota em edição de traçado. */
      editRouteVertexHandles: [],
      /** Geometria local alterada (arraste) ainda não aplicada/salva. */
      editRouteGeometryDirty: false,
      /** Após remover traçado: cliques no mapa redesenham a linha (não adicionam vias). */
      editRouteRedrawing: false,
      /** Azimute (graus) do handle de raio por id_cerca, para manter na borda ao mover/redimensionar. */
      fenceRadiusHandleBearings: {},
      drawFenceVertices: [],
      drawFenceHandler: null,
      drawRouteVertices: [],
      drawRouteHandler: null,
      routeClickDebounce: null,
      /** Evita que o clique na polyline também adicione uma via no mapa (igual Roteirização). */
      lastPolylineClickMs: 0,
      /** Full route detail for preview/association of a lazy-selected id. */
      selectedRotaDetail: null,
      /** Full cerca detail for preview/association of a lazy-selected id. */
      selectedCercaDetail: null,
      /** @type {Record<number, object>} */
      cercaDetailCache: {},
      itemCerca: {
        id_cerca: null,
        id_sentido: null,
        id_tipo_cerca: null,
        ordem_parada: 1,
        tempo_chegada_minutos: 0,
        show_sinotico: true,
      },
      editingCerca: false,
      editingCercaId: null,
      editingCercaSentido: null,
      /**
       * Snapshot da cerca ao trocar circular→polígono (limpa círculo e redesenha).
       * Se o desenho for cancelado, restaura a geometria anterior.
       */
      redrawFenceSnapshot: null,
      itemRota: {
        id_rota: null,
        id_tipo_rota: null,
        id_sentido: null,
      },
      savingCercas: false,
      savingRotas: false,
      dialogTerminusOrder: false,
      dialogSemRota: false,
      selectedMapRouteId: null,
      dialogCercaForaRota: false,
      dialogCercaForaRotaMensagem: "",
      pendingCercaPayload: null,
      persistCercaAfterDialog: false,
      pendingCercaRedesenhar: false,
      roteirizandoPorCercas: false,
      dialogRoteirizarPorCercas: false,
      /** Prévia da roteirização por cercas aguardando salvar/cancelar. */
      previewRoteirizacao: null,
      previewRoteirizacaoRev: 0,
      gerandoPreviewRoteirizacao: false,
      salvandoPreviewRoteirizacao: false,
      mostrarRotaAtualPreview: true,
      mostrarRotaNovaPreview: true,
      dialogSentidoRoteirizacao: false,
      /** Sentido escolhido no diálogo que antecede a roteirização por cercas. */
      sentidoRoteirizacao: null,
      /** Rota alvo da roteirização, resolvida antes de abrir o diálogo de sentido. */
      idRotaRoteirizacao: null,
      /**
       * Com `true` (padrão) o ponto inicial ancora a roteirização e nunca troca de
       * posição; com `false` ele concorre como cerca comum e a primeira da nova
       * sequência assume o tipo de ponto inicial. Preferência lembrada no navegador.
       */
      fixarPontoInicialRoteirizacao: true,
      /** Tarifas usadas na estimativa de custo da prévia (contrato do cliente ou padrão). */
      custoParamsRoteirizacao: null,
      carregandoCustoRoteirizacao: false,
      previewZoom: 13,
      previewCenter: [-22.88746, -43.29185],
      fenceDragRecalculating: false,
      confirmDeleteCercaDialog: false,
      cercaPendenteExclusao: null,
      pendingHistoricoCercaDetalhe: null,
      entityPreviewRequestSeq: 0,
      /** Força o remount das camadas de geometria após edição in-place no preview. */
      previewLayerRev: 0,
      drawerMoveFenceOpen: false,
      movendoCerca: false,
      moveFenceStep: 1,
      moveFenceLoadingSource: false,
      moveFenceSource: null,
      moveFenceSourceLinha: null,
      moveFenceTargetLinha: null,
      moveFenceTargetLabel: null,
      moveFenceSourceRows: [],
      moveFenceSourceRotas: [],
      moveFenceDestRows: [],
      moveFenceDestRotas: [],
      drawerRotasOpen: false,
      drawerCercasOpen: false,
      drawerLinhasOpen: false,
      drawerCreateOpen: false,
      drawerLinhasPage: 0,
      drawerCercaLinhasOpen: false,
      drawerCercaLinhasPage: 0,
      cercaLinhasSelecionadas: [],
      mapCercaLinhasSelecionadasRotas: [],
      cercaLinhasRequestSeq: 0,
      searchDrawerRotas: "",
      searchDrawerCercas: "",
      drawerCercasManagePanel: null,
      reorderFencesMode: false,
    };
  },
  computed: {
    isEntityPreview() {
      return ["route", "fence", "unit", "linha"].includes(this.entityPreviewType);
    },
    /** Itens a exibir no mapa: a seleção múltipla (se houver) ou só o item primário. */
    entityPreviewItemsResolved() {
      if (Array.isArray(this.entityPreviewItems) && this.entityPreviewItems.length) {
        return this.entityPreviewItems;
      }
      return this.entityPreviewItem ? [this.entityPreviewItem] : [];
    },
    isLinhasContext() {
      return this.mapContext === "linhas" && !this.isEntityPreview;
    },
    isUnidadesPreview() {
      return this.mapContext === "unidades" && this.entityPreviewType === "unit";
    },
    isCercasPreview() {
      return this.mapContext === "cercas" && this.entityPreviewType === "fence";
    },
    isRotasPreview() {
      return this.mapContext === "rotas" && this.entityPreviewType === "route";
    },
    isLinhaCreatePreview() {
      return this.mapContext === "linhas" && this.entityPreviewType === "linha";
    },
    showCreateEntityDrawer() {
      return (
        (this.isUnidadesPreview ||
          this.isCercasPreview ||
          this.isRotasPreview ||
          this.isLinhaCreatePreview ||
          (this.isLinhasContext && !!(this.linha && this.linha.id_linha))) &&
        this.canInsert
      );
    },
    createDrawerTitle() {
      if (this.isUnidadesPreview) return this.$t("crudUnidades.form.title.create");
      if (this.isCercasPreview) return this.$t("geofence.registerGeofence");
      if (this.isRotasPreview) return this.$t("routs.register");
      if (this.isLinhaCreatePreview) return this.$t("crudLinhas.register");
      if (this.isLinhasContext && this.cercaCreateSelectRoute) {
        return this.$t("crudLinhas.map.routeByFencesSelectRoute");
      }
      if (this.isLinhasContext) return this.$t("geofence.registerGeofence");
      return "";
    },
    drawerLinhasUnits() {
      return (this.entityPreviewItemsResolved || []).filter((u) => u && u.id_unidade != null);
    },
    drawerLinhasUnidadeAtual() {
      const units = this.drawerLinhasUnits;
      if (!units.length) return null;
      const page = Math.min(Math.max(0, this.drawerLinhasPage), units.length - 1);
      return units[page] || null;
    },
    drawerLinhasUnitLabel() {
      const item = this.drawerLinhasUnidadeAtual;
      if (!item) return null;
      return [item.cod_unidade, item.descricao_unidade].filter(Boolean).join(" — ") || null;
    },
    drawerCercaLinhasItems() {
      return (this.entityPreviewItemsResolved || []).filter((c) => c && c.id_cerca != null);
    },
    drawerCercaLinhasAtual() {
      const items = this.drawerCercaLinhasItems;
      if (!items.length) return null;
      const page = Math.min(Math.max(0, this.drawerCercaLinhasPage), items.length - 1);
      return items[page] || null;
    },
    drawerCercaLinhasLabel() {
      const item = this.drawerCercaLinhasAtual;
      if (!item) return null;
      return [item.cod_cerca, item.nome_cerca].filter(Boolean).join(" — ") || null;
    },
    /** Visibilidade das rotas no mapa (linhas associadas no preview de unidade). */
    mapRotasVisible() {
      return this.isUnidadesPreview ? this.linhasAssociadasVisible : this.rotasVisible;
    },
    showMapControls() {
      return this.mapControlItems.length > 0 || this.mapControlMenuItems.length > 0;
    },
    mapControlItems() {
      if (this.isLinhasContext) {
        const items = [
          {
            id: "toggle-routes-drawer",
            icon: "mdi-routes",
            label: this.$t("crudLinhas.map.controls.routesDrawer"),
            active: this.drawerRotasOpen,
          },
          {
            id: "toggle-fences-drawer",
            icon: "mdi-vector-polygon",
            label: this.$t("crudLinhas.map.controls.fencesDrawer"),
            active: this.drawerCercasOpen,
          },
        ];
        if (this.canEdit) {
          items.push({
            id: "edit-fence-drag",
            icon: "mdi-map-marker-path",
            label: this.$t("crudLinhas.map.controls.editFenceDrag"),
            active: this.editMode === "editFences",
            disabled: this.fenceDragRecalculating,
          });
        }
        return items;
      }
      const items = [];
      if (this.isUnidadesPreview && this.drawerLinhasUnits.length) {
        items.push({
          id: "toggle-lines-drawer",
          icon: "mdi-link-variant",
          label: this.$t("crudLinhas.map.controls.manageLineAssociation"),
          active: this.drawerLinhasOpen,
        });
      }
      if (this.isCercasPreview && this.drawerCercaLinhasItems.length) {
        items.push({
          id: "toggle-cerca-lines-drawer",
          icon: "mdi-routes",
          label: this.$t("crudLinhas.map.controls.cercaLinesDrawer"),
          active: this.drawerCercaLinhasOpen,
        });
      }
      // Cercas: botão dedicado de arrasto (mesmo padrão do editStops da Roteirização).
      if (
        this.mapContext === "cercas" &&
        this.isEntityPreview &&
        (this.canEdit || (this.canInsert && this.drawerCreateOpen)) &&
        this.isCercasPreview && 
        this.drawerCercaLinhasItems.length
      ) {
        items.push({
          id: "edit-fence-drag",
          icon: "mdi-map-marker-path",
          label: this.$t("crudLinhas.map.controls.editFenceDrag"),
          active: this.editMode === "editFences",
          disabled: this.fenceDragRecalculating,
        });
      }
      if (this.mapContext === "rotas" && this.isEntityPreview && this.canEdit) {
        items.push({
          id: "edit-route-trace",
          icon: "mdi-vector-polyline-edit",
          label: this.$t("crudLinhas.map.controls.editRouteTrace"),
          active: this.editMode === "editRoute",
        });
      }
      return items;
    },
    mapControlMenuItems() {
      if (this.isLinhasContext) {
        // FAB "Ações" só aparece com uma rota clicada/selecionada no mapa.
        if (this.selectedMapRouteId == null) return [];
        const items = [];
        // Cada ação exige a permissão correspondente (create = canInsert, demais = canEdit).
        if (this.canInsert) {
          items.push({
            id: "create-fence-on-line",
            icon: "mdi-map-marker-plus",
            label: this.$t("crudLinhas.map.controls.createFenceOnLine"),
          });
        }
        if (this.canEdit) {
          items.push(
            {
              id: "edit-fence-drag",
              icon: "mdi-map-marker-path",
              label: this.$t("crudLinhas.map.controls.editFenceDrag"),
              active: this.editMode === "editFences",
            },
            {
              id: "edit-route-trace",
              icon: "mdi-vector-polyline-edit",
              label: this.$t("crudLinhas.map.controls.editRouteTrace"),
              active: this.editMode === "editRoute",
            },
            {
              id: "route-by-fences",
              icon: "mdi-routes",
              label: this.$t("crudLinhas.map.routeByFences"),
              disabled:
                this.roteirizandoPorCercas ||
                this.gerandoPreviewRoteirizacao ||
                this.salvandoPreviewRoteirizacao ||
                this.fenceDragRecalculating ||
                !this.linha ||
                !this.linha.id_linha,
            },
            {
              id: "change-fence-order",
              icon: "mdi-sort-numeric-ascending",
              label: this.$t("crudLinhas.map.controls.changeFenceOrder"),
              active: this.reorderFencesMode,
            },
            {
              id: "move-fence-to-line",
              icon: "mdi-transfer",
              label: this.$t("crudLinhas.map.controls.moveFenceToLine"),
              disabled:
                this.savingCercas ||
                this.movendoCerca ||
                this.fenceDragRecalculating ||
                !this.linha ||
                !this.linha.id_linha ||
                this.linhasSelecionadasNoMapa.length < 2,
            },
          );
        }
        return items;
      }
      // Rotas/Cercas/Unidades: sem menu ⋮ — só FABs.
      return [];
    },
    /** Qual barra de edição in-place exibir sobre o mapa de preview. */
    previewEditBar() {
      if (this.isEntityPreview) {
        if (this.mapContext === "rotas" && this.editMode === "editRoute") return "route";
        if (this.mapContext === "rotas" && this.editMode === "drawRoute") return "drawRoute";
        if (this.mapContext === "cercas" && this.editMode === "drawFence") return "drawFence";
        if (this.mapContext === "cercas" && this.editMode === "editFences") return "fence";
        return null;
      }
      if (this.isLinhasContext) {
        if (this.editMode === "editRoute") return "route";
        if (this.editMode === "drawFence") return "drawFence";
        if (this.editMode === "editFences") return "fence";
      }
      return null;
    },
    /** Há traçado (local ou cadastrado) na rota em edição. */
    hasEditRouteTrace() {
      if (this.activeEditRouteId == null) return false;
      if ((this.editRouteVertexHandles || []).length >= 2) return true;
      if (this.editRouteRedrawing || this.editRouteGeometryDirty) return false;
      const rota =
        (this.mapRotas || []).find((r) => Number(r.id_rota) === Number(this.activeEditRouteId)) ||
        this.resolveRotaById(this.activeEditRouteId) ||
        this.selectedRotaDetail;
      if (!rota) return false;
      const gj = this.parseGeojson(rota.geojson) || this.rotaToGeojson(rota);
      const coords = lngLatFromGeojson(gj);
      return Array.isArray(coords) && coords.length >= 2;
    },
    /** Largura do drawer esquerdo aberto (para centralizar overlays na área visível do mapa). */
    mapOverlayLeftInset() {
      if (this.drawerCreateOpen) return 400;
      if (
        this.drawerRotasOpen ||
        this.drawerCercasOpen ||
        this.drawerLinhasOpen ||
        this.drawerCercaLinhasOpen
      ) {
        return 360;
      }
      return 0;
    },
    previewEditBarStyle() {
      const inset = this.mapOverlayLeftInset;
      if (!inset) return {};
      return {
        left: `calc(50% + ${inset / 2}px)`,
        maxWidth: `calc(100% - ${inset}px - 24px)`,
      };
    },
    /** Todas as cercas editáveis no mapa (seleção múltipla + rascunho de criação). */
    activeEditableCercas() {
      if (this.isCercasPreview) {
        const list = [];
        const seen = new Set();
        if (this.drawerCreateOpen && this.createDraftCerca) {
          list.push(this.createDraftCerca);
          seen.add(DRAFT_CERCA_ID);
        }
        (this.entityPreviewItemsResolved || []).forEach((item) => {
          if (!item || item.id_cerca == null || isDraftCerca(item)) return;
          const id = Number(item.id_cerca);
          if (!Number.isFinite(id) || seen.has(id)) return;
          seen.add(id);
          list.push(this.resolveCercaById(id) || item);
        });
        return list;
      }
      if (this.isLinhasContext && this.editMode === "editFences") {
        return this.linhaFencesEligibleForDrag();
      }
      if (this.isLinhasContext && this.editingCerca && this.editingCercaId != null) {
        const cerca = this.resolveCercaById(this.editingCercaId);
        return cerca ? [cerca] : [];
      }
      return [];
    },
    activeEditableCerca() {
      const list = this.activeEditableCercas;
      return list.length ? list[0] : null;
    },
    /**
     * Cerca alvo da troca Circular/Polígono na barra de edição.
     * Na tela de linhas há várias editáveis: usa a selecionada (editingCercaId).
     */
    activeFormatTargetCerca() {
      if (this.editingCercaId != null) {
        if (this.editingCercaId === DRAFT_CERCA_ID || this.editingCercaId === "draft") {
          return this.createDraftCerca || null;
        }
        const byId = this.resolveCercaById(this.editingCercaId);
        if (byId) return byId;
        const list = this.activeEditableCercas || [];
        const hit = list.find((c) => Number(c.id_cerca) === Number(this.editingCercaId));
        if (hit) return hit;
      }
      const list = this.activeEditableCercas || [];
      if (list.length === 1) return list[0];
      return null;
    },
    activeFormatTargetLabel() {
      const cerca = this.activeFormatTargetCerca;
      if (!cerca) return "";
      return cerca.nome_cerca || cerca.cod_cerca || String(cerca.id_cerca || "");
    },
    fenceFormatSelectItems() {
      return (this.activeEditableCercas || [])
        .filter((c) => c && c.id_cerca != null)
        .map((c) => ({
          value: isDraftCerca(c) ? DRAFT_CERCA_ID : Number(c.id_cerca),
          text: c.nome_cerca || c.cod_cerca || String(c.id_cerca),
        }));
    },
    /** Formato da cerca ativa na barra de edição. */
    fenceEditBarFormato() {
      const cerca = this.activeFormatTargetCerca;
      if (!cerca) return null;
      return Number(cerca.is_circular) === 1 ? "circular" : "polygon";
    },
    canChangeFenceFormat() {
      return !!this.activeFormatTargetCerca && !this.fenceDragRecalculating;
    },
    fenceEditBarHint() {
      if (this.editMode === "editFences" && !this.activeFormatTargetCerca) {
        return this.$t("crudLinhas.map.fenceFormatSelectHint");
      }
      const cerca = this.activeFormatTargetCerca;
      if (cerca) {
        if (Number(cerca.is_circular) === 1) {
          return this.$t("crudLinhas.map.fenceRadiusDragHint");
        }
        return this.$t("crudLinhas.map.fenceVertexDragHint");
      }
      const list = this.activeEditableCercas || [];
      const hasCircular = list.some((c) => c && Number(c.is_circular) === 1);
      const hasPolygon = list.some((c) => c && Number(c.is_circular) !== 1);
      if (hasCircular && !hasPolygon) {
        return this.$t("crudLinhas.map.fenceRadiusDragHint");
      }
      if (hasPolygon && !hasCircular) {
        return this.$t("crudLinhas.map.fenceVertexDragHint");
      }
      if (hasCircular && hasPolygon) {
        return this.$t("crudLinhas.map.previewFenceDragHint");
      }
      return this.$t("crudLinhas.map.fenceVertexDragHint");
    },
    /** Linhas visíveis no mapa (primária + demais marcadas na tabela). */
    linhasSelecionadasNoMapa() {
      const rows = [];
      const seen = new Set();
      const push = (linha) => {
        if (!linha || linha.id_linha == null) return;
        const id = Number(linha.id_linha);
        if (!Number.isFinite(id) || seen.has(id)) return;
        seen.add(id);
        rows.push({
          ...linha,
          _moveLabel: [linha.cod_linha, linha.nome_linha].filter(Boolean).join(" — ") || String(id),
        });
      };
      push(this.linha);
      (this.additionalLinhas || []).forEach(push);
      return rows;
    },
    moveFenceLinhasDestinoOpcoes() {
      const sourceId = this.moveFenceSourceLinha;
      return this.linhasSelecionadasNoMapa.filter(
        (linha) => sourceId == null || Number(linha.id_linha) !== Number(sourceId)
      );
    },
    /** Cercas da linha de origem elegíveis para mover (exclui terminais fixas). */
    moveFenceCercasElegiveis() {
      return (this.moveFenceSourceRows || [])
        .filter(
          (cerca) =>
            !isPinnedTerminusFence(cerca, {
              pinStart: lineHasDisplacement(this.moveFenceSourceRotas, cerca.id_sentido),
            })
        )
        .map((cerca) => ({
          ...cerca,
          _moveLabel: [
            cerca.cod_cerca || cerca.nome_cerca || cerca.id_cerca,
            cerca.descricao_sentido || this.descricaoSentido(cerca.id_sentido),
          ]
            .filter(Boolean)
            .join(" — "),
        }));
    },
    moveFenceOriginLabel() {
      const linha = this.linha;
      if (!linha || Number(linha.id_linha) !== Number(this.moveFenceSourceLinha)) {
        const fromMap = this.linhasSelecionadasNoMapa.find(
          (l) => Number(l.id_linha) === Number(this.moveFenceSourceLinha)
        );
        return fromMap ? fromMap._moveLabel : null;
      }
      return [linha.cod_linha, linha.nome_linha].filter(Boolean).join(" — ") || String(linha.id_linha);
    },
    moveFenceSourceLabel() {
      const source = this.moveFenceSource;
      if (!source) return null;
      return [
        source.cod_cerca || source.nome_cerca || source.id_cerca,
        source.descricao_sentido || this.descricaoSentido(source.id_sentido),
      ]
        .filter(Boolean)
        .join(" — ");
    },
    moveFenceDestLabel() {
      if (this.moveFenceTargetLabel) return this.moveFenceTargetLabel;
      const linha = this.linhasSelecionadasNoMapa.find(
        (l) => Number(l.id_linha) === Number(this.moveFenceTargetLinha)
      );
      return linha ? linha._moveLabel : null;
    },
    canAdvanceMoveFenceStep() {
      if (this.moveFenceStep === 1) {
        return !!this.moveFenceSource && !this.moveFenceLoadingSource;
      }
      if (this.moveFenceStep === 2) {
        return (
          this.moveFenceTargetLinha != null &&
          this.moveFenceTargetLinha !== "" &&
          Number(this.moveFenceTargetLinha) !== Number(this.moveFenceSourceLinha)
        );
      }
      return false;
    },
    canConfirmMoveFence() {
      return (
        this.moveFenceSourceLinha != null &&
        this.moveFenceSource != null &&
        this.moveFenceTargetLinha != null &&
        this.moveFenceTargetLinha !== "" &&
        Number(this.moveFenceTargetLinha) !== Number(this.moveFenceSourceLinha)
      );
    },
    confirmDeleteCercaMessage() {
      const cerca = this.cercaPendenteExclusao || {};
      const linha = this.linha || {};
      const fence = cerca.nome_cerca || cerca.cod_cerca || cerca.id_cerca || "-";
      const line = linha.cod_linha || linha.nome_linha || linha.id_linha || "-";
      return this.$t("crudLinhas.messages.confirmFenceDeletion", { fence, line });
    },
    defaultCenter() {
      return this.$store.getters["config/centerDefault"] || [-22.88746, -43.29185];
    },
    mapPassageiros() {
      return (this.passageiros || []).filter(
        (p) => p.lat != null && p.lng != null && !Number.isNaN(Number(p.lat)) && !Number.isNaN(Number(p.lng))
      );
    },
    /**
     * Tracejado cinza do endereço do passageiro alocado até a cerca de embarque.
     */
    passengerWalkConnectionLayers() {
      if (!this.passageirosVisible) return [];
      const walkStyle = {
        color: "#9E9E9E",
        weight: 2,
        opacity: 0.85,
        dashArray: "4, 8",
      };
      const layers = [];
      const pinByFence = new Map();
      (this.mapCercaMarkers || []).forEach((pin) => {
        if (pin && pin.id_cerca != null) pinByFence.set(String(pin.id_cerca), pin);
      });

      (this.mapPassageiros || []).forEach((pax) => {
        if (!pax || pax.id_cerca_embarque == null) return;
        const pLat = Number(pax.lat);
        const pLng = Number(pax.lng);
        if (!Number.isFinite(pLat) || !Number.isFinite(pLng)) return;

        let fLat = null;
        let fLng = null;
        const cerca =
          this.resolveCercaById(pax.id_cerca_embarque) ||
          (this.cercasLinhaLocal || []).find(
            (c) => Number(c.id_cerca) === Number(pax.id_cerca_embarque)
          );
        if (cerca) {
          fLat = Number(cerca.lat_centro);
          fLng = Number(cerca.lng_centro);
        }
        if (!Number.isFinite(fLat) || !Number.isFinite(fLng)) {
          const pin = pinByFence.get(String(pax.id_cerca_embarque));
          if (pin) {
            fLat = Number(pin.lat);
            fLng = Number(pin.lng);
          }
        }
        if (!Number.isFinite(fLat) || !Number.isFinite(fLng)) return;

        layers.push({
          _linkId: `pax-walk-${pax.id_pessoa}-${pax.id_cerca_embarque}`,
          latLngs: [
            [pLat, pLng],
            [fLat, fLng],
          ],
          style: walkStyle,
        });
      });
      return layers;
    },
    idsCercasNaLinha() {
      return new Set((this.rawCercas || []).map((c) => Number(c.id_cerca)));
    },
    previewCercasCatalog() {
      const onLinha = this.idsCercasNaLinha;
      const ids = new Set();
      if (this.itemCerca.id_cerca != null) {
        const idSel = Number(this.itemCerca.id_cerca);
        if (!onLinha.has(idSel)) ids.add(idSel);
      }
      (this.cercasLinhaLocal || []).forEach((c) => {
        const id = Number(c.id_cerca);
        if (!onLinha.has(id)) ids.add(id);
      });
      return [...ids]
        .map((id) => this.resolveCercaById(id))
        .filter(Boolean);
    },
    previewCercasGeojson() {
      const layers = [];
      this.previewCercasCatalog.forEach((cerca) => {
        if (Number(cerca.is_circular) === 1) return;
        if (cerca.cod_tipo_cerca === "POI") return;
        const gj = this.parseGeojson(cerca.geojson);
        if (!gj) return;
        const nome = cerca.nome_cerca || cerca.cod_cerca || "";
        layers.push({
          id_cerca: cerca.id_cerca,
          geojson: gj,
          style: {
            color: PREVIEW_CERCA_COLOR,
            fillColor: PREVIEW_CERCA_COLOR,
            fillOpacity: 0.18,
            weight: 2,
            dashArray: "8 6",
            opacity: 0.95,
          },
          options: {
            onEachFeature: (feat, layer) => {
              const label = nome || (feat.properties && feat.properties.name) || "";
              if (label) layer.bindPopup(`<strong>${String(label)}</strong>`);
            },
          },
        });
      });
      return layers;
    },
    previewCercasCirculares() {
      const layers = [];
      this.previewCercasCatalog.forEach((cerca) => {
        if (Number(cerca.is_circular) !== 1) return;
        const r = Number(cerca.raio_mts);
        if (cerca.lat_centro == null || cerca.lng_centro == null || Number.isNaN(r) || r <= 0) return;
        layers.push({
          ...cerca,
          _strokeColor: PREVIEW_CERCA_COLOR,
          _fillColor: PREVIEW_CERCA_COLOR,
        });
      });
      return layers;
    },
    previewCercaMarkers() {
      const pins = [];
      this.previewCercasCatalog.forEach((cerca) => {
        if (cerca.cod_tipo_cerca === "POI") return;
        const gj =
          Number(cerca.is_circular) === 1 ? null : this.parseGeojson(cerca.geojson);
        const pos = this.cercaPinPosition(cerca, gj);
        if (!pos) return;
        pins.push({
          id_cerca: cerca.id_cerca,
          lat: pos[0],
          lng: pos[1],
          nome: cerca.nome_cerca || cerca.cod_cerca || "",
          color: PREVIEW_CERCA_COLOR,
        });
      });
      return pins;
    },
    rotasNoSentido() {
      const sentido = this.sentidoSelecionado;
      return (this.rawRotas || []).filter((r) => this.rotaPassaFiltroSentido(r, sentido));
    },
    hasDisplacementOnSentido() {
      return lineHasDisplacement(this.rawRotas, this.sentidoSelecionado);
    },
    terminusPinOptions() {
      return { pinStart: this.hasDisplacementOnSentido };
    },
    terminusFieldsLocked() {
      return isPinnedTerminusFence(this.itemCerca, this.terminusPinOptions);
    },
    previewRotaGeojson() {
      const id = this.itemRota && this.itemRota.id_rota;
      if (id == null) return null;
      const onLinha = (this.rawRotas || []).some((r) => Number(r.id_rota) === Number(id));
      // Já associada: o highlight vem de activeEditRouteId / mapRotas
      if (onLinha) return null;
      const rota = this.resolveRotaById(id);
      if (!rota) return null;
      const gj = this.rotaToGeojson(rota);
      if (!gj) return null;
      const nome = rota.nome_rota || rota.cod_rota || "";
      return {
        id_rota: id,
        geojson: gj,
        style: {
          color: PREVIEW_ROTA_COLOR,
          weight: 6,
          opacity: 0.95,
          dashArray: "10 6",
        },
        options: {
          onEachFeature: (feat, layer) => {
            const label = nome || (feat.properties && feat.properties.name) || "";
            if (label) layer.bindPopup(String(label));
          },
        },
      };
    },
    rotasDrawerFiltradas() {
      const sentido = this.sentidoSelecionado;
      const list = (this.rotasLinhaLocal || []).filter((r) => this.rotaPassaFiltroSentido(r, sentido));
      const q = (this.searchDrawerRotas || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((r) => {
        const fields = [
          r.nome_rota,
          r.cod_rota,
          r.descricao_tipo_rota,
          r.descricao_sentido,
          r.id_rota,
          r.id_tipo_rota,
          r.id_sentido,
        ];
        return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
      });
    },
    /** Linha com rota(s) de ida e sem rota de volta — habilita espelhar paradas da volta. */
    showGenerateReturnFromOutbound() {
      if (!this.isLinhasContext || !this.canEdit) return false;
      const rotas = this.rawRotas || this.rotasLinhaLocal || [];
      if (!rotas.length) return false;
      const sentidosIda = new Set([1, 3, 4]);
      const sentidosVolta = new Set([2, 5, 6]);
      const hasVoltaOrBoth = rotas.some((r) => {
        const s = Number(r.id_sentido);
        return sentidosVolta.has(s) || s === -1;
      });
      if (hasVoltaOrBoth) return false;
      return rotas.some((r) => sentidosIda.has(Number(r.id_sentido)));
    },
    cercasDrawerFiltradas() {
      const sentido = this.sentidoSelecionado;
      const list = (this.cercasLinhaLocal || [])
        .filter((c) => this.cercaPassaFiltroSentido(c, sentido))
        .slice()
        .sort((a, b) => {
          const oa = a.ordem_parada != null ? Number(a.ordem_parada) : Number.MAX_SAFE_INTEGER;
          const ob = b.ordem_parada != null ? Number(b.ordem_parada) : Number.MAX_SAFE_INTEGER;
          if (oa !== ob) return oa - ob;
          return String(a.nome_cerca || a.id_cerca).localeCompare(String(b.nome_cerca || b.id_cerca));
        });
      const q = (this.searchDrawerCercas || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((c) => {
        const fields = [c.nome_cerca, c.cod_cerca, c.descricao_sentido, c.ordem_parada, c.id_cerca];
        return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
      });
    },
    headersCercasReorder() {
      return [
        {
          text: this.$t("crudLinhas.form.fences.fence"),
          value: "nome_cerca",
          sortable: false,
        },
        {
          text: this.$t("config.headersConfigTable.order"),
          value: "order",
          sortable: false,
          align: "center",
          width: "120px",
        },
      ];
    },
    cercasReorderItems() {
      return (this.cercasDrawerFiltradas || []).map((cerca) => ({
        ...cerca,
        _reorderKey: `${cerca.id_cerca}-${cerca.id_sentido}`,
      }));
    },
    /** Traçado sugerido pela roteirização por cercas (prévia não gravada). */
    roteirizacaoPreviewLayer() {
      const preview = this.previewRoteirizacao;
      const plan = preview && preview.planResult;
      const gj = plan ? this.parseGeojson(plan.geojson) : null;
      if (!gj) return null;
      return {
        rev: this.previewRoteirizacaoRev,
        geojson: gj,
        style: {
          color: PREVIEW_ROTEIRIZACAO_COLOR,
          weight: 8,
          opacity: 0.85,
          lineCap: "round",
          lineJoin: "round",
        },
      };
    },
    /** Traçado atual da rota principal do sentido, para comparar com o sugerido. */
    rotaAtualPreviewLayer() {
      const preview = this.previewRoteirizacao;
      const gj = preview && preview.mainRota ? this.rotaToGeojson(preview.mainRota) : null;
      if (!gj) return null;
      return {
        geojson: gj,
        style: {
          color: ROUTE_COLORS[0],
          weight: 4,
          opacity: 1,
          dashArray: "8 9",
          lineCap: "butt",
        },
      };
    },
    roteirizacaoComparacao() {
      if (!this.previewRoteirizacao) return null;
      return buildRoteirizacaoComparison(this.previewRoteirizacao);
    },
    /**
     * Custo estimado antes/depois pela mesma fórmula da Roteirização
     * (fixo + mão de obra + variável × km), com as tarifas de `custoParamsRoteirizacao`.
     */
    roteirizacaoCustoComparacao() {
      const params = this.custoParamsRoteirizacao;
      const comp = this.roteirizacaoComparacao;
      if (!params || !comp) return null;
      const kmAntes = comp.distancia_antes != null ? metersToKm(comp.distancia_antes) : null;
      const kmDepois = comp.distancia_depois != null ? metersToKm(comp.distancia_depois) : null;
      if (kmAntes == null && kmDepois == null) return null;
      const antes = kmAntes != null ? computeRouteCost(kmAntes, params) : null;
      const depois = kmDepois != null ? computeRouteCost(kmDepois, params) : null;
      const totalAntes = antes ? antes.custo_total : null;
      const totalDepois = depois ? depois.custo_total : null;
      return {
        // Fixo e mão de obra não dependem da quilometragem: cobrados uma vez por viagem.
        custo_fixo: params.fixedCost,
        custo_mao_obra: params.laborCost,
        variavel_antes: antes ? antes.custo_variavel : null,
        variavel_depois: depois ? depois.custo_variavel : null,
        total_antes: totalAntes,
        total_depois: totalDepois,
        diferenca_total:
          totalAntes != null && totalDepois != null
            ? Number((totalDepois - totalAntes).toFixed(2))
            : null,
      };
    },
    /** Sentidos disponíveis para escolher antes de roteirizar por cercas. */
    sentidosRoteirizacaoItems() {
      return (this.sentidos || [])
        .filter((s) => s && s.id_sentido != null)
        .map((s) => ({
          value: s.id_sentido,
          text: s.descricao_sentido || String(s.id_sentido),
        }));
    },
    /**
     * Cercas que a roteirização vai usar no sentido escolhido. Ida e volta têm paradas
     * próprias; só as do sentido (mais as de ida e volta, `-1`) entram no cálculo.
     */
    cercasDoSentidoRoteirizacao() {
      const sentido = this.sentidoRoteirizacao;
      if (sentido == null || sentido === "") return [];
      return (this.rawCercas || [])
        .filter((c) => {
          if (!c) return false;
          if (Number(c.id_tipo_cerca) === FENCE_TYPE.POI) return false;
          return this.cercaPassaFiltroSentido(c, sentido);
        })
        .slice()
        .sort((a, b) => (Number(a.ordem_parada) || 0) - (Number(b.ordem_parada) || 0));
    },
    /** True quando o sentido escolhido é de volta: a rota parte do ponto final. */
    sentidoRoteirizacaoEhVolta() {
      return this.sentidoRoteirizacao != null && isSentidoVolta(this.sentidoRoteirizacao);
    },
    /** True quando a prévia aberta é de um sentido de volta. */
    previewEhVolta() {
      const preview = this.previewRoteirizacao;
      return !!preview && isSentidoVolta(preview.sentido);
    },
    /** Origem das tarifas + tipo de veículo usado, exibido no rodapé da tabela de custo. */
    custoFonteLabel() {
      const params = this.custoParamsRoteirizacao;
      if (!params) return "—";
      const fonte =
        params.fonte === "contrato"
          ? this.$t("crudLinhas.map.routeByFencesPreviewCostFromContract")
          : this.$t("crudLinhas.map.routeByFencesPreviewCostFromDefaults");
      if (!params.tipo) return fonte;
      const cap = Number(params.capacidade);
      return Number.isFinite(cap) && cap > 0
        ? `${fonte} · ${params.tipo} (${cap})`
        : `${fonte} · ${params.tipo}`;
    },
    /** Pinos numerados com a sequência sugerida (verde = terminais). */
    previewSequenciaMarkers() {
      const preview = this.previewRoteirizacao;
      if (!preview) return [];
      const pins = [];
      (preview.orderedCercas || []).forEach((cerca, i) => {
        const tipo = Number(cerca.id_tipo_cerca);
        if (tipo === FENCE_TYPE.POI || tipo === FENCE_TYPE.GARAGE) return;
        const lat = Number(cerca.lat_centro);
        const lng = Number(cerca.lng_centro);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const nome = cerca.nome_cerca || cerca.cod_cerca || cerca.id_cerca;
        pins.push({
          id_cerca: cerca.id_cerca,
          lat,
          lng,
          ordem: i + 1,
          color:
            tipo === FENCE_TYPE.START || tipo === FENCE_TYPE.END
              ? PREVIEW_ROTEIRIZACAO_COLOR
              : "#455A64",
          tooltip: `${i + 1} · ${nome} (${this.descricaoTipoCerca(tipo)})`,
        });
      });
      return pins;
    },
  },
  watch: {
    entityPreviewType: {
      immediate: true,
      handler() {
        this.syncEntityPreview();
      },
    },
    entityPreviewItem: {
      immediate: true,
      deep: false,
      handler() {
        this.syncEntityPreview();
      },
    },
    entityPreviewItems: {
      deep: false,
      handler() {
        this.syncEntityPreview();
      },
    },
    entityPreviewRotas: {
      deep: true,
      handler() {
        if (this.entityPreviewType === "unit" || this.entityPreviewType === "linha") {
          this.syncEntityPreview();
        }
      },
    },
    entityPreviewCerca: {
      deep: true,
      handler() {
        if (this.entityPreviewType === "unit" || this.entityPreviewType === "linha") {
          this.syncEntityPreview();
        }
      },
    },
    entityPreviewCercasLinha: {
      deep: true,
      handler() {
        if (this.entityPreviewType === "linha") {
          this.syncEntityPreview();
        }
      },
    },
    entityPreviewFitPreferCerca() {
      if (this.entityPreviewType === "linha") {
        this.syncEntityPreview();
      }
    },
    entityPreviewUnitGroups: {
      deep: true,
      handler() {
        if (this.entityPreviewType === "unit") this.syncEntityPreview();
      },
    },
    additionalLinhas: {
      deep: true,
      handler() {
        // Criação de nova linha (isLinhaCreatePreview) também mostra as demais linhas
        // marcadas/ativa anteriormente como pano de fundo, mesmo sendo um entity-preview.
        if (!this.isEntityPreview || this.isLinhaCreatePreview) this.syncAdditionalLinhas();
      },
    },
    drawerRotasOpen(val) {
      if (val) this.drawerCercasOpen = false;
      else {
        this.searchDrawerRotas = "";
      }
      this.invalidateMapSizeSoon();
    },
    drawerCercasOpen(val) {
      if (val) this.drawerRotasOpen = false;
      else {
        this.searchDrawerCercas = "";
        this.drawerCercasManagePanel = null;
        this.reorderFencesMode = false;
        if (this.editMode === "editFences") this.editMode = "view";
      }
      this.invalidateMapSizeSoon();
    },
    drawerLinhasOpen(val) {
      if (val) this.drawerCreateOpen = false;
      this.invalidateMapSizeSoon({ skipFocus: true });
      if (val) {
        this.$nextTick(() => {
          this.focusDrawerUnidadeAtual();
          this.focusUnidadeLinhasAssociate();
        });
      }
    },
    drawerCercaLinhasOpen(val) {
      if (val) this.drawerCreateOpen = false;
      else {
        this.clearCercaLinhasSelecionadas();
      }
      this.invalidateMapSizeSoon({ skipFocus: true });
      if (val) {
        this.$nextTick(() => this.focusDrawerCercaLinhasAtual());
      }
    },
    drawerCreateOpen(val) {
      if (val) {
        this.drawerLinhasOpen = false;
        this.drawerCercaLinhasOpen = false;
      } else {
        this.setPickLocationMode(false);
        if (this.editMode === "drawFence") this.editMode = "view";
        if (this.editMode === "drawRoute") this.editMode = "view";
        this.clearCreateDraftRota();
      }
      this.invalidateMapSizeSoon({ skipFocus: true });
      this.$nextTick(() => {
        this.rebuildFenceEditHandles();
        if (val && this.isLinhaCreatePreview) {
          this.syncEntityPreview();
        }
      });
    },
    drawerMoveFenceOpen(val) {
      if (!val) this.moveFenceStep = 1;
      this.invalidateMapSizeSoon({ skipFocus: true });
    },
    cercaCreateSelectRoute(val) {
      if (val) {
        this.rotasVisible = true;
        this.selectedMapRouteId = null;
        this.aplicarFiltroSentido();
      }
    },
    cercaCreateRouteId(val) {
      if (val != null && val !== "" && !this.cercaCreateSelectRoute) {
        this.setCercaCreateRouteId(val);
      }
    },
    drawerLinhasUnits(units) {
      if (!units.length) {
        this.drawerLinhasPage = 0;
        this.drawerLinhasOpen = false;
        return;
      }
      if (this.drawerLinhasPage > units.length - 1) {
        this.drawerLinhasPage = units.length - 1;
      }
    },
    drawerCercaLinhasItems(items) {
      if (!items.length) {
        this.drawerCercaLinhasPage = 0;
        this.drawerCercaLinhasOpen = false;
        this.clearCercaLinhasSelecionadas();
        return;
      }
      if (this.drawerCercaLinhasPage > items.length - 1) {
        this.drawerCercaLinhasPage = items.length - 1;
      }
    },
    cercaLinhasSelecionadas: {
      deep: true,
      handler() {
        this.syncCercaLinhasSelecionadas();
      },
    },
    linha: {
      immediate: true,
      handler(linha) {
        if (this.isEntityPreview) return;
        const id = linha && linha.id_linha != null ? Number(linha.id_linha) : null;
        if (!Number.isFinite(id) || id <= 0) {
          // Invalida requests em voo: após o destroy o vue-i18n zera `_i18n` e
          // qualquer `this.$t` residual vira "Cannot read properties of null (reading '_t')".
          this.loadedLinhaId = null;
          this.carregarDadosRequestSeq += 1;
          this.additionalLinhasRequestSeq += 1;
          this.resetState();
          this.syncAdditionalLinhas();
          return;
        }
        this.syncAdditionalLinhas();
        if (this.loadedLinhaId === id) return;
        this.loadedLinhaId = id;
        this.selectedMapRouteId = null;
        this.mapKey += 1;
        this.$nextTick(() => this.carregarDados({ fitMap: true }));
      },
    },
    sentidoSelecionado: {
      immediate: true,
      handler(val, oldVal) {
        if (this.isEntityPreview) return;
        if (this._isDestroyed || this._isBeingDestroyed) return;
        if (oldVal != null && val != null && Number(oldVal) !== Number(val)) {
          this.selectedMapRouteId = null;
        }
        this.itemCerca.id_sentido = val;
        this.itemRota.id_sentido = val;
        this.aplicarFiltroSentido({ fitMap: true });
      },
    },
    editMode(mode, prev) {
      this.teardownEditRoute();
      if (prev === "drawFence" && mode !== "drawFence") {
        const incompleteRedraw = !!this.redrawFenceSnapshot && !this._redrawFenceCompleted;
        this.teardownDrawFence();
        this.$emit("map-action", {
          context: this.mapContext,
          action: "draw-polygon-mode",
          active: false,
        });
        if (incompleteRedraw) {
          this.restoreRedrawFenceSnapshot();
        }
        this._redrawFenceCompleted = false;
      }
      if (prev === "drawRoute" && mode !== "drawRoute") {
        this.teardownDrawRoute();
        this.$emit("map-action", {
          context: this.mapContext,
          action: "draw-route-mode",
          active: false,
        });
      }
      if (mode === "editFences" || mode === "editRoute" || mode === "drawFence" || mode === "drawRoute") {
        this.setPickLocationMode(false);
      }
      if (mode === "editRoute") {
        this.setupEditRoute();
        this.appliedEditVias = [];
        if (this.isEntityPreview) {
          const previewRotaId =
            (this.selectedRotaDetail && this.selectedRotaDetail.id_rota) ||
            (this.entityPreviewItem && this.entityPreviewItem.id_rota) ||
            (this.entityPreviewItemsResolved &&
              this.entityPreviewItemsResolved[0] &&
              this.entityPreviewItemsResolved[0].id_rota) ||
            null;
          if (previewRotaId != null) {
            this.$nextTick(() => {
              if (this.editMode !== "editRoute") return;
              this.selectRouteForEdit(previewRotaId);
            });
          } else {
            this.activeEditRouteId = null;
            this.clearEditRouteVertexMarkers();
            if (this.isRotasPreview) this.refreshPreviewRouteLayers();
          }
        } else {
          this.itemRota.id_sentido = this.sentidoSelecionado;
          if (this.selectedMapRouteId != null) {
            const idRota = this.selectedMapRouteId;
            this.$nextTick(() => {
              if (this.editMode !== "editRoute") return;
              if (Number(this.activeEditRouteId) === Number(idRota)) {
                this.showEditRouteVertexMarkersForId(idRota);
                return;
              }
              this.selectRouteForEdit(idRota);
            });
          }
        }
      }
      if (mode === "drawFence") {
        this.setupDrawFence();
        this.$emit("map-action", {
          context: this.mapContext,
          action: "draw-polygon-mode",
          active: true,
        });
      }
      if (mode === "drawRoute") {
        this.setupDrawRoute();
        this.$emit("map-action", {
          context: this.mapContext,
          action: "draw-route-mode",
          active: true,
        });
      }
      if (mode === "editFences" && !this.isEntityPreview) {
        this.itemCerca.id_sentido = this.sentidoSelecionado;
      }
      if (
        mode === "editFences" ||
        prev === "editFences" ||
        mode === "drawFence" ||
        prev === "drawFence"
      ) {
        this.$nextTick(() => this.rebuildFenceEditHandles());
      }
      if (mode === "editFences") {
        this.$nextTick(() => {
          if (this.editingCercaId != null) return;
          const list = this.activeEditableCercas || [];
          if (list.length === 1 && list[0] && list[0].id_cerca != null) {
            this.selectFenceForFormatEdit(list[0].id_cerca, list[0].id_sentido);
          }
        });
      }
      // No preview não há drawer nem seleção pendente: manter os detalhes carregados.
      if (this.isEntityPreview) {
        if (prev === "editRoute" && mode !== "editRoute" && this.isRotasPreview) {
          this.refreshPreviewRouteLayers();
        }
        return;
      }
      if (prev === "editFences" && mode !== "editFences") {
        this.itemCerca.id_cerca = null;
        this.editingCerca = false;
        this.editingCercaId = null;
        this.editingCercaSentido = null;
        this.selectedCercaDetail = null;
        this.$nextTick(() => this.syncCercaSelect(null));
      }
      if (prev === "editRoute" && mode !== "editRoute") {
        this.itemRota.id_rota = null;
        this.selectedRotaDetail = null;
        this.$nextTick(() => this.syncRotaSelect(null));
      }
    },
    previewLayerRev() {
      this.$nextTick(() => this.rebuildFenceEditHandles());
    },
    createDraftCerca: {
      deep: true,
      handler() {
        // Durante o arrasto do handle de raio, reconstruir markers destrói o
        // marker Leaflet no meio do drag e congela a UI.
        if (this._fenceHandleDragging || this.fenceDragRecalculating) return;
        this.$nextTick(() => {
          if (this._fenceHandleDragging || this.fenceDragRecalculating) return;
          this.rebuildFenceEditHandles();
        });
      },
    },
    "itemCerca.id_cerca"(id) {
      if (id != null) {
        this.$nextTick(() => this.focusSelectedCerca());
      }
    },
    "itemRota.id_rota"(id) {
      if (id != null) {
        this.$nextTick(() => this.focusRotaOnMap(id));
      }
    },
    mostrarRotaAtualPreview() {
      this.trazerRotaAtualParaFrente();
    },
    mostrarRotaNovaPreview() {
      this.trazerRotaAtualParaFrente();
    },
    previewRoteirizacaoRev() {
      this.trazerRotaAtualParaFrente();
    },
    fixarPontoInicialRoteirizacao(val) {
      this.persistirFixarPontoInicial(val);
      // Com a prévia aberta, mudar a regra exige refazer o cálculo para o usuário
      // ver na hora o efeito de soltar (ou fixar) o ponto inicial.
      if (this.dialogRoteirizarPorCercas) this.recalcularPreviewRoteirizacao();
    },
  },
  created() {
    this.fixarPontoInicialRoteirizacao = this.lerFixarPontoInicial();
    this._drawFenceMarkers = [];
    this._drawFenceOutline = null;
    this._drawRouteMarkers = [];
    this._drawRouteOutline = null;
    /** Coordenadas [lng,lat] completas da rota sob edição de vértices. */
    this._editRouteLngLats = null;
    /** Snapshot da geometria salva ao entrar na edição (para descartar sem Aplicar). */
    this._editRouteBaseline = null;
    this._routeVertexDragging = false;
    this._fenceHandleDragging = false;
    /** @type {Record<string, import('leaflet').Circle>} camadas Leaflet (não reativas). */
    this._circularCercaLayers = Object.create(null);
    /** Ícones do handle cacheados — recriar DivIcon a cada render trava o drag. */
    this._fenceHandleIcons = Object.create(null);
    /** Bearing temporário durante o drag (não reativo). */
    this._fenceRadiusBearingsDrag = Object.create(null);
  },
  beforeDestroy() {
    this._fenceHandleDragging = false;
    this._circularCercaLayers = Object.create(null);
    this.teardownPickLocation();
    this.teardownEditRoute();
    this.teardownDrawFence();
    this.teardownDrawRoute();
    if (this.routeClickDebounce) clearTimeout(this.routeClickDebounce);
  },
  methods: {
    invalidateMapSizeSoon(opts = {}) {
      this.$nextTick(() => {
        if (this.map && typeof this.map.invalidateSize === "function") {
          this.map.invalidateSize({ animate: false });
        }
        if (!opts.skipFocus) this.scheduleFocusMap();
      });
      setTimeout(() => {
        if (this.map && typeof this.map.invalidateSize === "function") {
          this.map.invalidateSize({ animate: false });
        }
      }, 280);
    },
    onMapControlSelect(actionId) {
      if (actionId === "toggle-create-drawer") {
        this.toggleCreateDrawer();
        return;
      }
      if (this.isUnidadesPreview && actionId === "toggle-lines-drawer") {
        this.drawerLinhasOpen = !this.drawerLinhasOpen;
        return;
      }
      if (this.isCercasPreview && actionId === "toggle-cerca-lines-drawer") {
        this.drawerCercaLinhasOpen = !this.drawerCercaLinhasOpen;
        return;
      }
      if (this.isLinhasContext) {
        switch (actionId) {
          case "create-linha":
            this.$emit("map-action", {
              context: this.mapContext,
              action: "create-linha",
              item: this.entityPreviewItem,
            });
            return;
          case "toggle-routes-drawer":
            this.drawerRotasOpen = !this.drawerRotasOpen;
            return;
          case "toggle-fences-drawer":
            this.drawerCercasOpen = !this.drawerCercasOpen;
            if (this.drawerCercasOpen && this.canEdit && this.editMode !== "editFences") {
              this.editMode = "editFences";
            }
            return;
          case "create-fence-on-line":
            this.reorderFencesMode = false;
            this.openCreateDrawer();
            return;
          case "edit-fence-drag":
            this.reorderFencesMode = false;
            this.editMode = this.editMode === "editFences" ? "view" : "editFences";
            if (this.editMode === "editFences") {
              this.drawerCercasOpen = true;
              this.drawerCercasManagePanel = null;
            }
            return;
          case "edit-route-trace":
            this.reorderFencesMode = false;
            this.editMode = this.editMode === "editRoute" ? "view" : "editRoute";
            if (this.editMode === "editRoute") {
              this.drawerRotasOpen = false;
            }
            return;
          case "route-by-fences":
            this.roteirizarPorCercas();
            return;
          case "change-fence-order":
            this.reorderFencesMode = !this.reorderFencesMode;
            if (this.reorderFencesMode) {
              this.editMode = "view";
              this.drawerCercasOpen = true;
              this.drawerCercasManagePanel = null;
            }
            return;
          case "move-fence-to-line":
            this.openMoveFenceDrawer();
            return;
          default:
            return;
        }
      }
      // Previews de rota/cerca: arrasto e edição de traçado são tratados aqui mesmo.
      if (this.isEntityPreview) {
        if (this.mapContext === "cercas" && actionId === "edit-fence-drag") {
          this.editMode = this.editMode === "editFences" ? "view" : "editFences";
          return;
        }
        if (this.mapContext === "rotas" && actionId === "edit-route-trace") {
          this.editMode = this.editMode === "editRoute" ? "view" : "editRoute";
          return;
        }
      }
      this.$emit("map-action", {
        context: this.mapContext,
        action: actionId,
        item: this.entityPreviewItem,
      });
    },
    openLinhasDrawer() {
      if (!this.isUnidadesPreview) return;
      this.drawerCreateOpen = false;
      this.drawerLinhasOpen = true;
      this.$nextTick(() => this.focusUnidadeLinhasAssociate());
    },
    toggleCreateDrawer() {
      if (!this.showCreateEntityDrawer) return;
      if (this.drawerCreateOpen) {
        this.closeCreateDrawer();
        return;
      }
      this.openCreateDrawer();
    },
    openCreateDrawer({ silent } = {}) {
      if (!this.showCreateEntityDrawer) return;
      this.drawerLinhasOpen = false;
      const alreadyOpen = this.drawerCreateOpen;
      this.drawerCreateOpen = true;
      if (silent || alreadyOpen) return;
      const action = this.isCercasPreview
        ? "create-fence"
        : this.isRotasPreview
          ? "create-route"
          : this.isLinhaCreatePreview
            ? "create-linha"
            : this.isLinhasContext
              ? "create-fence-on-line"
              : "create-unit";
      this.$emit("map-action", {
        context: this.mapContext,
        action,
        item: this.entityPreviewItem,
        // Cria cerca na linha: reaproveita a rota já selecionada no mapa.
        idRota: this.isLinhasContext ? this.selectedMapRouteId : null,
      });
    },
    closeCreateDrawer({ silent } = {}) {
      const wasOpen = this.drawerCreateOpen;
      this.drawerCreateOpen = false;
      if (silent || !wasOpen) return;
      this.$emit("map-action", {
        context: this.mapContext,
        action: "close-create",
        item: this.entityPreviewItem,
      });
    },
    previewAndFocusCerca(cerca, { focus = true } = {}) {
      if (!cerca) return;
      // Sempre clona: se apontar para editedItem do formulário, o watcher deep
      // de createDraftCerca dispara a cada tecla e trava a tela.
      const snapshot = { ...cerca };
      const existingId =
        snapshot.id_cerca != null &&
        snapshot.id_cerca !== DRAFT_CERCA_ID &&
        !snapshot._createPreview
          ? Number(snapshot.id_cerca)
          : null;
      if (Number.isFinite(existingId) && existingId > 0 && !this.drawerCreateOpen) {
        this.$set(this.cercaDetailCache, existingId, {
          ...(this.cercaDetailCache[existingId] || {}),
          ...snapshot,
        });
        this.rebuildPreviewFenceLayers(snapshot);
        if (focus) this.focusCercaGeometry(snapshot);
        return;
      }
      this.createDraftCerca = snapshot;
      this.applyCreateDraftLayers(snapshot);
      if (focus) {
        this.focusCercaGeometry({
          ...snapshot,
          id_cerca: snapshot.id_cerca == null ? DRAFT_CERCA_ID : snapshot.id_cerca,
        });
      }
    },
    applyCreateDraftLayers(cerca) {
      if (!cerca) return;
      this.mapCercasGeojson = (this.mapCercasGeojson || []).filter((c) => !c._createPreview);
      this.mapCercasCirculares = (this.mapCercasCirculares || []).filter((c) => !c._createPreview);
      this.mapCercaMarkers = (this.mapCercaMarkers || []).filter((c) => !c._createPreview);
      const tagged = { ...cerca, _createPreview: true };
      if (tagged.id_cerca == null) tagged.id_cerca = DRAFT_CERCA_ID;
      this.applyCercaToMapLayers(tagged);
      const hasPreviewGeom =
        (this.mapCercasCirculares || []).some((c) => c._createPreview) ||
        (this.mapCercasGeojson || []).some((c) => c._createPreview) ||
        (this.mapCercaMarkers || []).some((c) => c._createPreview);
      if (!hasPreviewGeom) {
        this.pushCercaMarker(tagged, PREVIEW_CERCA_COLOR, tagged.nome_cerca || tagged.cod_cerca || "");
      }
    },
    clearCreateDraft() {
      this.createDraftCerca = null;
      this.mapCercasGeojson = (this.mapCercasGeojson || []).filter((c) => !c._createPreview);
      this.mapCercasCirculares = (this.mapCercasCirculares || []).filter((c) => !c._createPreview);
      this.mapCercaMarkers = (this.mapCercaMarkers || []).filter((c) => !c._createPreview);
    },
    setPickLocationMode(on) {
      const next = !!on;
      if (next === this.pickLocationMode) return;
      this.pickLocationMode = next;
      if (next) {
        this.editMode = "view";
        this.setupPickLocation();
      } else {
        this.teardownPickLocation();
      }
      this.$emit("map-action", {
        context: this.mapContext,
        action: "pick-location-mode",
        active: next,
      });
    },
    setupPickLocation() {
      if (!this.map) return;
      this.teardownPickLocation();
      this.pickLocationHandler = (e) => {
        if (!this.pickLocationMode || !e || !e.latlng) return;
        this.$emit("map-action", {
          context: this.mapContext,
          action: "pick-location",
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          item: this.entityPreviewItem,
        });
      };
      this.map.on("click", this.pickLocationHandler);
    },
    teardownPickLocation() {
      if (this.map && this.pickLocationHandler) {
        this.map.off("click", this.pickLocationHandler);
      }
      this.pickLocationHandler = null;
    },
    focusCercaGeometry(cerca) {
      if (!cerca) return;
      const group = { cerca, rotas: [] };
      const points = this.collectUnitGroupPoints(group);
      if (points.length) {
        this.$nextTick(() => this.fitMapToPoints(points));
        return;
      }
      if (cerca.id_cerca != null) this.focusCercaOnMap(cerca.id_cerca);
    },
    focusUnidadeLinhasAssociate() {
      const table = this.$refs.unidadeLinhasDrawer;
      if (table && typeof table.focusAssociate === "function") table.focusAssociate();
    },
    setDrawerLinhasPage(page) {
      const units = this.drawerLinhasUnits;
      if (!units.length) return;
      const next = Math.min(Math.max(0, Number(page) || 0), units.length - 1);
      if (next === this.drawerLinhasPage) {
        this.focusDrawerUnidadeAtual();
        return;
      }
      this.drawerLinhasPage = next;
      this.$nextTick(() => this.focusDrawerUnidadeAtual());
    },
    onUnidadeLinhasDrawerChanged() {
      this.$emit("unidade-linhas-changed");
    },
    onUnidadeLinhaSelect(linha) {
      if (!linha || linha.id_linha == null) return;
      this.focusLinhaAssociadaOnMap(linha.id_linha);
    },
    focusDrawerUnidadeAtual() {
      this.focusUnidadeOnMap(this.drawerLinhasUnidadeAtual);
    },
    setDrawerCercaLinhasPage(page) {
      const items = this.drawerCercaLinhasItems;
      if (!items.length) return;
      const next = Math.min(Math.max(0, Number(page) || 0), items.length - 1);
      if (next === this.drawerCercaLinhasPage) {
        this.focusDrawerCercaLinhasAtual();
        return;
      }
      this.clearCercaLinhasSelecionadas();
      this.drawerCercaLinhasPage = next;
      this.$nextTick(() => this.focusDrawerCercaLinhasAtual());
    },
    focusDrawerCercaLinhasAtual() {
      const cerca = this.drawerCercaLinhasAtual;
      if (!cerca || cerca.id_cerca == null) return;
      this.focusCercaOnMap(cerca.id_cerca);
    },
    clearCercaLinhasSelecionadas() {
      this.cercaLinhasRequestSeq += 1;
      this.cercaLinhasSelecionadas = [];
      this.mapCercaLinhasSelecionadasRotas = [];
    },
    async syncCercaLinhasSelecionadas() {
      const selected = Array.isArray(this.cercaLinhasSelecionadas)
        ? this.cercaLinhasSelecionadas
        : [];
      const requestSeq = ++this.cercaLinhasRequestSeq;
      if (!selected.length) {
        this.mapCercaLinhasSelecionadasRotas = [];
        return;
      }
      const idsSet = new Set(
        selected
          .map((l) => (l && l.id_linha != null ? Number(l.id_linha) : null))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
      const ids = [...idsSet];
      if (!ids.length) {
        this.mapCercaLinhasSelecionadasRotas = [];
        return;
      }
      try {
        const rotasRes = await apiController.request(apiController.endpoints.getRotasporlinha, {
          data: ids,
          silent: true,
        });
        if (
          this._isDestroyed ||
          this._isBeingDestroyed ||
          requestSeq !== this.cercaLinhasRequestSeq
        ) {
          return;
        }
        const sentidoByLinha = new Map();
        selected.forEach((item) => {
          if (!item || item.id_linha == null) return;
          const idLinha = Number(item.id_linha);
          if (!Number.isFinite(idLinha)) return;
          const sentidos = sentidoByLinha.get(idLinha) || new Set();
          const sentido =
            item.id_sentido !== undefined && item.id_sentido !== null
              ? Number(item.id_sentido)
              : null;
          sentidos.add(sentido);
          sentidoByLinha.set(idLinha, sentidos);
        });
        const matchesSentido = (rota) => {
          const idLinha = Number(rota && rota.id_linha);
          const allowed = sentidoByLinha.get(idLinha);
          if (!allowed || !allowed.size) return false;
          const rotaSentido =
            rota.id_sentido !== undefined && rota.id_sentido !== null
              ? Number(rota.id_sentido)
              : null;
          if (allowed.has(-1)) return true;
          return allowed.has(rotaSentido);
        };
        const seenRotas = new Set();
        this.mapCercaLinhasSelecionadasRotas = (Array.isArray(rotasRes.data) ? rotasRes.data : [])
          .filter((r) => {
            const id = Number(r && r.id_rota);
            if (!Number.isFinite(id) || seenRotas.has(id)) return false;
            if (!matchesSentido(r)) return false;
            seenRotas.add(id);
            return true;
          })
          .map((rota, index) => {
            const gj = this.rotaToGeojson(rota);
            if (!gj) return null;
            const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
            const nome = rota.nome_rota || rota.cod_rota || "";
            return {
              ...rota,
              geojson: gj,
              style: { color, weight: 5, opacity: 0.9 },
              options: {
                onEachFeature: (feat, layer) => {
                  const label = nome || (feat.properties && feat.properties.name) || "";
                  if (label) layer.bindPopup(String(label));
                },
              },
            };
          })
          .filter(Boolean);
      } catch (e) {
        if (
          this._isDestroyed ||
          this._isBeingDestroyed ||
          requestSeq !== this.cercaLinhasRequestSeq
        ) {
          return;
        }
        this.mapCercaLinhasSelecionadasRotas = [];
        EventBus.$emit("show-error", { error: e });
      }
    },
    resolveUnitPreviewGroup(unit) {
      if (!unit || unit.id_unidade == null) return null;
      const id = Number(unit.id_unidade);
      const groups = this.entityPreviewUnitGroups || [];
      return (
        groups.find((g) => g && g.unit && Number(g.unit.id_unidade) === id) ||
        null
      );
    },
    collectUnitGroupPoints(group) {
      const pts = [];
      if (!group) return pts;
      const cerca = group.cerca;
      if (cerca) {
        if (Number(cerca.is_circular) === 1) {
          const lat = Number(cerca.lat_centro);
          const lng = Number(cerca.lng_centro);
          const r = Number(cerca.raio_mts);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            if (Number.isFinite(r) && r > 0) {
              const dLat = r / 111320;
              const cosLat = Math.cos((lat * Math.PI) / 180);
              const dLng = cosLat !== 0 ? r / (111320 * cosLat) : dLat;
              pts.push([lat - dLat, lng - dLng], [lat + dLat, lng + dLng]);
            } else {
              pts.push([lat, lng]);
            }
          }
        } else {
          const gj = this.parseGeojson(cerca.geojson);
          if (gj) this.collectGeojsonLatLngs(gj, pts);
        }
        const lat = Number(cerca.lat_centro);
        const lng = Number(cerca.lng_centro);
        if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
      }
      (group.rotas || []).forEach((rota) => {
        const gj = this.rotaToGeojson(rota);
        if (gj) this.collectGeojsonLatLngs(gj, pts);
      });
      return pts;
    },
    fitMapToPoints(points) {
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      if (!map || !points || !points.length) return;
      try {
        map.invalidateSize({ animate: false });
        if (points.length === 1) {
          const z = this.normalizeMapZoom(14);
          this.currentCenter = points[0];
          this.zoom = z;
          map.setView(points[0], z, { animate: false });
          return;
        }
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 16 });
        const c = map.getCenter();
        this.currentCenter = [c.lat, c.lng];
        this.zoom = this.normalizeMapZoom(map.getZoom());
      } catch (e) {
        /* ignore */
      }
    },
    focusUnidadeOnMap(unit) {
      if (!unit) return;
      const group = this.resolveUnitPreviewGroup(unit);
      const points = this.collectUnitGroupPoints(group);
      if (!points.length) return;
      this.$nextTick(() => this.fitMapToPoints(points));
    },
    focusLinhaAssociadaOnMap(idLinha) {
      if (idLinha == null) return;
      this.linhasAssociadasVisible = true;
      const id = Number(idLinha);
      const points = [];
      const collectFromRotas = (rotas) => {
        (rotas || []).forEach((rota) => {
          if (!rota || Number(rota.id_linha) !== id || !rota.geojson) return;
          this.collectGeojsonLatLngs(rota.geojson, points);
        });
      };
      collectFromRotas(this.mapRotas);
      if (!points.length) collectFromRotas(this.outrasLinhasMapRotas);
      if (!points.length) {
        // Fallback: rotas do grupo da unidade atual ainda não filtradas no mapa.
        const group = this.resolveUnitPreviewGroup(this.drawerLinhasUnidadeAtual);
        (group && group.rotas ? group.rotas : []).forEach((rota) => {
          if (!rota || Number(rota.id_linha) !== id) return;
          const gj = this.rotaToGeojson(rota);
          if (gj) this.collectGeojsonLatLngs(gj, points);
        });
      }
      if (!points.length) return;
      this.$nextTick(() => this.fitMapToPoints(points));
    },
    hasMapGeometry() {
      return (
        (this.mapRotas || []).length > 0 ||
        (this.mapCercasGeojson || []).length > 0 ||
        (this.mapCercasCirculares || []).length > 0 ||
        (this.mapCercaMarkers || []).length > 0
      );
    },
    applyCercaToMapLayers(cerca, color = PREVIEW_CERCA_COLOR) {
      if (!cerca || cerca.cod_tipo_cerca === "POI") return;
      const nome = cerca.nome_cerca || cerca.cod_cerca || "";
      if (Number(cerca.is_circular) === 1) {
        const r = Number(cerca.raio_mts);
        if (cerca.lat_centro == null || cerca.lng_centro == null || Number.isNaN(r) || r <= 0) return;
        this.mapCercasCirculares.push({ ...cerca, _strokeColor: color, _fillColor: color });
        this.pushCercaMarker(cerca, color, nome);
        return;
      }
      const gj = this.parseGeojson(cerca.geojson);
      if (!gj) return;
      this.mapCercasGeojson.push({
        ...cerca,
        geojson: gj,
        style: { color, fillColor: color, fillOpacity: 0.22, weight: 2 },
        options: {
          onEachFeature: (feat, layer) => {
            const label = nome || (feat.properties && feat.properties.name) || "";
            if (label) layer.bindPopup(`<strong>${String(label)}</strong>`);
          },
        },
      });
      this.pushCercaMarker(cerca, color, nome, gj);
    },
    async applyEntityPreviewRoute(items) {
      const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
      const resolved = await Promise.all(
        list.map(async (item) => {
          if (item.id_rota == null) return item;
          // Respeita rascunho local (traçado removido / vértices arrastados).
          if (
            this.editRouteRedrawing &&
            this.activeEditRouteId != null &&
            Number(this.activeEditRouteId) === Number(item.id_rota)
          ) {
            return (
              (this.selectedRotaDetail &&
              Number(this.selectedRotaDetail.id_rota) === Number(item.id_rota)
                ? this.selectedRotaDetail
                : null) || { ...item, geojson: null, shape: null }
            );
          }
          if (
            this.editRouteGeometryDirty &&
            this.selectedRotaDetail &&
            Number(this.selectedRotaDetail.id_rota) === Number(item.id_rota)
          ) {
            return this.selectedRotaDetail;
          }
          return (await this.ensureRotaDetail(item.id_rota)) || item;
        })
      );
      // Seed com geometria resolvida; refresh aplica estilo + clique no traçado.
      this.mapRotas = resolved
        .map((rota) => {
          const gj = this.rotaToGeojson(rota);
          if (!gj) return null;
          return { ...rota, geojson: gj };
        })
        .filter(Boolean);
      if (!this.mapRotas.length) {
        if (!this.editRouteRedrawing) {
          this.mensagemVazia = this.$t("mapPreview.unavailable");
        } else {
          this.mensagemVazia = "";
        }
        return;
      }
      this.refreshPreviewRouteLayers();
    },
    /**
     * Reconstrói as polylines do preview de rotas com destaque da rota em edição
     * e clique no traçado para selecionar (mesmo fluxo da Roteirização).
     */
    refreshPreviewRouteLayers() {
      if (!this.isRotasPreview) return;
      const items = this.entityPreviewItemsResolved || [];
      const multi = items.length > 1;
      const prevById = {};
      (this.mapRotas || []).forEach((r) => {
        if (r && r.id_rota != null) prevById[Number(r.id_rota)] = r;
      });
      const list = [];
      items.forEach((item, idx) => {
        if (!item || item.id_rota == null) return;
        const id = Number(item.id_rota);
        // Traçado removido em rascunho: não redesenhar a polyline antiga.
        if (
          this.editRouteRedrawing &&
          this.activeEditRouteId != null &&
          Number(this.activeEditRouteId) === id &&
          (!Array.isArray(this._editRouteLngLats) || this._editRouteLngLats.length < 2)
        ) {
          return;
        }
        const rota =
          (this.selectedRotaDetail && Number(this.selectedRotaDetail.id_rota) === id
            ? this.selectedRotaDetail
            : null) ||
          (this.entityPreviewItem && Number(this.entityPreviewItem.id_rota) === id
            ? this.entityPreviewItem
            : null) ||
          prevById[id] ||
          item;
        const gj = this.rotaToGeojson(rota);
        if (!gj) return;
        const editing =
          this.editMode === "editRoute" &&
          this.activeEditRouteId != null &&
          Number(this.activeEditRouteId) === id;
        const baseColor = multi ? ROUTE_COLORS[idx % ROUTE_COLORS.length] : PREVIEW_ROTA_COLOR;
        const nome = rota.nome_rota || rota.cod_rota || "";
        const idRota = rota.id_rota;
        list.push({
          ...rota,
          geojson: gj,
          style: {
            color: editing ? EDIT_ROUTE_COLOR : baseColor,
            weight: editing ? 8 : 6,
            opacity: editing ? 1 : 0.95,
          },
          options: {
            onEachFeature: (feat, layer) => {
              const label = nome || (feat.properties && feat.properties.name) || "";
              if (label) layer.bindPopup(String(label));
              layer.on("click", (ev) => {
                if (ev) {
                  L.DomEvent.stopPropagation(ev);
                  if (ev.originalEvent) L.DomEvent.preventDefault(ev);
                }
                this.lastPolylineClickMs = Date.now();
                if (this.editMode !== "editRoute") return;
                if (this.routeClickDebounce) clearTimeout(this.routeClickDebounce);
                this.routeClickDebounce = setTimeout(() => {
                  this.selectRouteForEdit(idRota);
                }, 250);
              });
            },
          },
        });
      });
      this.previewLayerRev += 1;
      this.mapRotas = list.map((r) => ({ ...r, _rev: this.previewLayerRev }));
      if (!this.mapRotas.length) {
        // Durante redesenho após "Remover traçado", não mostrar alerta (muda o layout e “move” o mapa).
        if (!this.editRouteRedrawing) {
          this.mensagemVazia = this.$t("mapPreview.unavailable");
        } else {
          this.mensagemVazia = "";
        }
      } else {
        this.mensagemVazia = "";
      }
    },
    async applyEntityPreviewFence(items) {
      const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
      const multi = list.length > 1;
      const resolved = await Promise.all(
        list.map(async (item) => {
          if (item.id_cerca != null) {
            this.$set(this.cercaDetailCache, Number(item.id_cerca), { ...item });
            return (await this.ensureCercaDetail(item.id_cerca)) || item;
          }
          return item;
        })
      );
      resolved.forEach((cerca, idx) => {
        const color = multi ? CERCA_COLORS[idx % CERCA_COLORS.length] : PREVIEW_CERCA_COLOR;
        this.applyCercaToMapLayers(cerca, color);
      });
      if (!this.hasMapGeometry()) {
        this.mensagemVazia = this.$t("mapPreview.unavailable");
      }
    },
    applyEntityPreviewUnit() {
      const groups =
        this.entityPreviewUnitGroups && this.entityPreviewUnitGroups.length
          ? this.entityPreviewUnitGroups
          : [{ rotas: this.entityPreviewRotas, cerca: this.entityPreviewCerca }];
      const multi = groups.length > 1;
      groups.forEach((group, gIdx) => {
        const groupColor = multi ? ROUTE_COLORS[gIdx % ROUTE_COLORS.length] : null;
        (group.rotas || []).forEach((rota, idx) => {
          const gj = this.rotaToGeojson(rota);
          if (!gj) return;
          const color = groupColor || ROUTE_COLORS[idx % ROUTE_COLORS.length];
          const nome = rota.nome_rota || rota.cod_rota || "";
          this.mapRotas.push({
            ...rota,
            geojson: gj,
            style: { color, weight: 5, opacity: 0.9 },
            options: {
              onEachFeature: (feat, layer) => {
                const label = nome || (feat.properties && feat.properties.name) || "";
                if (label) layer.bindPopup(String(label));
              },
            },
          });
        });
        if (group.cerca) {
          this.applyCercaToMapLayers(group.cerca, groupColor || PREVIEW_CERCA_COLOR);
        }
      });
      if (!this.hasMapGeometry()) {
        this.mensagemVazia = this.$t("crudUnidades.form.fields.unidadeMapEmpty");
      }
    },
    async syncEntityPreview() {
      if (!this.isEntityPreview) {
        return;
      }
      // Remoção/redesenho local do traçado: não recarregar geometria do banco
      // (ensureRotaDetail / applyEntityPreviewRoute trariam a rota de volta).
      if (this.editRouteRedrawing && this.isRotasPreview) {
        this.refreshPreviewRouteLayers();
        return;
      }

      const requestSeq = ++this.entityPreviewRequestSeq;
      const type = this.entityPreviewType;

      // Criação de linha: camadas vêm das props entityPreviewRotas / entityPreviewCerca.
      if (type === "linha") {
        this.loading = true;
        this.mensagemVazia = "";
        this.rawRotas = [];
        this.rawCercas = [];
        this.mapRotas = [];
        this.mapCercasGeojson = [];
        this.mapCercasCirculares = [];
        this.mapCercaMarkers = [];
        // Mostra a(s) linha(s) marcada(s)/ativa anteriormente como pano de fundo,
        // para o mapa não "esvaziar" ao iniciar o cadastro de uma linha nova.
        this.syncAdditionalLinhas();
        try {
          const rotasRaw = this.entityPreviewRotas || [];
          const resolved = await Promise.all(
            rotasRaw.map(async (item) => {
              if (!item) return null;
              if (item.geojson || item.shape) return item;
              if (item.id_rota == null) return item;
              return (await this.ensureRotaDetail(item.id_rota)) || item;
            })
          );
          this.linhaCreatePreviewRotas = resolved.filter(Boolean);
          let cerca = this.entityPreviewCerca || null;
          if (cerca && cerca.id_cerca != null && !cerca.geojson && cerca.lat_centro == null) {
            cerca = (await this.ensureCercaDetail(cerca.id_cerca)) || cerca;
          }
          this.linhaCreatePreviewCerca = cerca;
          const assoc = this.entityPreviewCercasLinha || [];
          const resolvedAssoc = await Promise.all(
            assoc.map(async (item) => {
              if (!item || item.id_cerca == null) return null;
              const detail = await this.ensureCercaDetail(item.id_cerca);
              return detail ? { ...item, ...detail } : item;
            })
          );
          this.linhaCreatePreviewCercasLinha = resolvedAssoc.filter(Boolean);
          await this.applyLinhaCreatePreviewLayers({
            fit: true,
            preferCerca: this.entityPreviewFitPreferCerca,
          });
        } catch (e) {
          if (requestSeq === this.entityPreviewRequestSeq) {
            this.mensagemVazia = this.$t("mapPreview.unavailable");
          }
        }
        if (requestSeq !== this.entityPreviewRequestSeq) return;
        this.loading = false;
        this.$nextTick(() => this.scheduleFocusMap());
        return;
      }

      const items = this.entityPreviewItemsResolved;
      if (!items.length) {
        if (requestSeq !== this.entityPreviewRequestSeq) return;
        this.resetState();
        if (this.drawerCreateOpen && this.createDraftCerca) {
          this.applyCreateDraftLayers(this.createDraftCerca);
        }
        if (this.drawerCreateOpen && this.createDraftRota) {
          this.applyCreateDraftRouteLayers(this.createDraftRota);
        }
        return;
      }

      this.loading = true;
      this.mensagemVazia = "";
      this.rawRotas = [];
      this.rawCercas = [];
      this.mapRotas = [];
      this.mapCercasGeojson = [];
      this.mapCercasCirculares = [];
      this.mapCercaMarkers = [];

      try {
        if (type === "route") {
          await this.applyEntityPreviewRoute(items);
        } else if (type === "fence") {
          await this.applyEntityPreviewFence(items);
        } else if (type === "unit") {
          this.applyEntityPreviewUnit();
        }
      } catch (e) {
        if (requestSeq === this.entityPreviewRequestSeq) {
          this.mensagemVazia = this.$t("mapPreview.unavailable");
        }
      }
      if (requestSeq !== this.entityPreviewRequestSeq) return;
      if (this.drawerCreateOpen && this.createDraftCerca) {
        this.applyCreateDraftLayers(this.createDraftCerca);
      }
      if (this.drawerCreateOpen && this.createDraftRota) {
        this.applyCreateDraftRouteLayers(this.createDraftRota);
      }
      this.loading = false;
      this.$nextTick(() => {
        this.scheduleFocusMap();
        this.rebuildFenceEditHandles();
      });
    },
    /**
     * Atualiza as rotas exibidas no mapa durante a criação de linha.
     * @param {Array<object|number>} rotasOuIds
     */
    async setLinhaCreatePreviewRotas(rotasOuIds) {
      const list = Array.isArray(rotasOuIds) ? rotasOuIds : [];
      const resolved = [];
      for (const item of list) {
        if (item == null) continue;
        if (typeof item === "object") {
          const detail =
            item.geojson || item.shape
              ? item
              : (await this.ensureRotaDetail(item.id_rota)) || item;
          if (detail) resolved.push(detail);
          continue;
        }
        const detail = await this.ensureRotaDetail(item);
        if (detail) resolved.push(detail);
      }
      this.linhaCreatePreviewRotas = resolved;
      if (!this.isLinhaCreatePreview) return;
      await this.applyLinhaCreatePreviewLayers({ fit: true });
    },
    /**
     * Mostra a cerca-centro da unidade e centraliza o mapa nela.
     * @param {object|null} unit
     */
    async setLinhaCreatePreviewUnidade(unit) {
      this.linhaCreatePreviewCerca = null;
      if (unit && unit.id_cerca_centro != null && unit.id_cerca_centro !== "") {
        const cerca = await this.ensureCercaDetail(unit.id_cerca_centro);
        if (cerca) this.linhaCreatePreviewCerca = cerca;
      }
      if (!this.isLinhaCreatePreview) return;
      await this.applyLinhaCreatePreviewLayers({ fit: true, preferCerca: true });
    },
    clearLinhaCreatePreview() {
      this.linhaCreatePreviewRotas = [];
      this.linhaCreatePreviewCerca = null;
      this.linhaCreatePreviewCercasLinha = [];
      if (!this.isLinhaCreatePreview) return;
      this.mapRotas = [];
      this.mapCercasGeojson = (this.mapCercasGeojson || []).filter((c) => !c._linhaCreatePreview);
      this.mapCercasCirculares = (this.mapCercasCirculares || []).filter((c) => !c._linhaCreatePreview);
      this.mapCercaMarkers = (this.mapCercaMarkers || []).filter((c) => !c._linhaCreatePreview);
      this.mensagemVazia = "";
    },
    async applyLinhaCreatePreviewLayers({ fit = false, preferCerca = false } = {}) {
      this.mapRotas = [];
      this.mapCercasGeojson = (this.mapCercasGeojson || []).filter((c) => !c._linhaCreatePreview);
      this.mapCercasCirculares = (this.mapCercasCirculares || []).filter((c) => !c._linhaCreatePreview);
      this.mapCercaMarkers = (this.mapCercaMarkers || []).filter((c) => !c._linhaCreatePreview);

      const rotas = this.linhaCreatePreviewRotas || [];
      rotas.forEach((rota, idx) => {
        const gj = this.rotaToGeojson(rota);
        if (!gj) return;
        const color = rotas.length > 1 ? ROUTE_COLORS[idx % ROUTE_COLORS.length] : PREVIEW_ROTA_COLOR;
        const nome = rota.nome_rota || rota.cod_rota || "";
        this.mapRotas.push({
          ...rota,
          geojson: gj,
          style: { color, weight: 6, opacity: 0.95 },
          options: {
            onEachFeature: (feat, layer) => {
              const label = nome || (feat.properties && feat.properties.name) || "";
              if (label) layer.bindPopup(String(label));
            },
          },
        });
      });

      const cerca = this.linhaCreatePreviewCerca;
      if (cerca) {
        const tagged = { ...cerca, _linhaCreatePreview: true };
        this.applyCercaToMapLayers(tagged, PREVIEW_CERCA_COLOR);
      }
      (this.linhaCreatePreviewCercasLinha || []).forEach((item, idx) => {
        if (!item || item.id_cerca == null) return;
        const color =
          (this.linhaCreatePreviewCercasLinha || []).length > 1
            ? ROUTE_COLORS[(idx + 2) % ROUTE_COLORS.length]
            : PREVIEW_CERCA_COLOR;
        this.applyCercaToMapLayers({ ...item, _linhaCreatePreview: true }, color);
      });

      const hasAssocCercas = (this.linhaCreatePreviewCercasLinha || []).length > 0;
      if (!this.hasMapGeometry()) {
        this.mensagemVazia =
          rotas.length || cerca || hasAssocCercas
            ? this.$t("mapPreview.unavailable")
            : "";
      } else {
        this.mensagemVazia = "";
      }

      if (!fit) return;
      const points = [];
      if (preferCerca && cerca) {
        if (Number(cerca.is_circular) === 1) {
          const lat = Number(cerca.lat_centro);
          const lng = Number(cerca.lng_centro);
          if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
        } else {
          const gj = this.parseGeojson(cerca.geojson);
          if (gj) this.collectGeojsonLatLngs(gj, points);
          const lat = Number(cerca.lat_centro);
          const lng = Number(cerca.lng_centro);
          if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
        }
      }
      if (!points.length) {
        (this.mapRotas || []).forEach((rota) => {
          if (rota && rota.geojson) this.collectGeojsonLatLngs(rota.geojson, points);
        });
        if (cerca) {
          const lat = Number(cerca.lat_centro);
          const lng = Number(cerca.lng_centro);
          if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
          const gj = this.parseGeojson(cerca.geojson);
          if (gj) this.collectGeojsonLatLngs(gj, points);
        }
        (this.linhaCreatePreviewCercasLinha || []).forEach((item) => {
          if (!item) return;
          const lat = Number(item.lat_centro);
          const lng = Number(item.lng_centro);
          if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
          const gj = this.parseGeojson(item.geojson);
          if (gj) this.collectGeojsonLatLngs(gj, points);
        });
      }
      if (points.length) {
        this.$nextTick(() => {
          this.invalidateMapSizeSoon({ skipFocus: true });
          this.$nextTick(() => this.fitMapToPoints(points));
        });
      }
    },
    resetState() {
      this.limparPreviewRoteirizacao();
      this.rawRotas = [];
      this.rawCercas = [];
      this.cercasLinhaLocal = [];
      this.rotasLinhaLocal = [];
      this.mapRotas = [];
      this.mapCercasGeojson = [];
      this.mapCercasCirculares = [];
      this.mapCercaMarkers = [];
      this.outrasLinhasMapRotas = [];
      this.outrasLinhasMapCercasGeojson = [];
      this.outrasLinhasMapCercasCirculares = [];
      this.outrasLinhasMapCercaMarkers = [];
      this.mensagemVazia = "";
      this.selectedRotaDetail = null;
      this.selectedCercaDetail = null;
      this.selectedMapRouteId = null;
      this.cercaDetailCache = {};
      this.linhaCreatePreviewRotas = [];
      this.linhaCreatePreviewCerca = null;
      this.itemCerca = {
        id_cerca: null,
        id_sentido: this.sentidoSelecionado,
        id_tipo_cerca: null,
        ordem_parada: 1,
        tempo_chegada_minutos: 0,
        show_sinotico: true,
      };
      this.itemRota = {
        id_rota: null,
        id_tipo_rota: null,
        id_sentido: this.sentidoSelecionado,
      };
      this.editingCerca = false;
      this.editingCercaId = null;
      this.editingCercaSentido = null;
      this.reorderFencesMode = false;
      this.editMode = "view";
      this.teardownEditRoute();
      this.$nextTick(() => {
        this.syncRotaSelect(null);
        this.syncCercaSelect(null);
      });
    },
    cercaSelectText(item) {
      if (!item) return "";
      const cod = item.cod_cerca != null ? String(item.cod_cerca).trim() : "";
      const nome = item.nome_cerca != null ? String(item.nome_cerca).trim() : "";
      if (cod && nome) return `${cod} - ${nome}`;
      return cod || nome || "";
    },
    async onCercaSelectInput(val) {
      this.itemCerca.id_cerca = val;
      if (val == null) {
        this.selectedCercaDetail = null;
        return;
      }
      await this.ensureCercaDetail(val);
      this.$nextTick(() => this.focusSelectedCerca());
    },
    syncCercaSelect(idOrItem) {
      const ref = this.$refs.cercaSelect;
      if (!ref) return;
      if (idOrItem == null) {
        ref.resetData();
        return;
      }
      const id = typeof idOrItem === "object" ? idOrItem.id_cerca : idOrItem;
      const cerca =
        typeof idOrItem === "object" ? idOrItem : this.resolveCercaById(id);
      if (cerca) {
        ref.cacheSelectedItems(
          [
            {
              id_cerca: cerca.id_cerca,
              nome_cerca: cerca.nome_cerca,
              cod_cerca: cerca.cod_cerca,
            },
          ],
          "id_cerca"
        );
      }
      ref.setData(id);
    },
    async ensureCercaDetail(idCerca, { force = false } = {}) {
      if (idCerca == null) return null;
      const id = Number(idCerca);
      const existing = this.resolveCercaById(id);
      const hasGeometry = !!(
        existing &&
        (existing.geojson ||
          existing.shape ||
          (Number(existing.is_circular) === 1 &&
            existing.lat_centro != null &&
            existing.lng_centro != null))
      );
      if (!force && existing && hasGeometry) {
        this.selectedCercaDetail = existing;
        this.$set(this.cercaDetailCache, id, existing);
        return existing;
      }
      try {
        const res = await apiController.request(apiController.endpoints.getCercaporid, {
          params: { id },
          silent: true,
        });
        const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const detail = rows[0] || null;
        if (detail) {
          this.selectedCercaDetail = detail;
          this.$set(this.cercaDetailCache, id, detail);
          const ref = this.$refs.cercaSelect;
          if (ref) {
            ref.cacheSelectedItems(
              [
                {
                  id_cerca: detail.id_cerca,
                  nome_cerca: detail.nome_cerca,
                  cod_cerca: detail.cod_cerca,
                },
              ],
              "id_cerca"
            );
          }
        }
        return detail || existing || null;
      } catch (e) {
        return existing || null;
      }
    },
    async onRotaSelectInput(val) {
      this.itemRota.id_rota = val;
      if (val == null) {
        this.selectedRotaDetail = null;
        return;
      }
      await this.ensureRotaDetail(val);
      this.$nextTick(() => this.focusRotaOnMap(val));
    },
    syncRotaSelect(idOrItem) {
      const ref = this.$refs.rotaSelect;
      if (!ref) return;
      if (idOrItem == null) {
        ref.resetData();
        return;
      }
      const id = typeof idOrItem === "object" ? idOrItem.id_rota : idOrItem;
      const rota =
        typeof idOrItem === "object" ? idOrItem : this.resolveRotaById(id);
      if (rota) {
        ref.cacheSelectedItems(
          [
            {
              id_rota: rota.id_rota,
              nome_rota: rota.nome_rota,
              cod_rota: rota.cod_rota,
            },
          ],
          "id_rota"
        );
      }
      ref.setData(id);
    },
    async ensureRotaDetail(idRota, { force = false } = {}) {
      if (idRota == null) return null;
      const id = Number(idRota);
      const existing = this.resolveRotaById(id);
      if (!force && existing && (existing.geojson || existing.shape)) {
        this.selectedRotaDetail = existing;
        return existing;
      }
      try {
        const res = await apiController.request(apiController.endpoints.getRotaporid, {
          params: { id },
          silent: true,
        });
        const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const detail = rows[0] || null;
        if (detail) {
          this.selectedRotaDetail = detail;
          const ref = this.$refs.rotaSelect;
          if (ref) {
            ref.cacheSelectedItems(
              [
                {
                  id_rota: detail.id_rota,
                  nome_rota: detail.nome_rota,
                  cod_rota: detail.cod_rota,
                },
              ],
              "id_rota"
            );
          }
        }
        return detail;
      } catch (e) {
        return existing || null;
      }
    },
    resolveRotaById(idRota) {
      if (idRota == null) return null;
      const id = Number(idRota);
      if (this.selectedRotaDetail && Number(this.selectedRotaDetail.id_rota) === id) {
        return this.selectedRotaDetail;
      }
      return (
        (this.rawRotas || []).find((r) => Number(r.id_rota) === id) ||
        (this.rotasLinhaLocal || []).find((r) => Number(r.id_rota) === id) ||
        (this.mapRotas || []).find((r) => Number(r.id_rota) === id) ||
        (this.entityPreviewItemsResolved || []).find((r) => r && Number(r.id_rota) === id) ||
        null
      );
    },
    rotaToGeojson(rota) {
      if (!rota) return null;
      const fromGj = this.parseGeojson(rota.geojson);
      if (fromGj) return fromGj;
      let shape = rota.shape;
      if (typeof shape === "string") {
        try {
          shape = JSON.parse(shape);
        } catch (e) {
          shape = null;
        }
      }
      if (!Array.isArray(shape) || !shape.length) return null;
      const coords = shape
        .map((p) => {
          const lat = Number(p.lt != null ? p.lt : p.lat);
          const lng = Number(p.lg != null ? p.lg : p.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return [lng, lat];
        })
        .filter(Boolean);
      if (coords.length < 2) return null;
      return { type: "LineString", coordinates: coords };
    },
    focusRotaOnMap(idRota) {
      this.selectRouteOnMap(idRota);
    },
    isRouteHighlightedOnMap(idRota) {
      if (idRota == null) return false;
      const highlightedId = this.cercaCreateRouteId != null && !this.cercaCreateSelectRoute
        ? this.cercaCreateRouteId
        : this.selectedMapRouteId;
      return Number(highlightedId) === Number(idRota);
    },
    onDrawerRotaClick(idRota) {
      if (this.cercaCreateSelectRoute) {
        this.selectRouteForFenceCreate(idRota);
        return;
      }
      this.selectRouteOnMap(idRota);
    },
    setCercaCreateRouteId(idRota) {
      if (idRota == null || idRota === "") {
        this.selectedMapRouteId = null;
        this.aplicarFiltroSentido();
        return;
      }
      const rota = (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(idRota));
      if (!rota || !this.rotaPassaFiltroSentido(rota, this.sentidoSelecionado)) return;
      this.rotasVisible = true;
      this.selectedMapRouteId = Number(idRota);
      this.aplicarFiltroSentido();
      const gj = this.rotaToGeojson(rota);
      if (gj) this.$nextTick(() => this.fitMapToGeojson(gj));
    },
    selectRouteForFenceCreate(idRota) {
      if (!this.cercaCreateSelectRoute || idRota == null) return;
      const rota = (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(idRota));
      if (!rota || !this.rotaPassaFiltroSentido(rota, this.sentidoSelecionado)) return;
      this.rotasVisible = true;
      this.selectedMapRouteId = Number(idRota);
      this.aplicarFiltroSentido();
      const gj = this.rotaToGeojson(rota);
      if (gj) this.$nextTick(() => this.fitMapToGeojson(gj));
      this.$emit("update:cercaCreateRouteId", Number(idRota));
      this.$emit("map-action", {
        context: this.mapContext,
        action: "route-selected-for-fence-create",
        item: rota,
      });
    },
    selectRouteOnMap(idRota) {
      if (this.cercaCreateSelectRoute) {
        this.selectRouteForFenceCreate(idRota);
        return;
      }
      if (idRota == null) return;
      this.rotasVisible = true;
      const rota = (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(idRota));
      if (!rota || !this.rotaPassaFiltroSentido(rota, this.sentidoSelecionado)) return;
      if (Number(this.selectedMapRouteId) === Number(idRota)) {
        this.selectedMapRouteId = null;
        if (this.editMode === "editRoute" && Number(this.activeEditRouteId) === Number(idRota)) {
          this.discardUnsavedRouteEdit();
          this.activeEditRouteId = null;
          this.resetEditWaypoints();
          this.appliedEditVias = [];
          this.clearEditRouteVertexMarkers();
        }
        this.aplicarFiltroSentido();
        return;
      }
      this.selectedMapRouteId = Number(idRota);
      if (this.editMode === "editRoute") {
        if (
          this.activeEditRouteId != null &&
          Number(this.activeEditRouteId) !== Number(idRota)
        ) {
          this.discardUnsavedRouteEdit();
        }
        this.activeEditRouteId = idRota;
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(idRota));
      }
      this.aplicarFiltroSentido();
      const gj = this.rotaToGeojson(rota);
      if (!gj) return;
      this.$nextTick(() => this.fitMapToGeojson(gj));
    },
    resolveSelectedRouteIdForRoteirizacao() {
      const rotasSentido = this.rotasNoSentido || [];
      const allRotas = this.rawRotas || [];
      const candidates = [
        this.selectedMapRouteId,
        this.activeEditRouteId,
      ]
        .map((id) => (id == null || id === "" ? null : Number(id)))
        .filter((id) => Number.isFinite(id));

      for (const id of candidates) {
        if (rotasSentido.some((r) => Number(r.id_rota) === id)) {
          return id;
        }
      }
      // Rota clicada no mapa ainda existe na linha (filtro de sentido pode ter divergido).
      for (const id of candidates) {
        if (allRotas.some((r) => Number(r.id_rota) === id)) {
          return id;
        }
      }
      // Uma única rota no sentido atual: não exige clique extra.
      if (rotasSentido.length === 1 && rotasSentido[0].id_rota != null) {
        return Number(rotasSentido[0].id_rota);
      }
      return null;
    },
    /**
     * Sentido efetivo para roteirizar: o da rota escolhida (quando específico),
     * senão o filtro atual do mapa.
     */
    resolveSentidoForRoteirizacao(idRota, sentidoFallback) {
      const rota = (this.rawRotas || []).find(
        (r) => Number(r.id_rota) === Number(idRota)
      );
      const sentidoRota = rota && rota.id_sentido != null ? Number(rota.id_sentido) : null;
      if (Number.isFinite(sentidoRota) && sentidoRota !== -1) {
        return sentidoRota;
      }
      const sentido = Number(sentidoFallback);
      return Number.isFinite(sentido) ? sentido : null;
    },
    fitMapToGeojson(gj) {
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      if (!map || !gj) return;
      try {
        map.invalidateSize({ animate: false });
        const points = [];
        this.collectGeojsonLatLngs(gj, points);
        if (!points.length) return;
        if (points.length === 1) {
          const z = this.normalizeMapZoom(15);
          this.currentCenter = points[0];
          this.zoom = z;
          map.setView(points[0], z, { animate: false });
          return;
        }
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 16 });
        const c = map.getCenter();
        this.currentCenter = [c.lat, c.lng];
        this.zoom = this.normalizeMapZoom(map.getZoom());
      } catch (e) {
        /* ignore */
      }
    },
    resolveCercaById(idCerca) {
      if (idCerca == null || idCerca === DRAFT_CERCA_ID) return null;
      const id = Number(idCerca);
      if (!Number.isFinite(id)) return null;
      if (this.cercaDetailCache && this.cercaDetailCache[id]) {
        return this.cercaDetailCache[id];
      }
      if (this.selectedCercaDetail && Number(this.selectedCercaDetail.id_cerca) === id) {
        return this.selectedCercaDetail;
      }
      const fromPreview = (this.entityPreviewItemsResolved || []).find(
        (c) => c && Number(c.id_cerca) === id
      );
      if (fromPreview) return fromPreview;
      return (
        (this.rawCercas || []).find((c) => Number(c.id_cerca) === id) ||
        (this.cercasLinhaLocal || []).find((c) => Number(c.id_cerca) === id) ||
        null
      );
    },
    focusCercaOnMap(idCerca) {
      if (idCerca == null) return;
      this.cercasVisible = true;
      const cerca = this.resolveCercaById(idCerca);
      if (!cerca) return;
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      if (!map) return;
      try {
        map.invalidateSize({ animate: false });
        const points = [];
        if (Number(cerca.is_circular) === 1) {
          const lat = Number(cerca.lat_centro);
          const lng = Number(cerca.lng_centro);
          const r = Number(cerca.raio_mts);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            if (Number.isFinite(r) && r > 0) {
              const dLat = r / 111320;
              const cosLat = Math.cos((lat * Math.PI) / 180);
              const dLng = cosLat !== 0 ? r / (111320 * cosLat) : dLat;
              points.push([lat - dLat, lng - dLng], [lat + dLat, lng + dLng]);
            } else {
              points.push([lat, lng]);
            }
          }
        } else {
          const gj = this.parseGeojson(cerca.geojson);
          if (gj) this.collectGeojsonLatLngs(gj, points);
          if (!points.length) {
            const lat = Number(cerca.lat_centro);
            const lng = Number(cerca.lng_centro);
            if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
          }
        }
        if (!points.length) return;
        if (points.length === 1) {
          const z = this.normalizeMapZoom(16);
          this.currentCenter = points[0];
          this.zoom = z;
          map.setView(points[0], z, { animate: false });
          return;
        }
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 17 });
        const c = map.getCenter();
        this.currentCenter = [c.lat, c.lng];
        this.zoom = this.normalizeMapZoom(map.getZoom());
      } catch (e) {
        /* ignore */
      }
    },
    onCercaDrawerItemClick(c) {
      if (!c || c.id_cerca == null) return;
      if (this.canEdit) {
        this.editMode = "editFences";
      }
      this.selectFenceForFormatEdit(c.id_cerca, c.id_sentido);
      this.focusCercaOnMap(c.id_cerca);
    },
    onFenceDrawerFormatoRadio(val) {
      const next = Number(val) === 1 ? "circular" : "polygon";
      this.onFenceEditBarFormatoChange(next);
    },
    onCercaReorderRowClick(item) {
      if (!item || item.id_cerca == null) return;
      this.selectFenceForFormatEdit(item.id_cerca, item.id_sentido);
      this.focusCercaGeometry(item);
    },
    /** Define qual cerca recebe a troca Circular/Polígono na barra de edição. */
    selectFenceForFormatEdit(idCerca, idSentido) {
      if (idCerca == null) return;
      const draft = idCerca === DRAFT_CERCA_ID || idCerca === "draft";
      this.editingCerca = true;
      this.editingCercaId = draft ? DRAFT_CERCA_ID : Number(idCerca);
      if (idSentido != null && idSentido !== "") {
        this.editingCercaSentido = idSentido;
      } else if (!draft) {
        const assoc = (this.rawCercas || []).find(
          (c) => Number(c.id_cerca) === Number(idCerca),
        );
        if (assoc && assoc.id_sentido != null) {
          this.editingCercaSentido = assoc.id_sentido;
        }
      }
    },
    onFenceFormatTargetSelect(idCerca) {
      this.selectFenceForFormatEdit(idCerca);
      if (idCerca != null && idCerca !== DRAFT_CERCA_ID) {
        this.focusCercaOnMap(idCerca);
      }
    },
    onCercaPinClick(pin) {
      if (!pin || this.editMode !== "editFences") return;
      this.selectFenceForFormatEdit(pin.id_cerca);
    },
    onCercaPinDragStart(pin) {
      if (!pin || this.editMode !== "editFences") return;
      this.selectFenceForFormatEdit(pin.id_cerca);
    },
    focusSelectedCerca() {
      this.focusCercaOnMap(this.itemCerca.id_cerca);
    },
    syncSentidoSelecionado() {
      const items = this.sentidos || [];
      if (!items.length) return;
      const atual = this.sentidoSelecionado;
      if (atual != null && items.some((s) => Number(s.id_sentido) === Number(atual))) return;
      const ida = items.find((s) => Number(s.id_sentido) === 1);
      const next = ida ? ida.id_sentido : items[0].id_sentido;
      this.$emit("update:sentidoSelecionado", next);
    },
    parseGeojson(gj) {
      if (!gj) return null;
      if (typeof gj === "string") {
        try {
          return JSON.parse(gj);
        } catch (e) {
          return null;
        }
      }
      return gj;
    },
    /**
     * Ida/Volta isolados também incluem itens de Ida e Volta (-1).
     * Filtro -1 / null: mostra todos.
     */
    rotaPassaFiltroSentido(rota, sentido) {
      if (sentido == null || Number(sentido) === -1) return true;
      const id = Number(rota.id_sentido);
      return id === Number(sentido) || id === -1;
    },
    cercaPassaFiltroSentido(cerca, sentido) {
      if (sentido == null || Number(sentido) === -1) return true;
      const id = Number(cerca.id_sentido);
      return id === Number(sentido) || id === -1;
    },
    /** Sobreposição de sentido: -1 (ida e volta) cobre ida e volta isolados. */
    sentidoSobrepoe(a, b) {
      if (a == null || b == null || Number.isNaN(Number(a)) || Number.isNaN(Number(b))) {
        return true;
      }
      const na = Number(a);
      const nb = Number(b);
      return na === nb || na === -1 || nb === -1;
    },
    async carregarDados(opts = {}) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || !this.token) return;
      const fitMap = opts.fitMap === true;
      const requestSeq = ++this.carregarDadosRequestSeq;
      this.loading = true;
      this.mensagemVazia = "";
      try {
        const [rotasRes, cercasRes] = await Promise.all([
          apiController.request(apiController.endpoints.getRotasporlinha, {
            data: [idLinha],
            silent: true,
          }),
          apiController.request(apiController.endpoints.getCercasporlinha, {
            data: [idLinha],
            silent: true,
          }),
        ]);
        if (this._isDestroyed || this._isBeingDestroyed || requestSeq !== this.carregarDadosRequestSeq) {
          return;
        }
        this.rawRotas = Array.isArray(rotasRes.data) ? rotasRes.data : [];
        this.rawCercas = Array.isArray(cercasRes.data) ? cercasRes.data : [];
        this.cercasLinhaLocal = this.rawCercas.map((c) => ({
          ...c,
          show_sinotico_extenso:
            Number(c.show_sinotico) === 1
              ? this.$t("selectOptions.yes")
              : this.$t("selectOptions.no"),
        }));
        this.rotasLinhaLocal = this.rawRotas.map((r) => ({ ...r }));
        this.syncSentidoSelecionado();
        this.aplicarFiltroSentido({ fitMap: false });
      } catch (e) {
        if (this._isDestroyed || this._isBeingDestroyed || requestSeq !== this.carregarDadosRequestSeq) {
          return;
        }
        this.rawRotas = [];
        this.rawCercas = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        if (
          !this._isDestroyed &&
          !this._isBeingDestroyed &&
          requestSeq === this.carregarDadosRequestSeq
        ) {
          this.loading = false;
          if (fitMap) this.$nextTick(() => this.scheduleFocusMap());
        }
      }
    },
    /**
     * Sobreposição somente leitura das demais linhas marcadas na tabela (além de `linha`,
     * que continua sendo a única totalmente editável no mapa).
     */
    async syncAdditionalLinhas() {
      const idsSet = new Set(
        (this.additionalLinhas || [])
          .map((l) => (l && l.id_linha != null ? Number(l.id_linha) : null))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
      const idPrimaria = this.linha && this.linha.id_linha != null ? Number(this.linha.id_linha) : null;
      if (idPrimaria != null) idsSet.delete(idPrimaria);
      const ids = [...idsSet];
      const requestSeq = ++this.additionalLinhasRequestSeq;
      if (!ids.length) {
        this.outrasLinhasMapRotas = [];
        this.outrasLinhasMapCercasGeojson = [];
        this.outrasLinhasMapCercasCirculares = [];
        this.outrasLinhasMapCercaMarkers = [];
        return;
      }
      // Cor por linha: índice 0 é a linha primária; overlays começam em 1.
      const colorByLinhaId = new Map(
        ids.map((id, idx) => [id, ROUTE_COLORS[(idx + 1) % ROUTE_COLORS.length]])
      );
      const colorForLinha = (idLinha) => {
        const id = Number(idLinha);
        if (Number.isFinite(id) && colorByLinhaId.has(id)) return colorByLinhaId.get(id);
        return ROUTE_COLORS[1 % ROUTE_COLORS.length];
      };
      try {
        const [rotasRes, cercasRes] = await Promise.all([
          apiController.request(apiController.endpoints.getRotasporlinha, { data: ids, silent: true }),
          apiController.request(apiController.endpoints.getCercasporlinha, { data: ids, silent: true }),
        ]);
        if (
          this._isDestroyed ||
          this._isBeingDestroyed ||
          requestSeq !== this.additionalLinhasRequestSeq
        ) {
          return;
        }
        const seenRotas = new Set();
        this.outrasLinhasMapRotas = (Array.isArray(rotasRes.data) ? rotasRes.data : [])
          .filter((r) => {
            const id = Number(r && r.id_rota);
            if (!Number.isFinite(id) || seenRotas.has(id)) return false;
            seenRotas.add(id);
            return true;
          })
          .map((rota) => {
            const gj = this.rotaToGeojson(rota);
            if (!gj) return null;
            const color = colorForLinha(rota.id_linha);
            const nome = rota.nome_rota || rota.cod_rota || "";
            return {
              ...rota,
              geojson: gj,
              style: { color, weight: 4, opacity: 0.55, dashArray: "6 4" },
              options: {
                onEachFeature: (feat, layer) => {
                  const label = nome || (feat.properties && feat.properties.name) || "";
                  if (label) layer.bindPopup(String(label));
                  layer.on("click", (ev) => {
                    if (ev) {
                      L.DomEvent.stopPropagation(ev);
                      if (ev.originalEvent) L.DomEvent.preventDefault(ev);
                    }
                    this.selectLinhaFromMapClick(rota.id_linha);
                  });
                },
              },
            };
          })
          .filter(Boolean);

        const seenCercas = new Set();
        const geojsonList = [];
        const circularList = [];
        const markerList = [];
        (Array.isArray(cercasRes.data) ? cercasRes.data : []).forEach((cerca) => {
          const id = Number(cerca && cerca.id_cerca);
          if (!Number.isFinite(id) || seenCercas.has(id) || cerca.cod_tipo_cerca === "POI") return;
          seenCercas.add(id);
          const color = colorForLinha(cerca.id_linha);
          const nome = cerca.nome_cerca || cerca.cod_cerca || "";
          if (Number(cerca.is_circular) === 1) {
            const r = Number(cerca.raio_mts);
            if (cerca.lat_centro == null || cerca.lng_centro == null || Number.isNaN(r) || r <= 0) return;
            circularList.push({ ...cerca, _strokeColor: color, _fillColor: color });
            markerList.push({
              id_cerca: id,
              lat: Number(cerca.lat_centro),
              lng: Number(cerca.lng_centro),
              nome,
              color,
            });
            return;
          }
          const gj = this.parseGeojson(cerca.geojson);
          if (!gj) return;
          geojsonList.push({
            ...cerca,
            geojson: gj,
            style: { color, fillColor: color, fillOpacity: 0.14, weight: 2, dashArray: "6 4" },
            options: {
              onEachFeature: (feat, layer) => {
                const label = nome || (feat.properties && feat.properties.name) || "";
                if (label) layer.bindPopup(`<strong>${String(label)}</strong>`);
              },
            },
          });
          const pos = this.cercaPinPosition(cerca, gj);
          if (pos) {
            markerList.push({ id_cerca: id, lat: pos[0], lng: pos[1], nome, color });
          }
        });
        this.outrasLinhasMapCercasGeojson = geojsonList;
        this.outrasLinhasMapCercasCirculares = circularList;
        this.outrasLinhasMapCercaMarkers = markerList;
      } catch (e) {
        if (
          this._isDestroyed ||
          this._isBeingDestroyed ||
          requestSeq !== this.additionalLinhasRequestSeq
        ) {
          return;
        }
        this.outrasLinhasMapRotas = [];
        this.outrasLinhasMapCercasGeojson = [];
        this.outrasLinhasMapCercasCirculares = [];
        this.outrasLinhasMapCercaMarkers = [];
      } finally {
        if (
          !this._isDestroyed &&
          !this._isBeingDestroyed &&
          requestSeq === this.additionalLinhasRequestSeq
        ) {
          this.$nextTick(() => this.scheduleFocusMap());
        }
      }
    },
    /**
     * Clique no traçado (somente leitura) de uma linha sobreposta define qual linha
     * passa a ser a ativa/editável, sincronizando tabela e painel de detalhe.
     */
    selectLinhaFromMapClick(idLinha) {
      if (!this.isLinhasContext || idLinha == null) return;
      const linha = (this.additionalLinhas || []).find(
        (l) => l && Number(l.id_linha) === Number(idLinha)
      );
      if (!linha) return;
      this.$emit("map-action", {
        context: this.mapContext,
        action: "select-linha-map",
        item: linha,
      });
    },
    aplicarFiltroSentido(opts = {}) {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      const sentido = this.sentidoSelecionado;
      const lineColor = PRIMARY_LINE_COLOR;
      this.mapRotas = [];
      this.rawRotas.forEach((rota) => {
        if (!this.rotaPassaFiltroSentido(rota, sentido)) return;
        const gj = this.parseGeojson(rota.geojson);
        if (!gj) return;
        const editing =
          (this.activeEditRouteId != null && Number(this.activeEditRouteId) === Number(rota.id_rota)) ||
          (this.selectedMapRouteId != null && Number(this.selectedMapRouteId) === Number(rota.id_rota));
        const color = editing ? EDIT_ROUTE_COLOR : lineColor;
        const weight = editing ? 7 : 4;
        const idRota = rota.id_rota;
        this.mapRotas.push({
          ...rota,
          _rev: rota._rev || 0,
          geojson: gj,
          style: { color, weight, opacity: 0.85 },
          options: {
            onEachFeature: (feat, layer) => {
              const name =
                (feat.properties && feat.properties.name) || rota.nome_rota || rota.cod_rota;
              if (name) layer.bindPopup(String(name));
              layer.on("click", (ev) => {
                if (ev) {
                  L.DomEvent.stopPropagation(ev);
                  if (ev.originalEvent) L.DomEvent.preventDefault(ev);
                }
                this.lastPolylineClickMs = Date.now();
                if (this.cercaCreateSelectRoute) {
                  this.selectRouteForFenceCreate(idRota);
                  return;
                }
                if (this.editMode === "editRoute") {
                  if (this.routeClickDebounce) clearTimeout(this.routeClickDebounce);
                  this.routeClickDebounce = setTimeout(() => {
                    this.selectRouteForEdit(idRota);
                  }, 250);
                  return;
                }
                this.selectRouteOnMap(idRota);
              });
            },
          },
        });
      });

      this.mapCercasGeojson = [];
      this.mapCercasCirculares = [];
      this.mapCercaMarkers = [];
      this.rawCercas.forEach((cerca) => {
        if (cerca.cod_tipo_cerca === "POI") return;
        if (!this.cercaPassaFiltroSentido(cerca, sentido)) return;
        const color = lineColor;
        const nome = cerca.nome_cerca || cerca.cod_cerca || "";
        if (Number(cerca.is_circular) === 1) {
          const r = Number(cerca.raio_mts);
          if (cerca.lat_centro == null || cerca.lng_centro == null || Number.isNaN(r) || r <= 0) return;
          this.mapCercasCirculares.push({ ...cerca, _strokeColor: color, _fillColor: color });
          this.pushCercaMarker(cerca, color, nome);
          return;
        }
        const gj = this.parseGeojson(cerca.geojson);
        if (!gj) return;
        this.mapCercasGeojson.push({
          ...cerca,
          geojson: gj,
          style: { color, fillColor: color, fillOpacity: 0.22, weight: 2 },
          options: {
            onEachFeature: (feat, layer) => {
              const label = nome || (feat.properties && feat.properties.name) || "";
              if (label) layer.bindPopup(`<strong>${String(label)}</strong>`);
            },
          },
        });
        this.pushCercaMarker(cerca, color, nome, gj);
      });

      const temGeometria =
        this.mapRotas.length > 0 ||
        this.mapCercasGeojson.length > 0 ||
        this.mapCercasCirculares.length > 0;
      if (temGeometria) {
        this.mensagemVazia = "";
      } else if (!this.editRouteRedrawing) {
        this.mensagemVazia = this.$t("crudLinhas.map.noGeometry");
      } else {
        this.mensagemVazia = "";
      }
      if (opts.fitMap === true) this.$nextTick(() => this.scheduleFocusMap());
      if (this.editMode === "editFences") {
        this.$nextTick(() => this.rebuildFenceEditHandles());
      }
    },
    pushCercaMarker(cerca, color, nome, geojson) {
      const pos = this.cercaPinPosition(cerca, geojson);
      if (!pos) return;
      this.mapCercaMarkers.push({
        id_cerca: cerca.id_cerca,
        id_tipo_cerca: cerca.id_tipo_cerca,
        cod_tipo_cerca: cerca.cod_tipo_cerca,
        lat: pos[0],
        lng: pos[1],
        nome,
        color,
        _createPreview: !!cerca._createPreview,
      });
    },
    cercaPinPosition(cerca, geojson) {
      const la = parseFloat(cerca.lat_centro);
      const ln = parseFloat(cerca.lng_centro);
      if (!Number.isNaN(la) && !Number.isNaN(ln)) return [la, ln];
      if (geojson) return this.extractFirstLatLng(geojson);
      return null;
    },
    isCercaPinDraggable(pin) {
      if (this.editMode !== "editFences") return false;
      if (this.fenceDragRecalculating || this.roteirizandoPorCercas || this.savingCercas) {
        return false;
      }
      if (!pin || pin.id_cerca == null) return false;
      const draft = pin.id_cerca === DRAFT_CERCA_ID || pin._createPreview;
      if (!this.canEdit && !(this.canInsert && draft)) return false;
      if (this.isEntityPreview) {
        // Cerca avulsa: id_tipo_cerca vem de linhas_cercas e não se aplica aqui.
        // Só o preview de Cercas é editável (a cerca-centro da unidade não).
        return this.mapContext === "cercas";
      }
      const cerca = this.resolveCercaById(pin.id_cerca) || pin;
      return this.isLinhaFenceEligibleForDrag(cerca, this.sentidoSelecionado);
    },
    revertCercaPin(pin, ev, oldLat, oldLng) {
      if (ev && ev.target && typeof ev.target.setLatLng === "function") {
        ev.target.setLatLng([oldLat, oldLng]);
      }
      if (pin) {
        this.$set(pin, "lat", oldLat);
        this.$set(pin, "lng", oldLng);
      }
    },
    applyFenceTranslation(cerca, newLat, newLng) {
      const oldLat = Number(cerca.lat_centro);
      const oldLng = Number(cerca.lng_centro);
      const dLat = newLat - (Number.isFinite(oldLat) ? oldLat : newLat);
      const dLng = newLng - (Number.isFinite(oldLng) ? oldLng : newLng);
      const next = { ...cerca, lat_centro: newLat, lng_centro: newLng };
      if (Number(cerca.is_circular) === 1) {
        // Igual à Roteirização (buildFenceFromStop): Point no centro + raio.
        next.geojson = {
          type: "Point",
          coordinates: [newLng, newLat],
        };
        if (next.raio_mts == null || next.raio_mts === "") {
          next.raio_mts = Number(cerca.raio_mts) > 0 ? Number(cerca.raio_mts) : 75;
        }
        next.shape = null;
      } else {
        next.geojson = translateGeojson(cerca.geojson, dLat, dLng);
        next.shape = translateShape(cerca.shape, dLat, dLng);
      }
      return next;
    },
    buildUpdateCercaPayload(cerca) {
      return {
        id_cerca: cerca.id_cerca,
        cod_cerca: cerca.cod_cerca,
        nome_cerca: cerca.nome_cerca,
        shape: stringifyMaybe(cerca.shape),
        alarme_entrada: cerca.alarme_entrada != null ? cerca.alarme_entrada : 0,
        alarme_saida: cerca.alarme_saida != null ? cerca.alarme_saida : 0,
        endereco: cerca.endereco,
        observacoes: cerca.observacoes != null ? cerca.observacoes : null,
        lat_centro: cerca.lat_centro,
        lng_centro: cerca.lng_centro,
        ativa: cerca.ativa != null ? cerca.ativa : 1,
        alarme_velocidade: cerca.alarme_velocidade != null ? cerca.alarme_velocidade : 0,
        velocidade_maxima: cerca.velocidade_maxima,
        geojson: stringifyMaybe(cerca.geojson),
        is_circular: cerca.is_circular,
        raio_mts: cerca.raio_mts,
        id_empresa: cerca.id_empresa != null && cerca.id_empresa !== "" ? cerca.id_empresa : null,
        id_cliente: cerca.id_cliente != null && cerca.id_cliente !== "" ? cerca.id_cliente : null,
      };
    },
    /** Payload enxuto para arrasto: só geometria (evita validação cliente/empresa e campos incompletos). */
    buildUpdateCercaGeometryPayload(cerca) {
      return {
        id_cerca: cerca.id_cerca,
        geometry_only: true,
        shape: stringifyMaybe(cerca.shape),
        geojson: stringifyMaybe(cerca.geojson),
        lat_centro: cerca.lat_centro,
        lng_centro: cerca.lng_centro,
        is_circular: cerca.is_circular != null ? cerca.is_circular : 0,
        raio_mts: cerca.raio_mts,
      };
    },
    async onCercaPinDragend(pin, ev) {
      if (!this.isCercaPinDraggable(pin)) return;
      const newLatLng = ev && ev.target && typeof ev.target.getLatLng === "function"
        ? ev.target.getLatLng()
        : null;
      if (!newLatLng) return;
      const newLat = Number(newLatLng.lat);
      const newLng = Number(newLatLng.lng);
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) return;
      const oldLat = Number(pin.lat);
      const oldLng = Number(pin.lng);
      if (!Number.isFinite(oldLat) || !Number.isFinite(oldLng)) return;
      if (Math.abs(newLat - oldLat) < 1e-9 && Math.abs(newLng - oldLng) < 1e-9) return;

      if (this.isEntityPreview) {
        await this.applyPreviewFenceDrag(pin, ev, newLat, newLng, oldLat, oldLng);
        return;
      }

      const source = this.resolveCercaById(pin.id_cerca);
      if (!source) return;

      const moved = this.applyFenceTranslation(source, newLat, newLng);
      this.$set(pin, "lat", newLat);
      this.$set(pin, "lng", newLng);
      this.patchLocalCercaGeometry(moved);

      await this.applyLinhaFenceMoveAndRecalcRoute(moved, {
        oldLat,
        oldLng,
        onRevert: () => this.revertCercaPin(pin, ev, oldLat, oldLng),
      });
    },
    linhaFencesEligibleForDrag() {
      const sentido = this.sentidoSelecionado;
      return (this.rawCercas || [])
        .filter((cerca) => this.isLinhaFenceEligibleForDrag(cerca, sentido))
        .map((cerca) => this.resolveCercaById(cerca.id_cerca) || cerca);
    },
    isLinhaFenceEligibleForDrag(cerca, sentido) {
      if (!cerca || cerca.id_cerca == null) return false;
      if (sentido != null && sentido !== "" && cerca.id_sentido != null) {
        const s = Number(cerca.id_sentido);
        const sel = Number(sentido);
        if (s !== sel && s !== -1) return false;
      }
      const tipo = Number(cerca.id_tipo_cerca);
      const cod = cerca.cod_tipo_cerca;
      if (cod === "POI") return false;
      if (tipo === FENCE_TYPE.POI || tipo === FENCE_TYPE.GARAGE || tipo === FENCE_TYPE.END) return false;
      if (tipo === FENCE_TYPE.START && this.hasDisplacementOnSentido) return false;
      if (isPinnedTerminusFence(cerca, this.terminusPinOptions)) return false;
      return true;
    },
    /**
     * Recálculo de traçado após arrasto de cerca: mantém a ordem das paradas e
     * redesenha a rota via /plan (sem /solve, que reordena paradas).
     */
    async resolveRotaRecalculadaPorArrasto({ sentido, mainRota, cercasAtualizadas }) {
      const beforeCtx = resolveDirectionContext(this.rawCercas, mainRota, sentido);
      const cercasAntes = fenceSnapshotForHistory(beforeCtx.cercasBase);
      const { sentido: sentidoCtx, cercasBase, preservedExtraCercas } = resolveDirectionContext(
        cercasAtualizadas,
        mainRota,
        sentido
      );
      const ordered = enrichCercasWithCoords(cercasBase);
      if (!ordered.length) {
        throw new Error(this.$t("crudLinhas.map.routeByFencesNoRoute"));
      }
      const planResult = await planOrderedFencesRoute(ordered, {
        serviceTime: 0,
        sentido: sentidoCtx,
        hasDisplacement: this.hasDisplacementOnSentido,
      });
      if (!planResult || planResult.error || !planResult.geojson) {
        const rateLimited = /429|RATE_LIMIT|rate.?limit/i.test(
          String((planResult && planResult.message) || "")
        );
        throw new Error(
          rateLimited
            ? this.$t("crudLinhas.map.vroomRateLimit")
            : (planResult && planResult.message) || this.$t("crudLinhas.map.stopDragRecalcError")
        );
      }
      return {
        sentidoCtx,
        ordered,
        orderChanged: false,
        typesChanged: false,
        tiposAlterados: [],
        pinStart: !!planResult.pinStart,
        cercasAntes,
        planResult,
        preservedExtraCercas,
      };
    },
    async applyLinhaFenceMoveAndRecalcRoute(moved, { onRevert, oldLat, oldLng } = {}) {
      const idLinha = this.linha && this.linha.id_linha;
      const sentido = this.sentidoSelecionado;
      if (!idLinha) {
        if (typeof onRevert === "function") onRevert();
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.routeByFencesNoLine") },
        });
        return;
      }
      if (sentido == null || sentido === "") {
        if (typeof onRevert === "function") onRevert();
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.selectDirectionFirst") },
        });
        return;
      }
      if (isPinnedTerminusFence(moved, this.terminusPinOptions)) {
        if (typeof onRevert === "function") onRevert();
        this.dialogTerminusOrder = true;
        return;
      }

      this.fenceDragRecalculating = true;
      try {
        const mainRota = selectMainRouteForSentido(this.rawRotas, Number(sentido));
        if (!mainRota) {
          throw new Error(this.$t("crudLinhas.map.routeByFencesNoRoute"));
        }
        const cercasAtualizadas = (this.rawCercas || []).map((c) => (
          Number(c.id_cerca) === Number(moved.id_cerca) ? { ...c, ...moved } : c
        ));

        let resolved = await this.resolveRotaRecalculadaPorArrasto({
          sentido: Number(sentido),
          mainRota,
          cercasAtualizadas,
        });

        await apiController.request(apiController.endpoints.updateCerca, {
          data: this.buildUpdateCercaGeometryPayload(moved),
          silent: true,
        });

        await this.persistRotaRecalculada({ idLinha, mainRota, ...resolved });

        await this.persistHistoricoCercaDrag({
          orderChanged: resolved.orderChanged,
          typesChanged: resolved.typesChanged,
          tiposAlterados: resolved.tiposAlterados,
          pinStart: resolved.pinStart,
          cercasAntes: resolved.cercasAntes,
          orderedCercas: resolved.ordered,
          planResult: resolved.planResult,
          mainRota,
          sentido: Number(sentido),
          moved,
          oldLat,
          oldLng,
        });
        await this.carregarDados();
        this.$emit("cercas-changed");
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: resolved.orderChanged || resolved.typesChanged
            ? this.$t("crudLinhas.map.stopDragSuccessOrderChanged")
            : this.$t("crudLinhas.map.stopDragSuccess"),
        });
      } catch (e) {
        if (typeof onRevert === "function") onRevert();
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.fenceDragRecalculating = false;
      }
    },
    /**
     * Primeira etapa do recálculo de rota ao alterar cercas de uma linha: monta o
     * contexto de direção a partir de `cercasAtualizadas` e tenta reordenar via /solve
     * do vroom (falha tolerada quando não há paradas suficientes). Não persiste nada —
     * apenas resolve a nova ordem/geometria. Compartilhado entre arrasto de cerca
     * (`onCercaPinDragend`) e criação/associação de cerca na linha.
     * @throws {Error} quando o vroom falha de forma não tolerável.
     */
    async resolveRotaRecalculada({ sentido, mainRota, cercasAtualizadas }) {
      const beforeCtx = resolveDirectionContext(this.rawCercas, mainRota, sentido);
      const cercasAntes = fenceSnapshotForHistory(beforeCtx.cercasBase);
      const { sentido: sentidoCtx, cercasBase, preservedExtraCercas } = resolveDirectionContext(
        cercasAtualizadas,
        mainRota,
        sentido
      );
      const enriched = enrichCercasWithCoords(cercasBase);
      const hasDisplacement = this.hasDisplacementOnSentido;

      let ordered = enriched;
      let orderChanged = false;
      let typesChanged = false;
      let tiposAlterados = [];
      let planResult = null;
      let pinStart = hasDisplacement;

      const solved = await solveReorderLineWithPcd({
        orderedCercas: enriched,
        serviceSec: 0,
        sentido: sentidoCtx,
        hasDisplacement,
      });
      if (solved && !solved.error && solved.geojson) {
        ordered = solved.orderedCercas || enriched;
        orderChanged = fenceOrderKey(enriched) !== fenceOrderKey(ordered);
        tiposAlterados = solved.tiposAlterados || collectFenceTypeChanges(enriched, ordered);
        typesChanged = tiposAlterados.length > 0;
        planResult = solved;
        pinStart = !!solved.pinStart;
      } else if (solved && solved.error) {
        const skippable = /sem ponto final|sem paradas intermediárias/i.test(
          String(solved.message || "")
        );
        if (!skippable) {
          const rateLimited = /429|RATE_LIMIT|rate.?limit/i.test(String(solved.message || ""));
          throw new Error(
            rateLimited
              ? this.$t("crudLinhas.map.vroomRateLimit")
              : this.$t("crudLinhas.map.stopDragRecalcError")
          );
        }
      }

      return {
        sentidoCtx,
        ordered,
        orderChanged,
        typesChanged,
        tiposAlterados,
        pinStart,
        cercasAntes,
        planResult,
        preservedExtraCercas,
      };
    },
    /**
     * Segunda etapa: persiste a nova sequência de cercas (quando ordem/tipos mudaram) e
     * a geometria recalculada da rota principal, a partir do resultado de
     * `resolveRotaRecalculada`.
     * @throws {Error} quando a persistência falha.
     */
    async persistRotaRecalculada({
      idLinha,
      mainRota,
      sentidoCtx,
      ordered,
      orderChanged,
      typesChanged,
      planResult,
      preservedExtraCercas,
      force = false,
    }) {
      if (!planResult) return;
      if (force || orderChanged || typesChanged) {
        const persisted = await persistLineFenceSequence({
          idLinha,
          sentido: sentidoCtx,
          mainRota,
          otherRotas: (this.rawRotas || []).filter(
            (r) => r && mainRota && String(r.id_rota) !== String(mainRota.id_rota)
          ),
          orderedCercas: ordered,
          preservedExtraCercas,
          planGeojson: planResult.geojson,
          planDistance: planResult.distance,
          planShape: planResult.shape,
          etasByCerca: planResult.etasByCerca,
          durationSec: planResult.durationSec,
          replaceRoute: false,
        });
        if (!persisted || !persisted.ok) {
          throw new Error(
            (persisted && persisted.message) || this.$t("crudLinhas.map.routeByFencesPersistError")
          );
        }
      }
      const updated = await updateMainRouteGeometry(mainRota, planResult);
      if (!updated || !updated.ok) {
        throw new Error(
          (updated && updated.message) || this.$t("crudLinhas.map.routeByFencesUpdateError")
        );
      }
    },
    /**
     * Cria uma cerca nova (`/insertcerca`) e a associa à linha ativa: tenta definir sua
     * ordem via /solve do vroom (podendo reordenar as demais paradas) e, quando não há
     * paradas suficientes para o solve, cai para /plan mantendo a cerca ao final da
     * sequência atual. Usado pelo formulário de criação de cerca do drawer da linha.
     * @returns {Promise<{ ok: boolean, message?: string, idCerca?: number }>}
     */
    async criarCercaEAssociarNaLinha(payload, { idRota } = {}) {
      const idLinha = this.linha && this.linha.id_linha;
      const sentido = this.sentidoSelecionado;
      if (!idLinha) {
        return { ok: false, message: this.$t("crudLinhas.map.routeByFencesNoLine") };
      }
      if (sentido == null || sentido === "") {
        return { ok: false, message: this.$t("crudLinhas.map.selectDirectionFirst") };
      }
      const mainRota = idRota != null && idRota !== ""
        ? (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(idRota))
        : selectMainRouteForSentido(this.rawRotas, Number(sentido));
      if (!mainRota) {
        return { ok: false, message: this.$t("crudLinhas.map.routeByFencesNoRoute") };
      }

      try {
        let insertRes;
        try {
          insertRes = await apiController.request(apiController.endpoints.insertCerca, {
            data: payload,
            silent: true,
          });
        } catch (insertErr) {
          const apiBody = insertErr && insertErr.response && insertErr.response.data;
          const apiMsg =
            apiBody && (apiBody.mensagem || apiBody.message || apiBody.msg);
          throw new Error(
            apiMsg || (insertErr && insertErr.message) || this.$t("geofence.updateTable.error.insert")
          );
        }
        const body = (insertRes && insertRes.data) || {};
        if (body.erro || body.error) {
          throw new Error(
            body.mensagem || body.message || this.$t("geofence.updateTable.error.insert")
          );
        }
        const idCerca = body.id_cerca != null ? body.id_cerca : body.insertId;
        if (idCerca == null) {
          throw new Error(this.$t("geofence.updateTable.error.insert"));
        }

        const novaAssociacao = {
          ...payload,
          id_cerca: idCerca,
          id_sentido: Number(sentido),
          id_tipo_cerca: FENCE_TYPE.STOP,
          tempo_chegada_minutos: 0,
          show_sinotico: 1,
          ordem_parada: null,
        };
        const cercasAtualizadas = [...(this.rawCercas || []), novaAssociacao];

        let resolved = await this.resolveRotaRecalculada({
          sentido: Number(sentido),
          mainRota,
          cercasAtualizadas,
        });

        if (!resolved.planResult) {
          // /solve não aplicável (ex.: sem paradas intermediárias suficientes ainda) —
          // mantém a cerca ao final da sequência atual e redesenha via /plan.
          const { cercasBase, preservedExtraCercas } = resolveDirectionContext(
            cercasAtualizadas,
            mainRota,
            Number(sentido)
          );
          const ordered = enrichCercasWithCoords(cercasBase);
          const planned = await planOrderedFencesRoute(ordered, {
            serviceTime: 0,
            sentido: Number(sentido),
            hasDisplacement: this.hasDisplacementOnSentido,
          });
          if (!planned || planned.error || !planned.geojson) {
            throw new Error(
              (planned && planned.message) || this.$t("crudLinhas.map.stopDragRecalcError")
            );
          }
          resolved = { ...resolved, ordered, planResult: planned, preservedExtraCercas };
        }

        await this.persistRotaRecalculada({ idLinha, mainRota, ...resolved, force: true });

        await this.carregarDados();
        this.$emit("cercas-changed");
        this.$emit("rotas-changed");
        return { ok: true, idCerca };
      } catch (e) {
        const rateLimited = /429|RATE_LIMIT|rate.?limit/i.test(String((e && e.message) || ""));
        return {
          ok: false,
          message: rateLimited ? this.$t("crudLinhas.map.vroomRateLimit") : (e && e.message) || null,
        };
      }
    },
    /**
     * Persiste geometria de cerca: rascunho volta ao formulário; salva via PUT geometry_only.
     */
    async persistCercaGeometry(next, { onRevert, successMessage } = {}) {
      if (!next) return;
      const draft = isDraftCerca(next) || next.id_cerca === DRAFT_CERCA_ID;
      this.fenceDragRecalculating = true;
      try {
        if (draft) {
          const geometry = {
            shape: next.shape,
            geojson: next.geojson,
            lat_centro: next.lat_centro,
            lng_centro: next.lng_centro,
            is_circular: next.is_circular,
            raio_mts: next.raio_mts,
          };
          const updated = {
            ...(this.createDraftCerca || {}),
            ...next,
            id_cerca: null,
            _createPreview: true,
          };
          this.createDraftCerca = updated;
          // Não destruir markers no mesmo frame do dragend (Leaflet).
          this.$nextTick(() => {
            this.applyCreateDraftLayers(this.createDraftCerca);
            this.previewLayerRev += 1;
          });
          this.$emit("map-action", {
            context: this.mapContext,
            action: "fence-geometry-changed",
            item: this.createDraftCerca,
            geometry,
          });
          return;
        }
        const payload = this.buildUpdateCercaGeometryPayload(next);
        const res = await apiController.request(apiController.endpoints.updateCerca, {
          data: payload,
          silent: true,
        });
        if (res && res.data && res.data.erro) {
          throw new Error(res.data.mensagem || "updateCerca");
        }
        const persisted = {
          ...next,
          geojson: payload.geojson,
          shape: payload.shape,
        };
        this.selectedCercaDetail = { ...persisted };
        this.$set(this.cercaDetailCache, Number(persisted.id_cerca), { ...persisted });
        this.rebuildPreviewFenceLayers(persisted);
        this.$emit("preview-changed", { type: "fence", item: { ...persisted } });
        EventBus.$emit("show-success", {
          message: successMessage || this.$t("crudLinhas.map.fenceUpdated"),
        });
      } catch (e) {
        if (typeof onRevert === "function") onRevert();
        EventBus.$emit("show-error", { error: e });
        throw e;
      } finally {
        this.fenceDragRecalculating = false;
      }
    },
    /**
     * Arrasto de cerca no preview de Cercas: translada a geometria e persiste
     * (cerca salva) ou devolve ao formulário (rascunho).
     */
    async applyPreviewFenceDrag(pin, ev, newLat, newLng, oldLat, oldLng) {
      const isDraft = pin.id_cerca === DRAFT_CERCA_ID || pin._createPreview;
      let source = null;
      if (isDraft) {
        source = this.createDraftCerca;
      } else {
        source = this.resolveCercaById(pin.id_cerca);
        try {
          const detail = await this.ensureCercaDetail(pin.id_cerca, { force: true });
          if (detail) source = detail;
        } catch (e) {
          /* usa o que já está em cache */
        }
      }
      if (!source) {
        this.revertCercaPin(pin, ev, oldLat, oldLng);
        return;
      }
      const moved = this.applyFenceTranslation(source, newLat, newLng);
      if (isDraft) {
        moved.id_cerca = null;
        moved._createPreview = true;
      }
      this.$set(pin, "lat", newLat);
      this.$set(pin, "lng", newLng);
      this.patchLocalCercaGeometry(moved);
      try {
        await this.persistCercaGeometry(moved, {
          onRevert: () => this.revertCercaPin(pin, ev, oldLat, oldLng),
          successMessage: this.$t("crudLinhas.map.fenceMoved"),
        });
      } catch (e) {
        /* persistCercaGeometry já reverteu e notificou */
      }
    },
    patchLocalCercaGeometry(cerca, { rebuildHandles = true } = {}) {
      if (!cerca) return;
      const id = cerca.id_cerca != null ? Number(cerca.id_cerca) : null;
      if (id != null && Number.isFinite(id)) {
        const idx = (this.rawCercas || []).findIndex((c) => Number(c.id_cerca) === id);
        if (idx >= 0) {
          this.$set(this.rawCercas, idx, { ...this.rawCercas[idx], ...cerca });
        }
        this.$set(this.cercaDetailCache, id, {
          ...(this.cercaDetailCache[id] || {}),
          ...cerca,
        });
        const circIdx = (this.mapCercasCirculares || []).findIndex((c) => Number(c.id_cerca) === id);
        const isCirc = Number(cerca.is_circular) === 1 && Number(cerca.raio_mts) > 0;
        if (isCirc) {
          const base =
            circIdx >= 0
              ? this.mapCercasCirculares[circIdx]
              : { ...cerca, _strokeColor: "#FF6F00", _fillColor: "#FF6F00" };
          const nextCirc = { ...base, ...cerca };
          if (circIdx >= 0) this.$set(this.mapCercasCirculares, circIdx, nextCirc);
          else this.mapCercasCirculares.push(nextCirc);
        } else if (circIdx >= 0) {
          this.mapCercasCirculares.splice(circIdx, 1);
        }
        const gjIdx = (this.mapCercasGeojson || []).findIndex((c) => Number(c.id_cerca) === id);
        const gj = this.parseGeojson(cerca.geojson);
        const isPoly =
          Number(cerca.is_circular) !== 1 && gj && gj.type !== "Point";
        if (isPoly) {
          const color =
            (gjIdx >= 0 && this.mapCercasGeojson[gjIdx]._strokeColor) ||
            (gjIdx >= 0 && this.mapCercasGeojson[gjIdx].style && this.mapCercasGeojson[gjIdx].style.color) ||
            "#FF6F00";
          const nextPoly = {
            ...(gjIdx >= 0 ? this.mapCercasGeojson[gjIdx] : cerca),
            ...cerca,
            geojson: gj,
            style: { color, fillColor: color, fillOpacity: 0.22, weight: 2 },
          };
          if (gjIdx >= 0) this.$set(this.mapCercasGeojson, gjIdx, nextPoly);
          else this.mapCercasGeojson.push(nextPoly);
        } else if (gjIdx >= 0) {
          this.mapCercasGeojson.splice(gjIdx, 1);
        }
        const pin = (this.mapCercaMarkers || []).find((p) => Number(p.id_cerca) === id);
        if (pin && cerca.lat_centro != null && cerca.lng_centro != null) {
          this.$set(pin, "lat", Number(cerca.lat_centro));
          this.$set(pin, "lng", Number(cerca.lng_centro));
        }
      } else if (isDraftCerca(cerca) || cerca._createPreview) {
        this.createDraftCerca = { ...(this.createDraftCerca || {}), ...cerca };
      }
      if (rebuildHandles) {
        this.$nextTick(() => this.rebuildFenceEditHandles());
      }
    },
    restoreRedrawFenceSnapshot() {
      const snap = this.redrawFenceSnapshot;
      this.redrawFenceSnapshot = null;
      if (!snap || !snap.prev) return;
      this.patchLocalCercaGeometry(snap.prev, { rebuildHandles: true });
      if (snap.id_cerca != null) {
        this.selectFenceForFormatEdit(snap.id_cerca, snap.id_sentido);
      }
    },
    circularFenceRadiusHandlePosition(cerca, idKey) {
      const lat = Number(cerca.lat_centro);
      const lng = Number(cerca.lng_centro);
      const r = Math.max(MIN_FENCE_RADIUS_M, Number(cerca.raio_mts) || 0);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || r <= 0) return null;
      const key = idKey != null ? String(idKey) : String(cerca.id_cerca);
      const bearing = this.fenceRadiusHandleBearings[key] ?? 90;
      return destinationPointMeters(lat, lng, bearing, r);
    },
    snapRadiusHandleToBorder(handle, cerca) {
      if (!handle || !cerca || handle.kind !== "radius") return;
      const lat = Number(cerca.lat_centro);
      const lng = Number(cerca.lng_centro);
      const r = Math.max(MIN_FENCE_RADIUS_M, Number(cerca.raio_mts) || 0);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || r <= 0) return;
      const key = handle.id_cerca != null ? String(handle.id_cerca) : String(cerca.id_cerca);
      const bearing = bearingDegrees(lat, lng, Number(handle.lat), Number(handle.lng));
      this.$set(this.fenceRadiusHandleBearings, key, bearing);
      const border = destinationPointMeters(lat, lng, bearing, r);
      this.$set(handle, "lat", border.lat);
      this.$set(handle, "lng", border.lng);
      this.$set(handle, "bearing", bearing);
    },
    rebuildFenceEditHandles() {
      if (this._fenceHandleDragging) return;
      if (this._isDestroyed || this._isBeingDestroyed) {
        this.fenceEditHandles = [];
        return;
      }
      if (
        !(
          ((this.mapContext === "cercas" && this.isEntityPreview) || this.isLinhasContext) &&
          this.editMode === "editFences"
        )
      ) {
        this.fenceEditHandles = [];
        return;
      }
      const cercas = this.activeEditableCercas || [];
      if (!cercas.length) {
        this.fenceEditHandles = [];
        return;
      }
      const handles = [];
      cercas.forEach((cerca) => {
        if (!cerca) return;
        const draft = isDraftCerca(cerca) || !!cerca._createPreview;
        const idKey = draft ? DRAFT_CERCA_ID : Number(cerca.id_cerca);
        if (Number(cerca.is_circular) === 1) {
          const border = this.circularFenceRadiusHandlePosition(cerca, idKey);
          if (!border) return;
          const key = String(idKey);
          handles.push({
            kind: "radius",
            index: 0,
            lat: border.lat,
            lng: border.lng,
            bearing: this.fenceRadiusHandleBearings[key] ?? 90,
            id_cerca: idKey,
            _createPreview: draft,
          });
          return;
        }
        const ring = ringFromCerca(cerca);
        (ring || []).forEach((pt, index) => {
          handles.push({
            kind: "vertex",
            index,
            lat: pt[0],
            lng: pt[1],
            id_cerca: idKey,
            _createPreview: draft,
          });
        });
      });
      this.fenceEditHandles = handles;
    },
    applyFenceVertexHandle(cerca, index, lat, lng) {
      const ring = ringFromCerca(cerca).slice();
      if (index < 0 || index >= ring.length) return null;
      ring[index] = [lat, lng];
      const built = buildFenceFromRing(ring);
      return {
        ...cerca,
        ...built,
      };
    },
    applyFenceRadiusHandle(cerca, lat, lng) {
      const cLat = Number(cerca.lat_centro);
      const cLng = Number(cerca.lng_centro);
      if (!Number.isFinite(cLat) || !Number.isFinite(cLng)) return null;
      const meters = haversineMeters(cLat, cLng, lat, lng);
      const raio = Math.max(MIN_FENCE_RADIUS_M, Math.round(meters));
      return {
        ...cerca,
        raio_mts: raio,
        shape: null,
        geojson: {
          type: "Point",
          coordinates: [cLng, cLat],
        },
        is_circular: 1,
        lat_centro: cLat,
        lng_centro: cLng,
      };
    },
    /**
     * Converte a cerca ativa entre circular e polígono a partir da barra de edição.
     * Circular → polígono: apaga o círculo e habilita desenho no mapa.
     * Polígono → circular: centro + raio médio.
     */
    async onFenceEditBarFormatoChange(nextFormato) {
      if (!this.canChangeFenceFormat || this.fenceDragRecalculating) return;
      const cerca = this.activeFormatTargetCerca;
      if (!cerca) return;
      const wantCircular = nextFormato === "circular" || Number(nextFormato) === 1;
      const isCircular = Number(cerca.is_circular) === 1;
      if (wantCircular === isCircular) return;

      if (wantCircular) {
        const ring = ringFromCerca(cerca);
        let lat = Number(cerca.lat_centro);
        let lng = Number(cerca.lng_centro);
        if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && ring.length) {
          lat = ring.reduce((s, p) => s + p[0], 0) / ring.length;
          lng = ring.reduce((s, p) => s + p[1], 0) / ring.length;
        }
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("geofence.circularGeometryRequired") },
          });
          return;
        }
        let raio = Number(cerca.raio_mts);
        if (!Number.isFinite(raio) || raio <= 0) {
          if (ring.length) {
            raio = Math.max(
              ...ring.map(([rLat, rLng]) => haversineMeters(lat, lng, rLat, rLng)),
              MIN_FENCE_RADIUS_M,
            );
          } else {
            raio = 50;
          }
        }
        raio = Math.max(MIN_FENCE_RADIUS_M, Math.round(raio));
        const next = {
          ...cerca,
          is_circular: 1,
          raio_mts: raio,
          lat_centro: lat,
          lng_centro: lng,
          shape: null,
          geojson: { type: "Point", coordinates: [lng, lat] },
        };
        const prev = { ...cerca };
        this.patchLocalCercaGeometry(next, { rebuildHandles: true });
        try {
          await this.persistCercaGeometry(next, {
            onRevert: () => this.patchLocalCercaGeometry(prev, { rebuildHandles: true }),
            successMessage: this.$t("crudLinhas.map.fenceFormatUpdated"),
          });
        } catch (e) {
          /* persistCercaGeometry já emite erro / reverte */
        }
        return;
      }

      // Circular → polígono: remove o círculo da visualização e entra no desenho.
      const prev = { ...cerca };
      this.redrawFenceSnapshot = {
        id_cerca: cerca.id_cerca,
        id_sentido: cerca.id_sentido != null ? cerca.id_sentido : this.editingCercaSentido,
        prev,
      };
      this._redrawFenceCompleted = false;
      const cleared = {
        ...cerca,
        is_circular: 0,
        raio_mts: 0,
        shape: null,
        geojson: null,
      };
      this.selectFenceForFormatEdit(cerca.id_cerca, this.redrawFenceSnapshot.id_sentido);
      this.patchLocalCercaGeometry(cleared, { rebuildHandles: true });
      this.editMode = "drawFence";
      EventBus.$emit("show-success", {
        message: this.$t("crudLinhas.map.fenceRedrawPolygonHint"),
      });
    },
    onFenceHandleDrag(handle, ev) {
      if (!handle || handle.kind !== "radius" || this.fenceDragRecalculating) return;
      const newLatLng =
        ev && ev.target && typeof ev.target.getLatLng === "function"
          ? ev.target.getLatLng()
          : null;
      if (!newLatLng) return;
      const newLat = Number(newLatLng.lat);
      const newLng = Number(newLatLng.lng);
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) return;

      const draft = handle.id_cerca === DRAFT_CERCA_ID || handle._createPreview;
      const cerca = draft ? this.createDraftCerca : this.resolveCercaById(handle.id_cerca);
      if (!cerca) return;

      const cLat = Number(cerca.lat_centro);
      const cLng = Number(cerca.lng_centro);
      if (!Number.isFinite(cLat) || !Number.isFinite(cLng)) return;

      this._fenceHandleDragging = true;
      const raio = Math.max(MIN_FENCE_RADIUS_M, Math.round(haversineMeters(cLat, cLng, newLat, newLng)));
      const bearing = bearingDegrees(cLat, cLng, newLat, newLng);
      const key = handle.id_cerca != null ? String(handle.id_cerca) : String(cerca.id_cerca);
      // Sem $set/reatividade no mousemove — só Leaflet setRadius (evita travar Linhas e Cercas).
      this._fenceRadiusBearingsDrag[key] = bearing;
      this.setCircularCercaRadiusImperative(handle.id_cerca, raio, draft);
    },
    circularCercaLayerKey(cerca) {
      if (!cerca) return null;
      if (cerca._createPreview || cerca.id_cerca === DRAFT_CERCA_ID || isDraftCerca(cerca)) {
        return String(DRAFT_CERCA_ID);
      }
      if (cerca.id_cerca == null) return null;
      return String(cerca.id_cerca);
    },
    onCircularCercaLayerReady(cerca, layer) {
      const key = this.circularCercaLayerKey(cerca);
      if (!key || !layer) return;
      this._circularCercaLayers[key] = layer;
    },
    onPreviewCircularCercaLayerReady(cerca, layer) {
      if (!cerca || cerca.id_cerca == null || !layer) return;
      this._circularCercaLayers[`preview-${cerca.id_cerca}`] = layer;
    },
    setCircularCercaRadiusImperative(idCerca, raio, draft = false) {
      const keys =
        draft || idCerca === DRAFT_CERCA_ID
          ? [String(DRAFT_CERCA_ID)]
          : [String(idCerca), `preview-${idCerca}`];
      keys.forEach((key) => {
        const layer = this._circularCercaLayers[key];
        if (layer && typeof layer.setRadius === "function") {
          layer.setRadius(raio);
        }
      });
    },
    async onFenceHandleDragend(handle, ev) {
      this._fenceHandleDragging = false;
      if (!handle || this.fenceDragRecalculating) return;
      const newLatLng =
        ev && ev.target && typeof ev.target.getLatLng === "function"
          ? ev.target.getLatLng()
          : null;
      if (!newLatLng) return;
      const newLat = Number(newLatLng.lat);
      const newLng = Number(newLatLng.lng);
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) return;
      const oldLat = Number(handle.lat);
      const oldLng = Number(handle.lng);
      const revert = () => {
        if (ev && ev.target && typeof ev.target.setLatLng === "function") {
          ev.target.setLatLng([oldLat, oldLng]);
        }
        this.$set(handle, "lat", oldLat);
        this.$set(handle, "lng", oldLng);
      };

      const draft = handle.id_cerca === DRAFT_CERCA_ID || handle._createPreview;
      let cerca = null;
      if (draft) {
        cerca = this.createDraftCerca;
      } else {
        cerca = this.resolveCercaById(handle.id_cerca);
        // Raio só precisa do centro já em cache; force refresh só para vértices (shape).
        if (handle.kind !== "radius" && handle.id_cerca != null) {
          try {
            const detail = await this.ensureCercaDetail(handle.id_cerca, { force: true });
            if (detail) cerca = detail;
          } catch (e) {
            /* cache */
          }
        }
      }
      if (!cerca) {
        revert();
        return;
      }
      let next = null;
      if (handle.kind === "radius") {
        next = this.applyFenceRadiusHandle(cerca, newLat, newLng);
      } else {
        next = this.applyFenceVertexHandle(cerca, handle.index, newLat, newLng);
      }
      if (!next) {
        revert();
        return;
      }
      if (draft) {
        next.id_cerca = null;
        next._createPreview = true;
      }
      if (handle.kind === "radius") {
        const key = handle.id_cerca != null ? String(handle.id_cerca) : String(cerca.id_cerca);
        const dragBearing = this._fenceRadiusBearingsDrag[key];
        if (dragBearing != null) {
          this.$set(this.fenceRadiusHandleBearings, key, dragBearing);
          delete this._fenceRadiusBearingsDrag[key];
        }
        this.snapRadiusHandleToBorder(handle, next);
        if (ev && ev.target && typeof ev.target.setLatLng === "function") {
          ev.target.setLatLng([Number(handle.lat), Number(handle.lng)]);
        }
      } else {
        this.$set(handle, "lat", newLat);
        this.$set(handle, "lng", newLng);
      }
      this.patchLocalCercaGeometry(next);
      try {
        // Alterar só o raio não muda o centro: não recalcular traçado (VROOM).
        if (this.isLinhasContext && !draft && handle.kind === "radius") {
          await this.persistCercaGeometry(next, {
            onRevert: revert,
            successMessage: this.$t("crudLinhas.map.fenceUpdated"),
          });
          this.$emit("cercas-changed");
          return;
        }
        if (this.isLinhasContext && !draft) {
          await this.applyLinhaFenceMoveAndRecalcRoute(next, { onRevert: revert });
          return;
        }
        await this.persistCercaGeometry(next, {
          onRevert: revert,
          successMessage: this.$t("crudLinhas.map.fenceUpdated"),
        });
      } catch (e) {
        /* já tratado */
      }
    },
    iconFenceHandle(kind) {
      const cacheKey = kind === "radius" ? "radius" : "vertex";
      if (this._fenceHandleIcons[cacheKey]) return this._fenceHandleIcons[cacheKey];
      let icon;
      if (kind === "radius") {
        icon = L.divIcon({
          className: "linha-map-fence-handle",
          html:
            `<div style="width:14px;height:14px;border-radius:50%;background:#FF9800;` +
            `border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45)"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        });
      } else {
        icon = L.divIcon({
          className: "linha-map-fence-handle",
          html:
            `<div style="width:12px;height:12px;background:#fff;border:2px solid #1565C0;` +
            `box-shadow:0 1px 3px rgba(0,0,0,.45)"></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
      }
      this._fenceHandleIcons[cacheKey] = icon;
      return icon;
    },
    setDrawPolygonMode(on) {
      if (!this.isCercasPreview && !this.isLinhasContext) return;
      this.editMode = on ? "drawFence" : "view";
    },
    setDrawRouteMode(on) {
      if (!this.isRotasPreview) return;
      if (on) {
        this.editMode = "drawRoute";
        return;
      }
      if (this.editMode === "drawRoute") this.editMode = "view";
    },
    setupDrawFence() {
      if (!this.map) return;
      this.teardownDrawFenceClick();
      if (this.map.doubleClickZoom) this.map.doubleClickZoom.disable();
      this.drawFenceHandler = (e) => {
        if (this.editMode !== "drawFence" || !e || !e.latlng) return;
        this.addDrawFenceVertex(e.latlng.lat, e.latlng.lng);
      };
      this.map.on("click", this.drawFenceHandler);
    },
    teardownDrawFenceClick() {
      if (this.map && this.drawFenceHandler) {
        this.map.off("click", this.drawFenceHandler);
        this.drawFenceHandler = null;
      }
    },
    teardownDrawFence() {
      this.resetDrawFenceVertices();
      this.teardownDrawFenceClick();
      if (this.map && this.map.doubleClickZoom) this.map.doubleClickZoom.enable();
    },
    resetDrawFenceVertices() {
      (this._drawFenceMarkers || []).forEach((m) => {
        if (m && this.map) this.map.removeLayer(m);
      });
      this._drawFenceMarkers = [];
      if (this._drawFenceOutline && this.map) {
        this.map.removeLayer(this._drawFenceOutline);
      }
      this._drawFenceOutline = null;
      this.drawFenceVertices = [];
      if (this.editMode === "drawFence") {
        this.syncDrawFenceDraftToForm();
      }
    },
    addDrawFenceVertex(lat, lng) {
      const la = Number(lat);
      const ln = Number(lng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
      this.drawFenceVertices.push({ lat: la, lng: ln });
      if (this.map) {
        const marker = L.circleMarker([la, ln], {
          radius: 6,
          color: "#FF6F00",
          weight: 2,
          fillColor: "#FFB74D",
          fillOpacity: 1,
        });
        marker.addTo(this.map);
        if (!this._drawFenceMarkers) this._drawFenceMarkers = [];
        this._drawFenceMarkers.push(marker);
      }
      this.redrawDrawFenceOutline();
      this.syncDrawFenceDraftToForm();
    },
    undoLastDrawFenceVertex() {
      this.drawFenceVertices.pop();
      const last = (this._drawFenceMarkers || []).pop();
      if (last && this.map) this.map.removeLayer(last);
      this.redrawDrawFenceOutline();
      this.syncDrawFenceDraftToForm();
    },
    redrawDrawFenceOutline() {
      if (this._drawFenceOutline && this.map) {
        this.map.removeLayer(this._drawFenceOutline);
        this._drawFenceOutline = null;
      }
      const pts = (this.drawFenceVertices || []).map((v) => [v.lat, v.lng]);
      if (!this.map || pts.length < 2) return;
      if (pts.length === 2) {
        this._drawFenceOutline = L.polyline(pts, {
          color: "#FF6F00",
          weight: 2,
          dashArray: "6 4",
        });
      } else {
        this._drawFenceOutline = L.polygon(pts, {
          color: "#FF6F00",
          weight: 2,
          dashArray: "6 4",
          fillColor: "#FF6F00",
          fillOpacity: 0.15,
        });
      }
      this._drawFenceOutline.addTo(this.map);
    },
    /**
     * Espelha o polígono em desenho no formulário (a partir de 3 vértices),
     * sem sair do modo desenho — evita salvar sem formato definido.
     * No redesenho de cerca existente não emite para o formulário de criação.
     */
    syncDrawFenceDraftToForm() {
      if (this.editMode !== "drawFence") return;
      if (this.redrawFenceSnapshot) return;
      if (this.drawFenceVertices.length < 3) {
        this.$emit("map-action", {
          context: this.mapContext,
          action: "fence-polygon-draft",
          geometry: {
            shape: null,
            geojson: null,
            is_circular: 0,
            raio_mts: 0,
          },
        });
        return;
      }
      const ring = this.drawFenceVertices.map((v) => [v.lat, v.lng]);
      const geometry = buildFenceFromRing(ring);
      this.$emit("map-action", {
        context: this.mapContext,
        action: "fence-polygon-draft",
        geometry,
      });
    },
    async finalizeDrawFence() {
      if (this.drawFenceVertices.length < 3) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.drawFenceMinVertices") },
        });
        return false;
      }
      const ring = this.drawFenceVertices.map((v) => [v.lat, v.lng]);
      const geometry = buildFenceFromRing(ring);

      // Redesenho após trocar circular → polígono: grava na cerca existente.
      const snap = this.redrawFenceSnapshot;
      if (snap && snap.id_cerca != null) {
        const base =
          this.resolveCercaById(snap.id_cerca) ||
          (snap.prev ? { ...snap.prev } : null) ||
          {};
        const next = {
          ...base,
          ...geometry,
          id_cerca: snap.id_cerca,
          is_circular: 0,
          raio_mts: 0,
        };
        this._redrawFenceCompleted = true;
        this.redrawFenceSnapshot = null;
        this.editMode = "editFences";
        this.selectFenceForFormatEdit(snap.id_cerca, snap.id_sentido);
        this.patchLocalCercaGeometry(next, { rebuildHandles: true });
        try {
          await this.persistCercaGeometry(next, {
            onRevert: () => {
              if (snap.prev) {
                this.patchLocalCercaGeometry(snap.prev, { rebuildHandles: true });
              }
            },
            successMessage: this.$t("crudLinhas.map.fenceFormatUpdated"),
          });
        } catch (e) {
          /* persistCercaGeometry já emite erro / reverte */
        }
        return true;
      }

      // Fluxo de criação: sai do desenho e devolve geometria ao formulário.
      this.editMode = "view";
      this.$emit("map-action", {
        context: this.mapContext,
        action: "fence-polygon-drawn",
        geometry,
      });
      return true;
    },
    /** Usado no save: confirma polígono pendente sem exigir clique em Finalizar. */
    finalizeDrawFenceIfPending() {
      if (this.editMode !== "drawFence") return false;
      if (this.drawFenceVertices.length < 3) return false;
      return this.finalizeDrawFence();
    },
    setupDrawRoute() {
      if (!this.map) return;
      this.teardownDrawRouteClick();
      if (this.map.doubleClickZoom) this.map.doubleClickZoom.disable();
      this.drawRouteHandler = (e) => {
        if (this.editMode !== "drawRoute" || !e || !e.latlng) return;
        this.addDrawRouteVertex(e.latlng.lat, e.latlng.lng);
      };
      this.map.on("click", this.drawRouteHandler);
    },
    teardownDrawRouteClick() {
      if (this.map && this.drawRouteHandler) {
        this.map.off("click", this.drawRouteHandler);
        this.drawRouteHandler = null;
      }
    },
    teardownDrawRoute() {
      this.resetDrawRouteVertices({ skipSync: true });
      this.teardownDrawRouteClick();
      if (this.map && this.map.doubleClickZoom) this.map.doubleClickZoom.enable();
    },
    resetDrawRouteVertices({ skipSync } = {}) {
      (this._drawRouteMarkers || []).forEach((m) => {
        if (m && this.map) this.map.removeLayer(m);
      });
      this._drawRouteMarkers = [];
      if (this._drawRouteOutline && this.map) {
        this.map.removeLayer(this._drawRouteOutline);
      }
      this._drawRouteOutline = null;
      this.drawRouteVertices = [];
      if (!skipSync && this.editMode === "drawRoute") {
        this.syncDrawRouteDraftToForm();
      }
    },
    addDrawRouteVertex(lat, lng) {
      const la = Number(lat);
      const ln = Number(lng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
      this.drawRouteVertices.push({ lat: la, lng: ln });
      if (this.map) {
        const marker = L.circleMarker([la, ln], {
          radius: 6,
          color: PREVIEW_ROTA_COLOR,
          weight: 2,
          fillColor: "#ffb74d",
          fillOpacity: 1,
        });
        marker.addTo(this.map);
        if (!this._drawRouteMarkers) this._drawRouteMarkers = [];
        this._drawRouteMarkers.push(marker);
      }
      this.redrawDrawRouteOutline();
      this.syncDrawRouteDraftToForm();
    },
    undoLastDrawRouteVertex() {
      this.drawRouteVertices.pop();
      const last = (this._drawRouteMarkers || []).pop();
      if (last && this.map) this.map.removeLayer(last);
      this.redrawDrawRouteOutline();
      this.syncDrawRouteDraftToForm();
    },
    redrawDrawRouteOutline() {
      if (this._drawRouteOutline && this.map) {
        this.map.removeLayer(this._drawRouteOutline);
        this._drawRouteOutline = null;
      }
      const pts = (this.drawRouteVertices || []).map((v) => [v.lat, v.lng]);
      if (!this.map || pts.length < 2) return;
      this._drawRouteOutline = L.polyline(pts, {
        color: PREVIEW_ROTA_COLOR,
        weight: 4,
        dashArray: "6 4",
        opacity: 0.95,
      });
      this._drawRouteOutline.addTo(this.map);
    },
    syncDrawRouteDraftToForm() {
      if (this.editMode !== "drawRoute") return;
      if (this.drawRouteVertices.length < 2) {
        this.$emit("map-action", {
          context: this.mapContext,
          action: "route-line-draft",
          geometry: { shape: null, geojson: null, distancia: null },
        });
        return;
      }
      const geometry = buildRouteFromVertices(this.drawRouteVertices);
      this.$emit("map-action", {
        context: this.mapContext,
        action: "route-line-draft",
        geometry,
      });
    },
    finalizeDrawRoute() {
      if (this.drawRouteVertices.length < 2) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.drawRouteMinVertices") },
        });
        return false;
      }
      const geometry = buildRouteFromVertices(this.drawRouteVertices);
      this.editMode = "view";
      this.$emit("map-action", {
        context: this.mapContext,
        action: "route-line-drawn",
        geometry,
      });
      return true;
    },
    /** Usado no save: confirma linha pendente sem exigir clique em Concluir. */
    finalizeDrawRouteIfPending() {
      if (this.editMode !== "drawRoute") return false;
      if (this.drawRouteVertices.length < 2) return false;
      return this.finalizeDrawRoute();
    },
    previewAndFocusRota(rota, { focus = true } = {}) {
      if (!rota) return;
      const snapshot = { ...rota };
      this.createDraftRota = snapshot;
      this.applyCreateDraftRouteLayers(snapshot);
      if (focus) {
        const gj = this.parseGeojson(snapshot.geojson) || this.rotaToGeojson(snapshot);
        if (gj) {
          const points = [];
          this.collectGeojsonLatLngs(gj, points);
          if (points.length && this.map) {
            try {
              this.map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
            } catch (e) {
              /* ignore */
            }
          }
        }
      }
    },
    applyCreateDraftRouteLayers(rota) {
      if (!rota) return;
      this.mapRotas = (this.mapRotas || []).filter((r) => !r._createPreview);
      const gj = this.parseGeojson(rota.geojson) || this.rotaToGeojson(rota);
      if (!gj) return;
      this.mapRotas.push({
        ...rota,
        id_rota: rota.id_rota == null ? DRAFT_ROTA_ID : rota.id_rota,
        geojson: gj,
        _createPreview: true,
        style: { color: PREVIEW_ROTA_COLOR, weight: 5, opacity: 0.95 },
        options: {
          onEachFeature: (feat, layer) => {
            const label = rota.nome_rota || rota.cod_rota || "";
            if (label) layer.bindPopup(String(label));
          },
        },
      });
    },
    clearCreateDraftRota() {
      this.createDraftRota = null;
      this.mapRotas = (this.mapRotas || []).filter((r) => !r._createPreview);
    },
    /**
     * Redesenha polígono/círculo da cerca do preview após o arrasto.
     * O marker é preservado: destruí-lo com o handler de dragend ainda na
     * pilha quebra o Leaflet.
     */
    rebuildPreviewFenceLayers(cerca) {
      if (cerca && cerca.id_cerca != null && !isDraftCerca(cerca)) {
        this.$set(this.cercaDetailCache, Number(cerca.id_cerca), { ...cerca });
      }
      const markers = this.mapCercaMarkers;
      this.mapCercasGeojson = [];
      this.mapCercasCirculares = [];
      this.mapCercaMarkers = [];
      this.previewLayerRev += 1;

      const items = this.entityPreviewItemsResolved || [];
      const multi = items.length > 1;
      if (items.length) {
        items.forEach((item, idx) => {
          if (!item || item.id_cerca == null || isDraftCerca(item)) return;
          let c = this.resolveCercaById(item.id_cerca) || item;
          if (
            cerca &&
            !isDraftCerca(cerca) &&
            Number(c.id_cerca) === Number(cerca.id_cerca)
          ) {
            c = cerca;
          }
          const color = multi ? CERCA_COLORS[idx % CERCA_COLORS.length] : PREVIEW_CERCA_COLOR;
          this.applyCercaToMapLayers(c, color);
        });
      } else if (cerca && !isDraftCerca(cerca)) {
        this.applyCercaToMapLayers(cerca, PREVIEW_CERCA_COLOR);
      }

      if (this.drawerCreateOpen && this.createDraftCerca) {
        this.applyCreateDraftLayers(this.createDraftCerca);
      }

      if (!this.mapCercaMarkers.length) {
        this.mapCercaMarkers = markers;
      }
    },
    /**
     * Edição de traçado no preview de Rotas. Sem cercas de parada, usa o
     * primeiro e o último ponto do próprio traçado como início/fim e as vias
     * clicadas como pontos intermediários.
     * Vias de applies anteriores na mesma sessão são reenviadas para o plan
     * (senão o 2º apply recalcula só com início/fim e desfaz o 1º).
     */
    async applyPreviewRouteEdit() {
      if (this.editRecalculating) return;
      if (!this.editWaypoints.length && !this.editRouteGeometryDirty) return;
      const rotaId =
        this.activeEditRouteId != null
          ? this.activeEditRouteId
          : (this.selectedRotaDetail && this.selectedRotaDetail.id_rota) ||
            (this.entityPreviewItem && this.entityPreviewItem.id_rota);
      if (rotaId == null) return;

      // Só vértices arrastados: persiste o rascunho local, sem VROOM.
      if (!this.editWaypoints.length && this.editRouteGeometryDirty) {
        return this.persistDraftRouteVertexEdit(rotaId);
      }

      // Geometria atual: mapa (o que o usuário vê). Com alterações pendentes,
      // não sobrescrever com o banco (senão perde o arraste antes do plan).
      const fromMap = (this.mapRotas || []).find((r) => Number(r.id_rota) === Number(rotaId));
      let rota =
        fromMap ||
        this.resolveRotaById(rotaId) ||
        this.selectedRotaDetail ||
        this.entityPreviewItem;
      if (!this.editRouteGeometryDirty) {
        const fresh = await this.ensureRotaDetail(rotaId, { force: true });
        if (fresh) {
          rota = fresh;
          if (fromMap && (fromMap.geojson || fromMap.shape)) {
            rota = {
              ...rota,
              geojson: fromMap.geojson != null ? fromMap.geojson : rota.geojson,
              shape: fromMap.shape != null ? fromMap.shape : rota.shape,
            };
          }
        }
      } else if (fromMap && (fromMap.geojson || fromMap.shape)) {
        rota = {
          ...(rota || {}),
          geojson: fromMap.geojson != null ? fromMap.geojson : rota && rota.geojson,
          shape: fromMap.shape != null ? fromMap.shape : rota && rota.shape,
          distancia: fromMap.distancia != null ? fromMap.distancia : rota && rota.distancia,
        };
      }
      if (!rota || rota.id_rota == null) return;

      const gj = this.parseGeojson(rota.geojson) || this.rotaToGeojson(rota);
      const lineCoords = lngLatFromGeojson(gj);
      if (!lineCoords || lineCoords.length < 2) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.noGeometry") },
        });
        return;
      }
      const startCoord = lineCoords[0];
      const endCoord = lineCoords[lineCoords.length - 1];
      // Rota circular: início e fim coincidem e o VROOM devolve plano degenerado.
      if (haversineMeters(startCoord[1], startCoord[0], endCoord[1], endCoord[0]) < 20) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.previewRouteCircular") },
        });
        return;
      }
      const latlngs = lineCoords.map((c) => L.latLng(c[1], c[0]));
      const allWaypoints = [
        ...(this.appliedEditVias || []).map((v) => ({ lat: v.lat, lng: v.lng })),
        ...(this.editWaypoints || []),
      ];
      const viasOrdenadas = sortViasForPlan({
        latlngs,
        stopsLngLat: [],
        hasStart: true,
        hasEnd: true,
        startCoord,
        endCoord,
        editWaypoints: allWaypoints,
      });
      const payload = buildVersoPlanPayload({
        stopCoordsLngLat: [],
        viasOrdenadas,
        startCoord,
        endCoord,
        serviceTime: 0,
      });

      this.editRecalculating = true;
      try {
        const planRes = await apiController.request(apiController.endpoints.versoPlan, {
          data: payload,
          silent: true,
        });
        const planned = planRes.data;
        if (!planned || planned.code !== 0 || !Array.isArray(planned.routes) || !planned.routes.length) {
          if (planRes.status === 429 || (planned && planned.codigo === "RATE_LIMIT_VROOM")) {
            throw new Error(this.$t("crudLinhas.map.vroomRateLimit"));
          }
          throw new Error(
            (planned && (planned.error || planned.message)) ||
              this.$t("crudLinhas.map.editRouteRecalcError")
          );
        }
        const decoded = decodePlanGeometry(planned.routes[0].geometry);
        if (!decoded) {
          throw new Error(this.$t("crudLinhas.map.editRouteRecalcError"));
        }
        const distancia = Number(planned.routes[0].distance) || rota.distancia || 0;
        const updated = await updateMainRouteGeometry(rota, {
          geojson: decoded.geojson,
          shape: decoded.shape,
          distance: distancia,
        });
        if (!updated || !updated.ok) {
          throw new Error(
            (updated && updated.message) || this.$t("crudLinhas.map.editRouteRecalcError")
          );
        }
        const newlyApplied = (this.editWaypoints || [])
          .map((v) => ({ lat: Number(v.lat), lng: Number(v.lng) }))
          .filter((v) => Number.isFinite(v.lat) && Number.isFinite(v.lng));
        this.appliedEditVias = [...(this.appliedEditVias || []), ...newlyApplied];
        const next = {
          ...rota,
          geojson:
            typeof decoded.geojson === "string"
              ? decoded.geojson
              : JSON.stringify(decoded.geojson),
          shape: decoded.shape,
          distancia,
        };
        this.selectedRotaDetail = next;
        this.resetEditWaypoints();
        this.editRouteGeometryDirty = false;
        this.markEditRouteBaselineCommitted(next);
        this.rebuildPreviewRouteLayer(next);
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(next.id_rota));
        this.$emit("preview-changed", { type: "route", item: next });
        EventBus.$emit("show-success", { message: this.$t("crudLinhas.map.routeUpdated") });
      } catch (e) {
        const status = e && e.response && e.response.status;
        const codigo = e && e.response && e.response.data && e.response.data.codigo;
        EventBus.$emit("show-error", {
          error:
            status === 429 || codigo === "RATE_LIMIT_VROOM"
              ? { message: this.$t("crudLinhas.map.vroomRateLimit") }
              : e,
        });
      } finally {
        this.editRecalculating = false;
      }
    },
    rebuildPreviewRouteLayer(rota) {
      if (rota && rota.id_rota != null) {
        this.selectedRotaDetail = rota;
        // Mantém geometria atualizada na lista do mapa antes do refresh.
        const idx = (this.mapRotas || []).findIndex(
          (r) => Number(r.id_rota) === Number(rota.id_rota)
        );
        if (idx >= 0) {
          this.$set(this.mapRotas, idx, { ...this.mapRotas[idx], ...rota });
        } else {
          this.mapRotas = [...(this.mapRotas || []), rota];
        }
      }
      this.refreshPreviewRouteLayers();
    },
    async persistHistoricoCercaDrag({
      orderChanged,
      typesChanged,
      tiposAlterados,
      pinStart,
      cercasAntes,
      orderedCercas,
      planResult,
      mainRota,
      sentido,
      moved,
      oldLat,
      oldLng,
    }) {
      let user = null;
      try {
        user = JSON.parse(sessionStorage.getItem("user"));
      } catch (e) {
        user = null;
      }
      const ordered = Array.isArray(orderedCercas)
        ? orderedCercas.map((cerca) => ({ ...cerca }))
        : [];
      const destinationIds = ordered
        .filter((cerca) => cerca && [2, 3].includes(Number(cerca.id_tipo_cerca)))
        .map((cerca) => cerca.id_cerca);
      const plan = planResult || {};
      const rota = mainRota || {};
      const sequenceChanged = !!(orderChanged || typesChanged);
      const detalhe = {
        origem: "CRUD_LINHAS",
        acao: "EDITAR_PARADA_ARRASTO",
        id_linha: this.linha && this.linha.id_linha,
        cod_linha: this.linha && this.linha.cod_linha,
        nome_linha: this.linha && this.linha.nome_linha,
        id_sentido: sentido,
        ordem_alterada: !!orderChanged,
        tipos_alterados: Array.isArray(tiposAlterados) ? tiposAlterados : [],
        ponto_inicial_pinado: !!pinStart,
        id_cerca: moved && moved.id_cerca,
        lat: moved && moved.lat_centro,
        lng: moved && moved.lng_centro,
        lat_anterior: oldLat,
        lng_anterior: oldLng,
        rota_atual: {
          id_rota: rota.id_rota,
          cod_rota: rota.cod_rota,
          nome_rota: rota.nome_rota,
          id_sentido: sentido,
        },
      };
      const historyResult = await persistHistoricoRoteirizacaoCrudEvent({
        idSessaoOrigem: this.historicoSessaoOrigemId,
        idSessaoCrud: this.historicoSessaoId,
        idCercaDestinoCandidates: destinationIds,
        idUsuario: user && user.id_usuario,
        tipoEvento: sequenceChanged || plan.geojson
          ? TIPO_EVENTO_HISTORICO.EDITADA_ROTA
          : TIPO_EVENTO_HISTORICO.EDITADA_PARADA,
        detalhe,
        snapshotDetalhe: {
          ...detalhe,
          cercas_antes: Array.isArray(cercasAntes) ? cercasAntes : [],
          cercas_depois: fenceSnapshotForHistory(ordered),
          rota_atual: {
            ...detalhe.rota_atual,
            geojson: plan.geojson,
            shape: plan.shape,
            distancia: plan.distance,
            duracao: plan.durationSec,
          },
        },
      });
      if (!historyResult || !historyResult.ok) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.map.stopDragHistorySaveError"),
          tipo: "warning",
        });
      }
    },
    extractFirstLatLng(geojson) {
      if (!geojson) return null;
      if (geojson.type === "Feature") return this.extractFirstLatLng(geojson.geometry);
      if (geojson.type === "FeatureCollection") {
        const feat = geojson.features && geojson.features[0];
        return feat ? this.extractFirstLatLng(feat) : null;
      }
      const coords = geojson.coordinates;
      if (!coords) return null;
      const walk = (c) => {
        if (!Array.isArray(c) || !c.length) return null;
        if (typeof c[0] === "number" && typeof c[1] === "number") return [c[1], c[0]];
        return walk(c[0]);
      };
      if (geojson.type === "LineString") return walk(coords);
      if (geojson.type === "MultiLineString") return walk(coords[0]);
      if (geojson.type === "Polygon") return walk(coords[0]);
      if (geojson.type === "Point") return walk(coords);
      return null;
    },
    firstRoutePoint() {
      const rota = this.mapRotas[0];
      if (!rota || !rota.geojson) return null;
      return this.extractFirstLatLng(rota.geojson);
    },
    iconPinCerca(color) {
      const fill = color || "#1976D2";
      return L.divIcon({
        className: "linha-map-cerca-pin",
        html:
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28">` +
          `<path fill="${fill}" stroke="#ffffff" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>` +
          `</svg>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
      });
    },
    iconPinPassageiro(pax) {
      const validated = isValidado(pax);
      const fill = validated ? "#2E7D32" : "#9E9E9E";
      return L.divIcon({
        className: "linha-map-pax-pin",
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${fill};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
    },
    latLng(lat, lng) {
      const fallback = L.latLng(this.defaultCenter[0], this.defaultCenter[1]);
      const la = parseFloat(lat);
      const ln = parseFloat(lng);
      if (Number.isNaN(la) || Number.isNaN(ln)) return fallback;
      return L.latLng(la, ln);
    },
    onMapReady() {
      const ref = this.$refs.mapRef;
      if (ref) {
        this.map = ref.mapObject;
        if (this.map && this.map.scrollWheelZoom) {
          this.map.scrollWheelZoom.enable();
        }
        this.$nextTick(() => {
          if (this.map) this.map.invalidateSize();
          this.scheduleFocusMap();
          if (this.editMode === "editRoute") {
            this.setupEditRoute();
            if (this.activeEditRouteId != null) {
              this.showEditRouteVertexMarkersForId(this.activeEditRouteId);
            }
          }
          if (this.editMode === "drawFence") this.setupDrawFence();
          if (this.editMode === "drawRoute") this.setupDrawRoute();
          if (this.pickLocationMode) this.setupPickLocation();
          this.rebuildFenceEditHandles();
        });
      }
    },
    scheduleFocusMap() {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      this.$nextTick(() => this.fitMapToLinha());
      setTimeout(() => {
        if (this._isDestroyed || this._isBeingDestroyed) return;
        this.fitMapToLinha();
      }, 280);
    },
    collectGeojsonLatLngs(geojson, out) {
      if (!geojson) return;
      if (geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) {
        geojson.features.forEach((f) => this.collectGeojsonLatLngs(f, out));
        return;
      }
      const geom = geojson.type === "Feature" ? geojson.geometry : geojson;
      if (!geom || !geom.coordinates) return;
      const pushCoords = (coords) => {
        if (!Array.isArray(coords)) return;
        if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
          const lat = Number(coords[1]);
          const lng = Number(coords[0]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) out.push([lat, lng]);
          return;
        }
        coords.forEach((c) => pushCoords(c));
      };
      pushCoords(geom.coordinates);
    },
    collectLinhaMapPoints() {
      const pts = [];
      (this.mapRotas || []).forEach((rota) => {
        if (rota && rota.geojson) this.collectGeojsonLatLngs(rota.geojson, pts);
      });
      (this.mapCercasGeojson || []).forEach((cerca) => {
        if (cerca && cerca.geojson) this.collectGeojsonLatLngs(cerca.geojson, pts);
      });
      (this.mapCercasCirculares || []).forEach((cerca) => {
        const lat = Number(cerca.lat_centro);
        const lng = Number(cerca.lng_centro);
        const r = Number(cerca.raio_mts);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (Number.isFinite(r) && r > 0) {
          const dLat = r / 111320;
          const cosLat = Math.cos((lat * Math.PI) / 180);
          const dLng = cosLat !== 0 ? r / (111320 * cosLat) : dLat;
          pts.push([lat - dLat, lng - dLng], [lat + dLat, lng + dLng]);
        } else {
          pts.push([lat, lng]);
        }
      });
      (this.mapCercaMarkers || []).forEach((pin) => {
        const lat = Number(pin.lat);
        const lng = Number(pin.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
      });
      // Sobreposição das demais linhas marcadas na tabela também entra no enquadramento.
      (this.outrasLinhasMapRotas || []).forEach((rota) => {
        if (rota && rota.geojson) this.collectGeojsonLatLngs(rota.geojson, pts);
      });
      (this.outrasLinhasMapCercasGeojson || []).forEach((cerca) => {
        if (cerca && cerca.geojson) this.collectGeojsonLatLngs(cerca.geojson, pts);
      });
      (this.outrasLinhasMapCercasCirculares || []).forEach((cerca) => {
        const lat = Number(cerca.lat_centro);
        const lng = Number(cerca.lng_centro);
        if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
      });
      (this.outrasLinhasMapCercaMarkers || []).forEach((pin) => {
        const lat = Number(pin.lat);
        const lng = Number(pin.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
      });
      if (pts.length) return pts;
      (this.mapPassageiros || []).forEach((pax) => {
        const lat = Number(pax.lat);
        const lng = Number(pax.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
      });
      return pts;
    },
    normalizeMapZoom(zoom) {
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      const min = map && typeof map.getMinZoom === "function" ? map.getMinZoom() : 1;
      const max = map && typeof map.getMaxZoom === "function" ? map.getMaxZoom() : 18;
      const snap = (this.mapOptions && this.mapOptions.zoomSnap) || 1;
      let z = Number(zoom);
      if (!Number.isFinite(z)) z = Number(this.zoom) || 12;
      z = Math.max(min, Math.min(max, z));
      if (snap > 0) z = Math.round(z / snap) * snap;
      return z;
    },
    fitMapToLinha() {
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      if (!map) return;
      try {
        map.invalidateSize({ animate: false });
        const points = this.collectLinhaMapPoints();
        if (!points.length) {
          // Em redesenho de traçado a rota some de propósito: não pular para o centro padrão.
          if (this.editRouteRedrawing) return;
          const center = this.defaultCenter.slice();
          const z = this.normalizeMapZoom(this.zoom || 14);
          this.currentCenter = center;
          this.zoom = z;
          map.setView(center, z, { animate: false });
          return;
        }
        if (points.length === 1) {
          const z = this.normalizeMapZoom(14);
          this.currentCenter = points[0];
          this.zoom = z;
          map.setView(points[0], z, { animate: false });
          return;
        }
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 16 });
        const c = map.getCenter();
        this.currentCenter = [c.lat, c.lng];
        this.zoom = this.normalizeMapZoom(map.getZoom());
      } catch (e) {
        /* ignore */
      }
    },
    focusMap() {
      this.fitMapToLinha();
    },
    selectRouteForEdit(rotaId) {
      if (this.editMode !== "editRoute") return;
      if (this.activeEditRouteId != null && Number(this.activeEditRouteId) === Number(rotaId)) {
        this.discardUnsavedRouteEdit();
        this.activeEditRouteId = null;
        this.selectedMapRouteId = null;
        this.resetEditWaypoints();
        this.appliedEditVias = [];
        this.clearEditRouteVertexMarkers();
        if (this.isEntityPreview && this.isRotasPreview) {
          this.refreshPreviewRouteLayers();
        } else {
          this.aplicarFiltroSentido();
        }
        return;
      }
      if (
        this.activeEditRouteId != null &&
        Number(this.activeEditRouteId) !== Number(rotaId)
      ) {
        this.discardUnsavedRouteEdit();
      }
      this.resetEditWaypoints();
      this.appliedEditVias = [];
      this.activeEditRouteId = rotaId;
      this.selectedMapRouteId = Number(rotaId);
      this.itemRota.id_rota = rotaId;
      if (this.isEntityPreview) {
        const rota = this.resolveRotaById(rotaId);
        if (rota) this.selectedRotaDetail = rota;
        if (this.isRotasPreview) this.refreshPreviewRouteLayers();
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(rotaId));
        return;
      }
      this.aplicarFiltroSentido();
      this.$nextTick(() => {
        this.syncRotaSelect(rotaId);
        this.focusRotaOnMap(rotaId);
        this.showEditRouteVertexMarkersForId(rotaId);
      });
    },
    sampleRouteVerticesForDisplay(latlngs, maxPoints = EDIT_ROUTE_VERTEX_MARKER_MAX) {
      const pts = Array.isArray(latlngs) ? latlngs : [];
      if (!pts.length) return [];
      if (pts.length <= maxPoints) {
        return pts.map((ll, index) => ({
          lat: ll.lat,
          lng: ll.lng,
          index,
          isEnd: index === 0 || index === pts.length - 1,
        }));
      }
      const last = pts.length - 1;
      const out = [];
      let prevIdx = -1;
      for (let i = 0; i < maxPoints; i++) {
        const idx = Math.round((i / (maxPoints - 1)) * last);
        if (idx === prevIdx) continue;
        out.push({
          lat: pts[idx].lat,
          lng: pts[idx].lng,
          index: idx,
          isEnd: idx === 0 || idx === last,
        });
        prevIdx = idx;
      }
      return out;
    },
    clearEditRouteVertexMarkers() {
      this.editRouteVertexHandles = [];
      this._editRouteLngLats = null;
      this._routeVertexDragging = false;
    },
    captureEditRouteBaseline(rota) {
      if (!rota || rota.id_rota == null) {
        this._editRouteBaseline = null;
        this.editRouteGeometryDirty = false;
        this.editRouteRedrawing = false;
        return;
      }
      this._editRouteBaseline = {
        id_rota: rota.id_rota,
        geojson: rota.geojson,
        shape: rota.shape,
        distancia: rota.distancia,
      };
      this.editRouteGeometryDirty = false;
      this.editRouteRedrawing = false;
    },
    discardUnsavedRouteEdit() {
      const bas = this._editRouteBaseline;
      const dirty = this.editRouteGeometryDirty;
      this.editRouteGeometryDirty = false;
      this.editRouteRedrawing = false;
      if (!dirty || !bas || bas.id_rota == null) {
        this._editRouteBaseline = null;
        return;
      }
      this.patchLocalRouteGeometry(bas.id_rota, {
        geojson: bas.geojson,
        shape: bas.shape,
        distancia: bas.distancia,
      });
      this._editRouteBaseline = null;
    },
    markEditRouteBaselineCommitted(rota) {
      this.editRouteRedrawing = false;
      this.captureEditRouteBaseline(rota);
    },
    buildGeometryFromEditRouteLngLats(coords) {
      const list = Array.isArray(coords) ? coords : [];
      if (list.length < 2) return null;
      const geometry = buildRouteFromVertices(
        list.map((c) => ({ lat: c[1], lng: c[0] }))
      );
      if (!geometry || !geometry.geojson || !geometry.shape) return null;
      let meters = 0;
      for (let i = 1; i < list.length; i += 1) {
        meters += haversineMeters(list[i - 1][1], list[i - 1][0], list[i][1], list[i][0]);
      }
      return {
        geojson: geometry.geojson,
        shape: geometry.shape,
        distance: meters,
        geojsonStr:
          typeof geometry.geojson === "string"
            ? geometry.geojson
            : JSON.stringify(geometry.geojson),
        shapeStr:
          typeof geometry.shape === "string"
            ? geometry.shape
            : JSON.stringify(geometry.shape),
      };
    },
    showEditRouteVertexMarkersForId(rotaId) {
      this.clearEditRouteVertexMarkers();
      if (!this.map || rotaId == null || this.editMode !== "editRoute") return;
      const rota =
        (this.mapRotas || []).find((r) => Number(r.id_rota) === Number(rotaId)) ||
        this.resolveRotaById(rotaId) ||
        (this.selectedRotaDetail && Number(this.selectedRotaDetail.id_rota) === Number(rotaId)
          ? this.selectedRotaDetail
          : null) ||
        (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(rotaId)) ||
        null;
      if (!rota) return;
      // Baseline só na primeira carga da sessão de edição desta rota.
      if (
        !this._editRouteBaseline ||
        Number(this._editRouteBaseline.id_rota) !== Number(rotaId)
      ) {
        this.captureEditRouteBaseline(rota);
      }
      const source =
        this.editRouteGeometryDirty && this._editRouteLngLats
          ? null
          : rota;
      const gj = source
        ? this.parseGeojson(source.geojson) || this.rotaToGeojson(source)
        : {
            type: "LineString",
            coordinates: this._editRouteLngLats,
          };
      const lineCoords = this.editRouteGeometryDirty && this._editRouteLngLats
        ? this._editRouteLngLats
        : lngLatFromGeojson(gj);
      if (!lineCoords || lineCoords.length < 2) return;
      this._editRouteLngLats = lineCoords.map((c) => [Number(c[0]), Number(c[1])]);
      const latlngs = this._editRouteLngLats.map((c) => L.latLng(c[1], c[0]));
      this.editRouteVertexHandles = this.sampleRouteVerticesForDisplay(latlngs);
    },
    iconRouteVertexHandle(isEnd) {
      if (!this._routeVertexHandleIcons) this._routeVertexHandleIcons = Object.create(null);
      const cacheKey = isEnd ? "end" : "mid";
      if (this._routeVertexHandleIcons[cacheKey]) return this._routeVertexHandleIcons[cacheKey];
      const size = isEnd ? 14 : 12;
      const fill = isEnd ? "#ff9800" : "#fff";
      const border = isEnd ? "#e65100" : "#fb8c00";
      const icon = L.divIcon({
        className: "linha-map-route-vertex-handle",
        html:
          `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${fill};` +
          `border:2px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,.45);cursor:move"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      this._routeVertexHandleIcons[cacheKey] = icon;
      return icon;
    },
    onRouteVertexDragStart() {
      this._routeVertexDragging = true;
      this.lastPolylineClickMs = Date.now();
    },
    onRouteVertexDragend(handle, ev) {
      this._routeVertexDragging = false;
      this.lastPolylineClickMs = Date.now();
      if (!handle || !this.canEdit || this.editRecalculating) return;
      if (this.activeEditRouteId == null) return;
      const newLatLng =
        ev && ev.target && typeof ev.target.getLatLng === "function"
          ? ev.target.getLatLng()
          : null;
      if (!newLatLng) return;
      const newLat = Number(newLatLng.lat);
      const newLng = Number(newLatLng.lng);
      if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) return;
      const oldLat = Number(handle.lat);
      const oldLng = Number(handle.lng);
      const revert = () => {
        if (ev && ev.target && typeof ev.target.setLatLng === "function") {
          ev.target.setLatLng([oldLat, oldLng]);
        }
        this.$set(handle, "lat", oldLat);
        this.$set(handle, "lng", oldLng);
      };
      if (Math.abs(newLat - oldLat) < 1e-9 && Math.abs(newLng - oldLng) < 1e-9) return;

      const coords = Array.isArray(this._editRouteLngLats)
        ? this._editRouteLngLats.map((c) => [c[0], c[1]])
        : null;
      if (!coords || handle.index < 0 || handle.index >= coords.length) {
        revert();
        return;
      }
      coords[handle.index] = [newLng, newLat];
      const built = this.buildGeometryFromEditRouteLngLats(coords);
      if (!built) {
        revert();
        return;
      }

      const rotaId = this.activeEditRouteId;
      const rota =
        (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(rotaId)) ||
        this.resolveRotaById(rotaId) ||
        this.selectedRotaDetail ||
        null;
      if (!rota || rota.id_rota == null) {
        revert();
        return;
      }
      if (
        !this._editRouteBaseline ||
        Number(this._editRouteBaseline.id_rota) !== Number(rotaId)
      ) {
        this.captureEditRouteBaseline(rota);
      }

      this.$set(handle, "lat", newLat);
      this.$set(handle, "lng", newLng);
      this._editRouteLngLats = coords;
      this.editRouteGeometryDirty = true;
      this.patchLocalRouteGeometry(rotaId, {
        geojson: built.geojsonStr,
        shape: built.shapeStr,
        distancia: built.distance,
      });
    },
    patchLocalRouteGeometry(rotaId, { geojson, shape, distancia }) {
      if (rotaId == null) return;
      const id = Number(rotaId);
      const map = this.map || (this.$refs.mapRef && this.$refs.mapRef.mapObject);
      let keepCenter = null;
      let keepZoom = null;
      if (map && typeof map.getCenter === "function") {
        try {
          const c = map.getCenter();
          keepCenter = [c.lat, c.lng];
          keepZoom = map.getZoom();
        } catch (e) {
          /* ignore */
        }
      }
      const apply = (r) => {
        if (!r || Number(r.id_rota) !== id) return r;
        const next = {
          ...r,
          // undefined = manter; null = limpar (ex.: remover traçado)
          geojson: geojson !== undefined ? geojson : r.geojson,
          shape: shape !== undefined ? shape : r.shape,
          distancia: distancia !== undefined ? distancia : r.distancia,
          _rev: (r._rev || 0) + 1,
        };
        return next;
      };
      if (Array.isArray(this.rawRotas)) {
        const idx = this.rawRotas.findIndex((r) => Number(r.id_rota) === id);
        if (idx >= 0) this.$set(this.rawRotas, idx, apply(this.rawRotas[idx]));
      }
      if (Array.isArray(this.mapRotas)) {
        // Sem geometria: remove da lista do mapa para a polyline sumir na hora.
        this.mapRotas = this.mapRotas
          .map((r) => apply(r))
          .filter((r) => {
            if (!r || Number(r.id_rota) !== id) return true;
            const gj = this.parseGeojson(r.geojson) || this.rotaToGeojson(r);
            return !!gj;
          });
      }
      if (this.selectedRotaDetail && Number(this.selectedRotaDetail.id_rota) === id) {
        this.selectedRotaDetail = apply(this.selectedRotaDetail);
      }
      // Não atribuir em entityPreviewItem (prop): dispara syncEntityPreview e
      // o ensureRotaDetail recoloca o traçado antigo do banco no mapa.
      if (this.isEntityPreview && this.isRotasPreview) {
        this.refreshPreviewRouteLayers();
      } else if (!this.isEntityPreview) {
        this.aplicarFiltroSentido({ fitMap: false });
      }
      // Remover/atualizar traçado não deve recentrar o mapa (nem via :center do l-map).
      if (keepCenter && map) {
        const z = this.normalizeMapZoom(keepZoom != null ? keepZoom : this.zoom);
        this.currentCenter = keepCenter;
        this.zoom = z;
        this.$nextTick(() => {
          try {
            if (this.map) this.map.setView(keepCenter, z, { animate: false });
          } catch (e) {
            /* ignore */
          }
        });
      }
    },
    resetEditRouteDraft() {
      this.resetEditWaypoints();
      this.editRouteRedrawing = false;
      if (!this.editRouteGeometryDirty || !this._editRouteBaseline) return;
      const bas = this._editRouteBaseline;
      this.editRouteGeometryDirty = false;
      this.patchLocalRouteGeometry(bas.id_rota, {
        geojson: bas.geojson,
        shape: bas.shape,
        distancia: bas.distancia,
      });
      if (this.activeEditRouteId != null) {
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(this.activeEditRouteId));
      }
    },
    removeEditRouteTrace() {
      if (!this.canEdit || this.editRecalculating) return;
      const rotaId = this.activeEditRouteId;
      if (rotaId == null) return;
      const rota =
        (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(rotaId)) ||
        this.resolveRotaById(rotaId) ||
        this.selectedRotaDetail ||
        this.entityPreviewItem;
      if (!rota || rota.id_rota == null) return;
      if (
        !this._editRouteBaseline ||
        Number(this._editRouteBaseline.id_rota) !== Number(rotaId)
      ) {
        this.captureEditRouteBaseline(rota);
      }
      this.resetEditWaypoints();
      this.appliedEditVias = [];
      this._editRouteLngLats = [];
      this.editRouteVertexHandles = [];
      this.editRouteRedrawing = true;
      this.editRouteGeometryDirty = true;
      // Tira a polyline do mapa imediatamente (antes do patch/rebuild).
      this.mapRotas = (this.mapRotas || []).filter(
        (r) => Number(r.id_rota) !== Number(rotaId)
      );
      if (this.selectedRotaDetail && Number(this.selectedRotaDetail.id_rota) === Number(rotaId)) {
        this.selectedRotaDetail = {
          ...this.selectedRotaDetail,
          geojson: null,
          shape: null,
          distancia: 0,
          _rev: (this.selectedRotaDetail._rev || 0) + 1,
        };
      }
      this.patchLocalRouteGeometry(rotaId, {
        geojson: null,
        shape: null,
        distancia: 0,
      });
    },
    syncRedrawRouteLocalGeometry() {
      const rotaId = this.activeEditRouteId;
      if (rotaId == null) return;
      const coords = this._editRouteLngLats || [];
      if (coords.length < 2) {
        this.editRouteGeometryDirty = true;
        this.patchLocalRouteGeometry(rotaId, {
          geojson: null,
          shape: null,
          distancia: 0,
        });
        this.editRouteVertexHandles = this.sampleRouteVerticesForDisplay(
          coords.map((c) => L.latLng(c[1], c[0]))
        );
        return;
      }
      const built = this.buildGeometryFromEditRouteLngLats(coords);
      if (!built) return;
      this.editRouteGeometryDirty = true;
      this.patchLocalRouteGeometry(rotaId, {
        geojson: built.geojsonStr,
        shape: built.shapeStr,
        distancia: built.distance,
      });
      this.editRouteVertexHandles = this.sampleRouteVerticesForDisplay(
        coords.map((c) => L.latLng(c[1], c[0]))
      );
    },
    addRedrawRouteVertex(lat, lng) {
      const la = Number(lat);
      const ln = Number(lng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
      if (!Array.isArray(this._editRouteLngLats)) this._editRouteLngLats = [];
      this._editRouteLngLats.push([ln, la]);
      this.syncRedrawRouteLocalGeometry();
    },
    undoLastRedrawRouteVertex() {
      if (!Array.isArray(this._editRouteLngLats) || !this._editRouteLngLats.length) return;
      this._editRouteLngLats.pop();
      this.syncRedrawRouteLocalGeometry();
    },
    setupEditRoute() {
      if (!this.map) return;
      this.teardownEditRouteClick();
      this.mapClickHandler = (e) => {
        if (this.editMode !== "editRoute" || this.activeEditRouteId == null) return;
        if (this._routeVertexDragging || this.editRecalculating) return;
        if (Date.now() - (this.lastPolylineClickMs || 0) < 250) return;
        if (!e || !e.latlng) return;
        if (this.editRouteRedrawing) {
          this.addRedrawRouteVertex(e.latlng.lat, e.latlng.lng);
          return;
        }
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        const marker = L.circleMarker([lat, lng], {
          radius: 7,
          color: "#ff9800",
          weight: 2,
          fillColor: "#ffb74d",
          fillOpacity: 1,
        });
        marker.addTo(this.map);
        this.editWaypoints.push({ lat, lng, marker });
      };
      this.map.on("click", this.mapClickHandler);
    },
    teardownEditRouteClick() {
      if (this.map && this.mapClickHandler) {
        this.map.off("click", this.mapClickHandler);
        this.mapClickHandler = null;
      }
    },
    teardownEditRoute() {
      this.discardUnsavedRouteEdit();
      this.resetEditWaypoints();
      this.appliedEditVias = [];
      this.activeEditRouteId = null;
      this.clearEditRouteVertexMarkers();
      this._editRouteBaseline = null;
      this.editRouteGeometryDirty = false;
      this.editRouteRedrawing = false;
      this.teardownEditRouteClick();
    },
    resetEditWaypoints() {
      (this.editWaypoints || []).forEach((v) => {
        if (v && v.marker && this.map) this.map.removeLayer(v.marker);
      });
      this.editWaypoints = [];
    },
    undoLastVia() {
      const last = this.editWaypoints.pop();
      if (last && last.marker && this.map) this.map.removeLayer(last.marker);
    },
    buildPlanStopsFromCercas(rota) {
      const sentido = rota.id_sentido != null ? Number(rota.id_sentido) : Number(this.sentidoSelecionado);
      const cercas = (this.rawCercas || [])
        .filter((c) => {
          if (c.cod_tipo_cerca === "POI") return false;
          return this.sentidoSobrepoe(c.id_sentido, sentido);
        })
        .slice()
        .sort((a, b) => Number(a.ordem_parada || 0) - Number(b.ordem_parada || 0));

      const coords = [];
      cercas.forEach((c) => {
        const la = parseFloat(c.lat_centro);
        const ln = parseFloat(c.lng_centro);
        if (!Number.isNaN(la) && !Number.isNaN(ln)) {
          coords.push([ln, la]);
          return;
        }
        const gj = this.parseGeojson(c.geojson);
        const pin = this.extractFirstLatLng(gj);
        if (pin) coords.push([pin[1], pin[0]]);
      });

      return coords;
    },
    countCercasDaRota(rota) {
      if (!rota) return 0;
      const sentido = rota.id_sentido != null ? Number(rota.id_sentido) : Number(this.sentidoSelecionado);
      return (this.rawCercas || []).filter((c) => {
        if (c.cod_tipo_cerca === "POI") return false;
        return this.sentidoSobrepoe(c.id_sentido, sentido);
      }).length;
    },
    async applyEditRoute() {
      // No preview não há rawRotas/rawCercas: caminho próprio, sem paradas.
      if (this.isEntityPreview) return this.applyPreviewRouteEdit();
      if (this.editRecalculating || this.activeEditRouteId == null) return;
      if (!this.editWaypoints.length && !this.editRouteGeometryDirty) return;

      // Só vértices arrastados: persiste o rascunho local, sem VROOM.
      if (!this.editWaypoints.length && this.editRouteGeometryDirty) {
        return this.persistDraftRouteVertexEdit(this.activeEditRouteId);
      }

      const rota = this.rawRotas.find((r) => Number(r.id_rota) === Number(this.activeEditRouteId));
      if (!rota) return;

      if (this.countCercasDaRota(rota) < 1) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.editRouteNoFences") },
        });
        return;
      }

      const gj = this.parseGeojson(rota.geojson);
      const lineCoords = lngLatFromGeojson(gj);
      const latlngs = lineCoords.map((c) => L.latLng(c[1], c[0]));
      const stopCoords = this.buildPlanStopsFromCercas(rota);
      if (stopCoords.length < 2) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.editRouteNoStops") },
        });
        return;
      }

      const planStops = stopCoords.slice(1, -1);
      const startCoord = stopCoords[0];
      const endCoord = stopCoords[stopCoords.length - 1];

      const viasOrdenadas = sortViasForPlan({
        latlngs,
        stopsLngLat: planStops,
        hasStart: !!startCoord,
        hasEnd: !!endCoord,
        startCoord,
        endCoord,
        editWaypoints: this.editWaypoints,
      });

      const payload = buildVersoPlanPayload({
        stopCoordsLngLat: planStops.length ? planStops : stopCoords,
        viasOrdenadas,
        startCoord,
        endCoord,
        serviceTime: 0,
      });

      this.editRecalculating = true;
      try {
        const planRes = await apiController.request(apiController.endpoints.versoPlan, {
          data: payload,
          silent: true,
        });
        const planned = planRes.data;
        if (
          !planned ||
          planned.code !== 0 ||
          !Array.isArray(planned.routes) ||
          !planned.routes.length
        ) {
          const planStatus = planRes.status;
          const planCodigo = planned && planned.codigo;
          if (planStatus === 429 || planCodigo === "RATE_LIMIT_VROOM") {
            throw new Error(this.$t("crudLinhas.map.vroomRateLimit"));
          }
          throw new Error(
            (planned && (planned.error || planned.message)) ||
              this.$t("crudLinhas.map.editRouteRecalcError")
          );
        }
        const newRoute = planned.routes[0];
        const decoded = decodePlanGeometry(newRoute.geometry);
        if (!decoded) throw new Error(this.$t("crudLinhas.map.editRouteRecalcError"));

        const distancia = Number(newRoute.distance) || rota.distancia;
        const anterior = snapshotTracadoRota(rota);
        const updated = await updateMainRouteGeometry(rota, {
          geojson: decoded.geojson,
          shape: decoded.shape,
          distance: distancia,
        });
        if (!updated || !updated.ok) {
          throw new Error(
            (updated && updated.message) || this.$t("crudLinhas.map.editRouteRecalcError")
          );
        }

        const geojsonStr =
          typeof decoded.geojson === "string"
            ? decoded.geojson
            : JSON.stringify(decoded.geojson);
        rota.geojson = geojsonStr;
        rota.shape = decoded.shape;
        rota.distancia = distancia;
        rota._rev = (rota._rev || 0) + 1;
        this.resetEditWaypoints();
        this.editRouteGeometryDirty = false;
        this.markEditRouteBaselineCommitted(rota);
        this.aplicarFiltroSentido();
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(rota.id_rota));
        await this.persistHistoricoAlteracaoCrud(rota, anterior);
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.routeUpdated"),
        });
        this.$emit("rotas-changed");
      } catch (e) {
        const errStatus = e && e.response && e.response.status;
        const errCodigo = e && e.response && e.response.data && e.response.data.codigo;
        if (errStatus === 429 || errCodigo === "RATE_LIMIT_VROOM") {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.vroomRateLimit") },
          });
        } else {
          EventBus.$emit("show-error", { error: e });
        }
      } finally {
        this.editRecalculating = false;
      }
    },
    async persistDraftRouteVertexEdit(rotaId) {
      if (this.editRecalculating || rotaId == null || !this.editRouteGeometryDirty) return;
      const built = this.buildGeometryFromEditRouteLngLats(this._editRouteLngLats);
      if (!built) {
        EventBus.$emit("show-error", {
          error: {
            message: this.editRouteRedrawing
              ? this.$t("crudLinhas.map.removeRouteTraceEmpty")
              : this.$t("crudLinhas.map.noGeometry"),
          },
        });
        return;
      }
      const rota =
        (this.rawRotas || []).find((r) => Number(r.id_rota) === Number(rotaId)) ||
        this.resolveRotaById(rotaId) ||
        this.selectedRotaDetail ||
        this.entityPreviewItem;
      if (!rota || rota.id_rota == null) return;
      // patchLocalRouteGeometry sobrescreve o objeto local: guarda o traçado antes
      const anterior = snapshotTracadoRota(rota);

      this.editRecalculating = true;
      try {
        const updated = await updateMainRouteGeometry(rota, {
          geojson: built.geojson,
          shape: built.shape,
          distance: built.distance,
        });
        if (!updated || !updated.ok) {
          throw new Error(
            (updated && updated.message) || this.$t("crudLinhas.map.editRouteRecalcError")
          );
        }
        const next = {
          ...rota,
          geojson: built.geojsonStr,
          shape: built.shapeStr,
          distancia: built.distance,
          _rev: (rota._rev || 0) + 1,
        };
        this.patchLocalRouteGeometry(rotaId, {
          geojson: built.geojsonStr,
          shape: built.shapeStr,
          distancia: built.distance,
        });
        this.editRouteGeometryDirty = false;
        this.markEditRouteBaselineCommitted(next);
        if (this.isEntityPreview) {
          this.selectedRotaDetail = next;
          this.rebuildPreviewRouteLayer(next);
          this.$emit("preview-changed", { type: "route", item: next });
        } else {
          await this.persistHistoricoAlteracaoCrud(next, anterior);
          this.$emit("rotas-changed");
        }
        this.$nextTick(() => this.showEditRouteVertexMarkersForId(rotaId));
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.routeUpdated"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.editRecalculating = false;
      }
    },
    routeByFencesErrorMessage(result) {
      const reason = result && result.reason;
      const keyByReason = {
        noLine: "crudLinhas.map.routeByFencesNoLine",
        noSentido: "crudLinhas.map.selectDirectionFirst",
        noRoute: "crudLinhas.map.selectRouteOnMapFirst",
        noFences: "crudLinhas.map.editRouteNoFences",
        insufficientStops: "crudLinhas.map.routeByFencesInsufficientStops",
        planFailed: "crudLinhas.map.routeByFencesPlanError",
        persistFailed: "crudLinhas.map.routeByFencesPersistError",
        updateFailed: "crudLinhas.map.routeByFencesUpdateError",
      };
      const key = keyByReason[reason] || "crudLinhas.map.routeByFencesPlanError";
      const base = this.$t(key);
      if (result && result.message) return `${base} (${result.message})`;
      return base;
    },
    limparPreviewRoteirizacao() {
      this.dialogRoteirizarPorCercas = false;
      this.previewRoteirizacao = null;
      this.mostrarRotaAtualPreview = true;
      this.mostrarRotaNovaPreview = true;
      this.custoParamsRoteirizacao = null;
      this.carregandoCustoRoteirizacao = false;
    },
    /** Pino numerado com a posição da cerca na sequência sugerida. */
    iconPinOrdem(pin) {
      const fill = pin && pin.color ? pin.color : PREVIEW_ROTEIRIZACAO_COLOR;
      return L.divIcon({
        className: "linha-map-cerca-pin",
        html:
          `<div style="width:22px;height:22px;border-radius:50%;background:${fill};` +
          `border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);color:#fff;` +
          `font-size:11px;font-weight:700;line-height:18px;text-align:center">${pin.ordem}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
    },
    /** Ajusta o mapa do diálogo às duas rotas assim que ele é montado. */
    onPreviewMapReady() {
      this.$nextTick(() => this.ajustarPreviewMap());
      // A transição do diálogo fullscreen ainda pode estar rodando: repete com o tamanho final.
      setTimeout(() => this.ajustarPreviewMap(), 300);
      setTimeout(() => this.ajustarPreviewMap(), 700);
      this.trazerRotaAtualParaFrente();
    },
    /**
     * A rota atual (tracejada) precisa ficar acima da sugerida (sólida) para que os
     * trechos coincidentes mostrem as duas. Ligar/desligar um switch readiciona a
     * camada ao mapa, então a ordem é reforçada a cada mudança.
     */
    trazerRotaAtualParaFrente() {
      this.$nextTick(() => {
        const layer = this.$refs.previewRotaAtualRef && this.$refs.previewRotaAtualRef.mapObject;
        if (layer && typeof layer.bringToFront === "function") layer.bringToFront();
      });
    },
    ajustarPreviewMap() {
      const map = this.$refs.previewMapRef && this.$refs.previewMapRef.mapObject;
      if (!map) return;
      map.invalidateSize({ animate: false });
      const points = [];
      if (this.rotaAtualPreviewLayer) {
        this.collectGeojsonLatLngs(this.rotaAtualPreviewLayer.geojson, points);
      }
      if (this.roteirizacaoPreviewLayer) {
        this.collectGeojsonLatLngs(this.roteirizacaoPreviewLayer.geojson, points);
      }
      if (!points.length) return;
      if (points.length === 1) {
        this.previewCenter = points[0];
        map.setView(points[0], 15, { animate: false });
        return;
      }
      map.fitBounds(L.latLngBounds(points), {
        padding: [30, 30],
        animate: false,
        maxZoom: 16,
      });
    },
    descricaoTipoCerca(idTipo) {
      if (idTipo == null) return "—";
      const tipo = (this.tiposCerca || []).find(
        (t) => Number(t.id_tipo_cerca) === Number(idTipo)
      );
      return (tipo && (tipo.descricao_tipo_cerca || tipo.cod_tipo_cerca)) || String(idTipo);
    },
    formatDistanciaKm(metros) {
      if (metros == null || metros === "") return "—";
      const n = Number(metros);
      if (!Number.isFinite(n)) return "—";
      const locale = (this.$i18n && this.$i18n.locale) || "pt-BR";
      return `${(n / 1000).toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} km`;
    },
    formatDiferencaKm(metros) {
      const n = Number(metros);
      if (!Number.isFinite(n)) return "—";
      const sinal = n > 0 ? "+" : "";
      return `${sinal}${this.formatDistanciaKm(n)}`;
    },
    formatMoeda(valor) {
      if (valor == null || valor === "") return "—";
      const n = Number(valor);
      if (!Number.isFinite(n)) return "—";
      const isEs = this.$i18n && String(this.$i18n.locale).trim() === "es-MX";
      return n.toLocaleString(isEs ? "es-MX" : "pt-BR", {
        style: "currency",
        currency: isEs ? "MXN" : "BRL",
      });
    },
    formatDiferencaMoeda(valor) {
      const n = Number(valor);
      if (!Number.isFinite(n)) return "—";
      const sinal = n > 0 ? "+" : "";
      return `${sinal}${this.formatMoeda(n)}`;
    },
    formatDuracao(segundos) {
      const n = Number(segundos);
      if (!Number.isFinite(n)) return "—";
      const total = Math.max(0, Math.round(n / 60));
      const horas = Math.floor(total / 60);
      const minutos = total % 60;
      return horas > 0 ? `${horas}h ${String(minutos).padStart(2, "0")}min` : `${minutos}min`;
    },
    /** Unidades associadas à linha; a primeira serve de escopo para o contrato de subsídio. */
    unidadesDaLinha() {
      const linha = this.linha || {};
      const ids = new Set();
      const add = (raw) => {
        if (raw == null || raw === "") return;
        const n = Number(typeof raw === "object" ? raw.id_unidade : raw);
        if (Number.isFinite(n) && n > 0) ids.add(n);
      };
      add(linha.id_unidade);
      if (Array.isArray(linha.unidades)) linha.unidades.forEach(add);
      else if (linha.unidades != null) String(linha.unidades).split(",").forEach((x) => add(x.trim()));
      if (linha.ids_unidades != null) {
        String(linha.ids_unidades).split(",").forEach((x) => add(x.trim()));
      }
      return Array.from(ids);
    },
    /**
     * Capacidade mínima que o veículo precisa ter para atender a linha: a maior entre a
     * lotação prevista (passageiros) e a quantidade de cercas da prévia, o que for maior.
     */
    capacidadeMinimaLinha() {
      const linha = this.linha || {};
      const preview = this.previewRoteirizacao;
      const passageiros = Number(linha.lotacao_prevista);
      const cercas = preview && Array.isArray(preview.orderedCercas)
        ? preview.orderedCercas.length
        : 0;
      const alvo = Math.max(
        Number.isFinite(passageiros) && passageiros > 0 ? passageiros : 0,
        cercas
      );
      return alvo > 0 ? alvo : null;
    },
    /**
     * Mantém só os tipos de veículo que têm ao menos uma das tarifas preenchida.
     * Um tipo com tudo zerado não serve de base de custo: comparar R$ 0,00 com R$ 0,00
     * não diz nada, então ele é descartado para que a busca caia na fonte seguinte.
     */
    tiposVeiculoComTarifa(rows, campos) {
      return (Array.isArray(rows) ? rows : []).filter((row) => {
        if (!row) return false;
        return campos.some((campo) => {
          const n = Number(row[campo]);
          return Number.isFinite(n) && n > 0;
        });
      });
    },
    /**
     * Escolhe o tipo de veículo cuja lotação melhor atende a linha: o menor que comporte
     * a capacidade mínima; se nenhum comportar (ou não houver alvo), o de maior capacidade.
     * Espelha a ideia da Roteirização, onde o solver escolhe pela capacidade.
     */
    escolheTipoVeiculoPorLotacao(rows, alvo, campoLotacao) {
      const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
      if (!list.length) return null;
      const cap = (row) => {
        const n = Number(row[campoLotacao]);
        return Number.isFinite(n) && n > 0 ? n : 0;
      };
      const ordenados = [...list].sort((a, b) => cap(a) - cap(b));
      if (Number.isFinite(alvo) && alvo > 0) {
        const cabe = ordenados.find((row) => cap(row) >= alvo);
        if (cabe) return cabe;
      }
      return ordenados[ordenados.length - 1];
    },
    /**
     * Tarifas da estimativa de custo: contrato de subsídio do cliente da linha quando
     * houver; senão as tarifas de simulação do cadastro de tipos de veículo — mesma
     * precedência (contrato → padrão) que a tela de Roteirização usa.
     */
    async carregarParametrosCustoLinha() {
      this.custoParamsRoteirizacao = null;
      this.carregandoCustoRoteirizacao = true;
      try {
        const linha = this.linha || {};
        const idEmpresa = Number(linha.id_empresa);
        const idCliente = Number(linha.id_cliente);
        const idUnidade = this.unidadesDaLinha()[0];
        const alvo = this.capacidadeMinimaLinha();
        const descricao = (row) =>
          translateDbField(row.translation_key, row.descricao_tipo_veiculo) ||
          (row.descricao_tipo_veiculo != null ? String(row.descricao_tipo_veiculo) : null);

        if (Number.isFinite(idEmpresa) && Number.isFinite(idCliente) && Number.isFinite(idUnidade)) {
          try {
            const res = await apiController.request(
              apiController.endpoints.subsidioGetallTiposveiculosfromcontratossubsidio,
              {
                query: { id_empresa: idEmpresa, id_cliente: idCliente, id_unidade: idUnidade },
                silent: true,
              }
            );
            const rows = this.tiposVeiculoComTarifa(res.data, [
              "valor_fixo",
              "valor_variavel",
              "valor_labor",
            ]);
            const row = this.escolheTipoVeiculoPorLotacao(rows, alvo, "lotacao");
            if (row) {
              this.custoParamsRoteirizacao = {
                fonte: "contrato",
                tipo: descricao(row),
                capacidade: Number(row.lotacao) || null,
                capacidadeMinima: alvo,
                fixedCost: Number(row.valor_fixo) || 0,
                costPerKm: Number(row.valor_variavel) || 0,
                laborCost: Number(row.valor_labor) || 0,
              };
              return;
            }
            // Contrato sem nenhuma tarifa preenchida: cai nos padrões de simulação,
            // senão a tabela mostraria a comparação inteira zerada.
          } catch (e) {
            // Sem contrato acessível: segue para as tarifas padrão.
          }
        }

        const res = await apiController.request(apiController.endpoints.getallTiposveiculos, {
          silent: true,
        });
        const rows = this.tiposVeiculoComTarifa(res.data, [
          "tarifa_fixa_simulacao",
          "tarifa_variavel_simulacao",
          "tarifa_mao_de_obra_simulacao",
        ]);
        const row = this.escolheTipoVeiculoPorLotacao(rows, alvo, "lotacao_padrao");
        if (!row) return;
        this.custoParamsRoteirizacao = {
          fonte: "padrao",
          tipo: descricao(row),
          capacidade: Number(row.lotacao_padrao) || null,
          capacidadeMinima: alvo,
          fixedCost: Number(row.tarifa_fixa_simulacao) || 0,
          costPerKm: Number(row.tarifa_variavel_simulacao) || 0,
          laborCost: Number(row.tarifa_mao_de_obra_simulacao) || 0,
        };
      } catch (e) {
        this.custoParamsRoteirizacao = null;
      } finally {
        this.carregandoCustoRoteirizacao = false;
      }
    },
    lerFixarPontoInicial() {
      try {
        const raw = window.localStorage.getItem(PIN_START_STORAGE_KEY);
        // Sem preferência salva mantém o comportamento histórico: ponto inicial fixo.
        return raw == null ? true : raw === "1";
      } catch (e) {
        return true;
      }
    },
    persistirFixarPontoInicial(val) {
      try {
        window.localStorage.setItem(PIN_START_STORAGE_KEY, val ? "1" : "0");
      } catch (e) {
        // Navegador sem storage disponível: a escolha vale só para esta sessão.
      }
    },
    /** Refaz a prévia já aberta com a regra atual do ponto inicial. */
    async recalcularPreviewRoteirizacao() {
      const preview = this.previewRoteirizacao;
      const idLinha = preview && preview.idLinha;
      const sentido = preview && preview.sentido;
      if (!idLinha || sentido == null) return;
      this.gerandoPreviewRoteirizacao = true;
      try {
        const novo = await previewRoteirizacaoPorCercas({
          idLinha,
          sentido: Number(sentido),
          idRota: this.idRotaRoteirizacao,
          fixarPontoInicial: this.fixarPontoInicialRoteirizacao,
        });
        if (!novo || !novo.ok) {
          EventBus.$emit("show-error", {
            error: { message: this.routeByFencesErrorMessage(novo) },
          });
          return;
        }
        this.previewRoteirizacao = novo;
        this.previewRoteirizacaoRev += 1;
        this.carregarParametrosCustoLinha();
        this.$nextTick(() => this.ajustarPreviewMap());
      } finally {
        this.gerandoPreviewRoteirizacao = false;
      }
    },
    /** Confirma o sentido escolhido e dispara a prévia. */
    confirmarSentidoRoteirizacao() {
      const sentido = this.sentidoRoteirizacao;
      if (sentido == null || sentido === "") return;
      this.dialogSentidoRoteirizacao = false;
      if (Number(sentido) !== Number(this.sentidoSelecionado)) {
        this.$emit("update:sentidoSelecionado", sentido);
      }
      // Trocar o sentido no diálogo pode deixar a rota escolhida fora dele; nesse caso a
      // prévia usa a rota principal do sentido em vez de falhar por rota inexistente.
      const rota = (this.rawRotas || []).find(
        (r) => Number(r.id_rota) === Number(this.idRotaRoteirizacao)
      );
      if (rota && Number(rota.id_sentido) !== Number(sentido)) {
        this.idRotaRoteirizacao = null;
      }
      this.executarPreviewRoteirizacao(Number(sentido));
    },
    /** Calcula a roteirização por cercas sem gravar nada e abre o diálogo de comparação. */
    async executarPreviewRoteirizacao(sentido) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || sentido == null || Number.isNaN(Number(sentido))) return;

      this.gerandoPreviewRoteirizacao = true;
      try {
        const preview = await previewRoteirizacaoPorCercas({
          idLinha,
          sentido: Number(sentido),
          idRota: this.idRotaRoteirizacao,
          fixarPontoInicial: this.fixarPontoInicialRoteirizacao,
        });
        if (!preview || !preview.ok) {
          EventBus.$emit("show-error", {
            error: { message: this.routeByFencesErrorMessage(preview) },
          });
          return;
        }
        this.previewRoteirizacao = preview;
        this.previewRoteirizacaoRev += 1;
        this.mostrarRotaAtualPreview = true;
        this.mostrarRotaNovaPreview = true;
        this.dialogRoteirizarPorCercas = true;
        this.carregarParametrosCustoLinha();
        if (preview.warning) {
          EventBus.$emit("show-alert", { mensagem: preview.warning, tipo: "warning" });
        }
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.gerandoPreviewRoteirizacao = false;
      }
    },
    descartarPreviewRoteirizacao() {
      this.limparPreviewRoteirizacao();
      EventBus.$emit("show-alert", {
        mensagem: this.$t("crudLinhas.map.routeByFencesPreviewDiscarded"),
        tipo: "info",
      });
    },
    /** Grava a prévia exibida no diálogo de comparação. */
    async salvarPreviewRoteirizacao() {
      const preview = this.previewRoteirizacao;
      if (!preview) return;
      this.salvandoPreviewRoteirizacao = true;
      try {
        const result = await aplicarRoteirizacaoPorCercas(preview);
        if (!result || !result.ok) {
          EventBus.$emit("show-error", {
            error: { message: this.routeByFencesErrorMessage(result) },
          });
          return;
        }
        const plan = result.planResult || {};
        const rota = result.mainRota || {};
        // mainRota ainda tem o traçado de antes da gravação (a geometria nova vem do plan)
        await this.persistHistoricoAlteracaoCrud({
          ...rota,
          id_sentido: Number(preview.sentido),
          geojson: plan.geojson != null ? plan.geojson : rota.geojson,
          shape: plan.shape != null ? plan.shape : rota.shape,
          distancia: plan.distance != null ? plan.distance : rota.distancia,
        }, snapshotTracadoRota(rota));
        this.limparPreviewRoteirizacao();
        await this.carregarDados();
        if (result.orderChanged || result.typesChanged) this.$emit("cercas-changed");
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: result.orderChanged || result.typesChanged
            ? this.$t("crudLinhas.map.routeByFencesSuccessOrderChanged")
            : this.$t("crudLinhas.map.routeByFencesSuccessRouteOnly"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.salvandoPreviewRoteirizacao = false;
      }
    },
    /**
     * Abre o diálogo de confirmação da roteirização por cercas: o usuário revisa o
     * sentido e as cercas que entram no cálculo antes de gerar a prévia comparativa.
     * Nada é gravado aqui — a gravação só acontece ao salvar a prévia.
     */
    roteirizarPorCercas() {
      const idLinha = this.linha && this.linha.id_linha;
      const sentido = this.sentidoSelecionado;
      if (!idLinha) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.routeByFencesNoLine") },
        });
        return;
      }
      if (sentido == null || sentido === "") {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.selectDirectionFirst") },
        });
        return;
      }
      if (!(this.rawRotas || []).length) {
        this.dialogSemRota = true;
        return;
      }
      const idRota = this.resolveSelectedRouteIdForRoteirizacao();
      if (idRota == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.selectRouteOnMapFirst") },
        });
        return;
      }
      const sentidoRoteirizacao = this.resolveSentidoForRoteirizacao(idRota, sentido);
      if (sentidoRoteirizacao == null || Number.isNaN(sentidoRoteirizacao)) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.selectDirectionFirst") },
        });
        return;
      }
      // Mantém seleção/filtro alinhados à rota que será roteirizada.
      this.selectedMapRouteId = Number(idRota);
      if (Number(this.sentidoSelecionado) !== Number(sentidoRoteirizacao)) {
        this.$emit("update:sentidoSelecionado", sentidoRoteirizacao);
      }
      // O sentido define de qual terminal a rota parte (na volta os papéis se invertem):
      // ele vem preenchido com o da rota escolhida, mas continua editável no diálogo.
      this.idRotaRoteirizacao = Number(idRota);
      this.sentidoRoteirizacao = Number(sentidoRoteirizacao);
      this.dialogSentidoRoteirizacao = true;
    },
    /**
     * Grava EDITADA_CRUD com custo recalculado, comparada com o baseline da rota
     * (ORIGINAL/CALCULADA). Rota sem baseline ganha um TRACADO_ANTERIOR com o traçado de
     * antes da edição (`anterior`, de snapshotTracadoRota). Falha tolerada.
     */
    async persistHistoricoAlteracaoCrud(rota, anterior = null) {
      try {
        if (!rota || rota.id_rota == null) return;
        let user = null;
        try {
          user = JSON.parse(sessionStorage.getItem("user"));
        } catch (e) {
          user = null;
        }
        const idUsuario = user && user.id_usuario != null ? Number(user.id_usuario) : null;
        if (!Number.isFinite(idUsuario) || idUsuario <= 0) return;

        const destinationIds = (this.cercasLinhaLocal || [])
          .filter((c) => c && [2, 3].includes(Number(c.id_tipo_cerca)))
          .map((c) => c.id_cerca);
        await persistHistoricoRoteirizacaoCrudEvent({
          idSessaoOrigem: this.historicoSessaoOrigemId,
          idSessaoCrud: this.historicoSessaoId,
          idCercaDestinoCandidates: destinationIds,
          idUsuario,
          tipoEvento: TIPO_EVENTO_HISTORICO.EDITADA_ROTA,
          detalhe: {
            origem: "CRUD_LINHAS",
            acao: "EDITAR_ROTA",
            id_linha: this.linha && this.linha.id_linha,
            cod_linha: this.linha && this.linha.cod_linha,
            nome_linha: this.linha && this.linha.nome_linha,
            id_sentido: rota.id_sentido != null ? rota.id_sentido : rota.sentido,
            rota_atual: { ...rota },
          },
        });

        const baseline = await resolveBaselineCrud({
          idRota: rota.id_rota,
          anterior,
          linha: this.linha,
          idUsuario,
        });
        if (!baseline || !baseline.id_grupo) return;

        let idVersao = 3;
        try {
          const grupoRes = await apiController.request(
            apiController.endpoints.getHistoricoalteracoesrotasporgrupo,
            { query: { id_grupo: baseline.id_grupo }, silent: true },
          );
          const versoes = Array.isArray(grupoRes && grupoRes.data) ? grupoRes.data : [];
          const maxV = versoes.reduce(
            (acc, row) => Math.max(acc, Number(row.id_versao) || 0),
            0,
          );
          idVersao = maxV + 1;
        } catch (e) {
          idVersao = 3;
        }

        const payload = buildEditadaCrudPayload({
          baseline,
          rota,
          idUsuario,
          dtEvento: nowSqlDatetime(),
          idVersao,
          idLinha: this.linha && this.linha.id_linha != null ? this.linha.id_linha : null,
        });

        await apiController.request(
          apiController.endpoints.insertHistoricoalteracoesrotas,
          { data: { ...payload, id_usuario: idUsuario }, silent: true },
        );
      } catch (e) {
        // falha tolerada
      }
    },
    async addRotaAssociacao() {
      if (!this.itemRota.id_rota) return false;
      this.itemRota.id_sentido = this.sentidoSelecionado;
      if (this.itemRota.id_sentido == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.selectDirectionFirst") },
        });
        return false;
      }
      if (
        this.rotasLinhaLocal.some(
          (r) =>
            Number(r.id_rota) === Number(this.itemRota.id_rota) &&
            Number(r.id_sentido) === Number(this.itemRota.id_sentido)
        )
      ) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.routeAlreadyAssociated") },
        });
        return false;
      }
      if (this.itemRota.id_tipo_rota == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.validation.required") },
        });
        return false;
      }
      await this.ensureRotaDetail(this.itemRota.id_rota);
      const cat = this.resolveRotaById(this.itemRota.id_rota);
      const sentido = this.sentidos.find(
        (s) => Number(s.id_sentido) === Number(this.itemRota.id_sentido)
      );
      const tipo = (this.tiposRota || []).find(
        (t) => Number(t.id_tipo_rota) === Number(this.itemRota.id_tipo_rota)
      );
      this.rotasLinhaLocal.push({
        id_rota: this.itemRota.id_rota,
        nome_rota: cat ? cat.nome_rota : String(this.itemRota.id_rota),
        cod_rota: cat ? cat.cod_rota : null,
        shape: cat ? cat.shape : null,
        geojson: cat ? cat.geojson : null,
        id_sentido: this.itemRota.id_sentido,
        descricao_sentido: sentido ? sentido.descricao_sentido : "",
        id_tipo_rota: this.itemRota.id_tipo_rota,
        descricao_tipo_rota: tipo ? tipo.descricao_tipo_rota : "",
        ativa: 1,
      });
      this.itemRota.id_rota = null;
      this.itemRota.id_tipo_rota = null;
      this.selectedRotaDetail = null;
      this.$nextTick(() => this.syncRotaSelect(null));
      return true;
    },
    async removeRotaAssociacao(r) {
      this.rotasLinhaLocal = this.rotasLinhaLocal.filter(
        (x) =>
          !(
            Number(x.id_rota) === Number(r.id_rota) &&
            Number(x.id_sentido) === Number(r.id_sentido)
          )
      );
      await this.persistRotasLinha();
    },
    async saveRotasLinha() {
      if (this.itemRota.id_rota) {
        if (!(await this.addRotaAssociacao())) return;
      }
      await this.persistRotasLinha();
    },
    async persistRotasLinha() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.savingRotas = true;
      try {
        await apiController.request(apiController.endpoints.deleteRotaslinhabulk, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (this.rotasLinhaLocal.length) {
          const rows = this.rotasLinhaLocal.map((r) => [
            idLinha,
            r.id_rota,
            r.id_tipo_rota,
            r.id_sentido,
            1,
          ]);
          await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        await this.carregarDados();
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.routesSaved"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.savingRotas = false;
      }
    },
    buildCercaPayload() {
      if (!this.editingCerca) {
        this.itemCerca.id_sentido = this.sentidoSelecionado;
      }
      const cat = this.resolveCercaById(this.itemCerca.id_cerca);
      const sentido = this.sentidos.find(
        (s) => Number(s.id_sentido) === Number(this.itemCerca.id_sentido)
      );
      const tipo = this.tiposCerca.find(
        (t) => Number(t.id_tipo_cerca) === Number(this.itemCerca.id_tipo_cerca)
      );
      return {
        id_cerca: this.itemCerca.id_cerca,
        nome_cerca: cat ? cat.nome_cerca : String(this.itemCerca.id_cerca),
        cod_cerca: cat ? cat.cod_cerca : null,
        endereco: cat ? cat.endereco : null,
        lat_centro: cat ? cat.lat_centro : null,
        lng_centro: cat ? cat.lng_centro : null,
        geojson: cat ? cat.geojson : null,
        shape: cat ? cat.shape : null,
        is_circular: cat ? cat.is_circular : null,
        raio_mts: cat ? cat.raio_mts : null,
        id_sentido: this.itemCerca.id_sentido,
        descricao_sentido: sentido ? sentido.descricao_sentido : "",
        id_tipo_cerca: this.itemCerca.id_tipo_cerca,
        descricao_tipo_cerca: tipo ? tipo.descricao_tipo_cerca : "",
        ordem_parada: this.itemCerca.ordem_parada,
        tempo_chegada_minutos: this.itemCerca.tempo_chegada_minutos,
        show_sinotico: this.itemCerca.show_sinotico ? 1 : 0,
        show_sinotico_extenso: this.itemCerca.show_sinotico
          ? this.$t("selectOptions.yes")
          : this.$t("selectOptions.no"),
      };
    },
    commitCercaPayload(payload) {
      if (this.editingCerca && this.editingCercaId != null) {
        this.cercasLinhaLocal = this.cercasLinhaLocal.filter(
          (c) =>
            !(
              Number(c.id_cerca) === Number(this.editingCercaId) &&
              Number(c.id_sentido) === Number(this.editingCercaSentido)
            )
        );
      }
      this.cercasLinhaLocal.push(payload);
      this.itemCerca.id_cerca = null;
      this.selectedCercaDetail = null;
      this.editingCerca = false;
      this.editingCercaId = null;
      this.editingCercaSentido = null;
      this.pendingCercaPayload = null;
      this.$nextTick(() => this.syncCercaSelect(null));
    },
    cercasSentidosConflitam(sentidoA, sentidoB) {
      const a = Number(sentidoA);
      const b = Number(sentidoB);
      return a === b || a === -1 || b === -1;
    },
    /**
     * Inclui a cerca do formulário na lista local.
     * @returns {Promise<'ok'|'blocked'|'pending'>} pending = aguardando dialog de geometria
     */
    async addCercaAssociacao() {
      if (!this.itemCerca.id_cerca) return "ok";
      if (!this.editingCerca) {
        this.itemCerca.id_sentido = this.sentidoSelecionado;
      }

      if (!this.rotasNoSentido.length) {
        this.dialogSemRota = true;
        return "blocked";
      }

      if (this.itemCerca.id_tipo_cerca == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.validation.fenceTypeRequired") },
        });
        return "blocked";
      }
      if (
        this.itemCerca.ordem_parada == null ||
        this.itemCerca.ordem_parada === "" ||
        Number.isNaN(Number(this.itemCerca.ordem_parada))
      ) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.validation.required") },
        });
        return "blocked";
      }

      const excludeEditing = this.editingCerca
        ? { id_cerca: this.editingCercaId, id_sentido: this.editingCercaSentido }
        : null;
      if (wouldBreakTerminusOrder(this.cercasLinhaLocal, this.itemCerca, excludeEditing, this.terminusPinOptions)) {
        this.dialogTerminusOrder = true;
        return "blocked";
      }

      const already = this.cercasLinhaLocal.some(
        (c) =>
          Number(c.id_cerca) === Number(this.itemCerca.id_cerca) &&
          this.cercasSentidosConflitam(c.id_sentido, this.itemCerca.id_sentido) &&
          !(
            this.editingCerca &&
            Number(c.id_cerca) === Number(this.editingCercaId) &&
            Number(c.id_sentido) === Number(this.editingCercaSentido)
          )
      );
      if (already) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.fenceAlreadyAssociated") },
        });
        return "blocked";
      }

      await this.ensureCercaDetail(this.itemCerca.id_cerca);
      const payload = this.buildCercaPayload();
      const cerca = this.resolveCercaById(this.itemCerca.id_cerca);
      const routeGjs = this.rotasNoSentido
        .map((r) => this.parseGeojson(r.geojson))
        .filter(Boolean);
      const check = cercaIntersectsRotas(cerca, routeGjs);
      if (!check.ok) {
        this.pendingCercaPayload = payload;
        this.dialogCercaForaRotaMensagem =
          check.reason === "no_geometry"
            ? this.$t("crudLinhas.map.dialogFenceNoGeometryBody")
            : this.$t("crudLinhas.map.dialogFenceOutsideBody");
        this.dialogCercaForaRota = true;
        return "pending";
      }
      this.commitCercaPayload(payload);
      return "ok";
    },
    cancelCercaForaRota() {
      this.dialogCercaForaRota = false;
      this.pendingCercaPayload = null;
      this.persistCercaAfterDialog = false;
      this.pendingHistoricoCercaDetalhe = null;
      this.pendingCercaRedesenhar = false;
    },
    async confirmCercaForaRota() {
      if (this.pendingCercaPayload) {
        this.commitCercaPayload(this.pendingCercaPayload);
      }
      this.dialogCercaForaRota = false;
      if (this.persistCercaAfterDialog) {
        this.persistCercaAfterDialog = false;
        const detalhe = this.pendingHistoricoCercaDetalhe;
        const redesenhar = this.pendingCercaRedesenhar;
        this.pendingHistoricoCercaDetalhe = null;
        this.pendingCercaRedesenhar = false;
        await this.persistCercasLinha(detalhe, { redesenhar });
      }
    },
    cercaReorderIndex(item) {
      if (!item) return -1;
      return (this.cercasDrawerFiltradas || []).findIndex(
        (c) =>
          Number(c.id_cerca) === Number(item.id_cerca) &&
          Number(c.id_sentido) === Number(item.id_sentido)
      );
    },
    isTerminusCercaForReorder(item) {
      if (!item) return false;
      const tipo = Number(item.id_tipo_cerca);
      return tipo === FENCE_TYPE.START || tipo === FENCE_TYPE.END;
    },
    canMoveCercaUpInReorder(item) {
      const idx = this.cercaReorderIndex(item);
      if (idx <= 0) return false;
      if (this.isTerminusCercaForReorder(item)) return false;
      // Impede trocar com o ponto inicial (2ª cerca não pode subir).
      if (idx === 1) return false;
      return true;
    },
    canMoveCercaDownInReorder(item) {
      const filtered = this.cercasDrawerFiltradas || [];
      const idx = this.cercaReorderIndex(item);
      if (idx < 0 || idx >= filtered.length - 1) return false;
      if (this.isTerminusCercaForReorder(item)) return false;
      // Impede trocar com o ponto final (penúltima cerca não pode descer).
      if (idx === filtered.length - 2) return false;
      return true;
    },
    async ordenarCerca(item, direction) {
      if (!this.reorderFencesMode || !this.canEdit || !item) return;
      if (direction === "up" && !this.canMoveCercaUpInReorder(item)) return;
      if (direction === "down" && !this.canMoveCercaDownInReorder(item)) return;
      const filtered = (this.cercasDrawerFiltradas || []).slice();
      const currentIndex = filtered.findIndex(
        (c) =>
          Number(c.id_cerca) === Number(item.id_cerca) &&
          Number(c.id_sentido) === Number(item.id_sentido)
      );
      if (currentIndex === -1) return;
      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= filtered.length) return;
      [filtered[currentIndex], filtered[targetIndex]] = [
        filtered[targetIndex],
        filtered[currentIndex],
      ];
      await this.applyCercaReorderOrder(filtered);
    },
    async applyCercaReorderOrder(filtered) {
      let sentidoAlterado = null;
      filtered.forEach((cerca, index) => {
        const local = (this.cercasLinhaLocal || []).find(
          (c) =>
            Number(c.id_cerca) === Number(cerca.id_cerca) &&
            Number(c.id_sentido) === Number(cerca.id_sentido)
        );
        if (local) {
          this.$set(local, "ordem_parada", index + 1);
          if (sentidoAlterado == null) sentidoAlterado = local.id_sentido;
        }
      });
      await this.persistCercasLinha();
      if (sentidoAlterado != null) {
        await this.redesenharTracadoMantendoOrdem(Number(sentidoAlterado));
      }
    },
    /**
     * Após reordenar cercas manualmente no drawer, redesenha o traçado via /plan do vroom
     * respeitando a ordem definida pelo usuário (sem reordenar via /solve).
     */
    async redesenharTracadoMantendoOrdem(sentido) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha || sentido == null || Number.isNaN(sentido)) return;
      this.fenceDragRecalculating = true;
      try {
        const mainRota = selectMainRouteForSentido(this.rawRotas, sentido);
        if (!mainRota) return;
        const { cercasBase } = resolveDirectionContext(this.rawCercas, mainRota, sentido);
        const ordered = enrichCercasWithCoords(cercasBase);
        if (!ordered.length) return;
        const hasDisplacement = lineHasDisplacement(this.rawRotas, sentido);
        const planResult = await planOrderedFencesRoute(ordered, {
          serviceTime: 0,
          sentido,
          hasDisplacement,
        });
        if (!planResult || planResult.error || !planResult.geojson) {
          const rateLimited = /429|RATE_LIMIT|rate.?limit/i.test(
            String((planResult && planResult.message) || "")
          );
          throw new Error(
            rateLimited
              ? this.$t("crudLinhas.map.vroomRateLimit")
              : this.$t("crudLinhas.map.stopDragRecalcError")
          );
        }
        const updated = await updateMainRouteGeometry(mainRota, planResult);
        if (!updated || !updated.ok) {
          throw new Error(
            (updated && updated.message) || this.$t("crudLinhas.map.routeByFencesUpdateError")
          );
        }
        await this.carregarDados();
        this.$emit("rotas-changed");
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.fenceDragRecalculating = false;
      }
    },
    editItemCerca(item) {
      this.editingCerca = true;
      this.editingCercaId = item.id_cerca;
      this.editingCercaSentido = item.id_sentido;
      this.$emit("update:sentidoSelecionado", item.id_sentido);
      this.itemCerca = {
        id_cerca: item.id_cerca,
        id_sentido: item.id_sentido,
        id_tipo_cerca: item.id_tipo_cerca,
        ordem_parada: item.ordem_parada,
        tempo_chegada_minutos: item.tempo_chegada_minutos,
        show_sinotico: Number(item.show_sinotico) === 1,
      };
      this.drawerCercasManagePanel = 0;
      this.$nextTick(() => {
        this.syncCercaSelect(item);
        this.scrollFenceEditorIntoView();
        this.rebuildFenceEditHandles();
      });
    },
    /** Sobe todos os ancestrais com scroll até o formulário (cerca/tipo/ordem). */
    scrollFenceEditorIntoView() {
      const apply = () => {
        const form = this.$refs.fenceEditForm;
        const panel = this.$refs.fencesSidePanel;
        const resetTop = (el) => {
          if (!el) return;
          el.scrollTop = 0;
        };
        resetTop(panel);
        let node = form;
        while (node && node !== document.body) {
          const style = window.getComputedStyle(node);
          const oy = style.overflowY;
          if (oy === "auto" || oy === "scroll" || oy === "overlay") {
            resetTop(node);
          }
          node = node.parentElement;
        }
        const scrolling = document.scrollingElement || document.documentElement;
        if (form) {
          const rect = form.getBoundingClientRect();
          if (rect.top < 72) {
            const nextTop = (scrolling.scrollTop || window.pageYOffset || 0) + rect.top - 72;
            window.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
          }
        }
      };
      this.$nextTick(() => {
        apply();
        window.requestAnimationFrame(() => {
          apply();
          window.setTimeout(apply, 80);
        });
      });
    },
    removeCercaAssociacao(c) {
      if (!c) return;
      this.cercaPendenteExclusao = { ...c };
      this.confirmDeleteCercaDialog = true;
    },
    cancelDeleteCerca() {
      this.confirmDeleteCercaDialog = false;
      this.cercaPendenteExclusao = null;
    },
    async confirmDeleteCerca() {
      const c = this.cercaPendenteExclusao;
      this.confirmDeleteCercaDialog = false;
      this.cercaPendenteExclusao = null;
      if (!c) return;
      const cercasAntes = this.cercasLinhaLocal.map((item) => ({ ...item }));
      this.cercasLinhaLocal = this.cercasLinhaLocal.filter(
        (x) =>
          !(
            Number(x.id_cerca) === Number(c.id_cerca) &&
            Number(x.id_sentido) === Number(c.id_sentido) &&
            Number(x.id_tipo_cerca) === Number(c.id_tipo_cerca)
          )
      );
      if (
        this.itemCerca.id_cerca != null &&
        Number(this.itemCerca.id_cerca) === Number(c.id_cerca) &&
        Number(this.itemCerca.id_sentido) === Number(c.id_sentido)
      ) {
        this.itemCerca.id_cerca = null;
        this.selectedCercaDetail = null;
        this.$nextTick(() => this.syncCercaSelect(null));
      }
      if (
        this.editingCercaId != null &&
        Number(this.editingCercaId) === Number(c.id_cerca) &&
        Number(this.editingCercaSentido) === Number(c.id_sentido)
      ) {
        this.editingCerca = false;
        this.editingCercaId = null;
        this.editingCercaSentido = null;
      }
      const gravou = await this.persistCercasLinha({
        origem: "CRUD_LINHAS",
        acao: "EXCLUIR_CERCA",
        cerca_anterior: { ...c },
        cercas_antes: cercasAntes,
      });
      if (!gravou) return;
      // Reroteiriza sem a cerca excluída. persistCercasLinha termina em carregarDados,
      // então rawCercas/rawRotas já refletem a linha sem ela.
      const sentidoCerca = Number(c.id_sentido);
      const sentido =
        Number.isFinite(sentidoCerca) && sentidoCerca !== -1
          ? sentidoCerca
          : Number(this.sentidoSelecionado);
      if (Number.isFinite(sentido)) {
        await this.redesenharTracadoMantendoOrdem(sentido);
      }
    },
    async gerarCercasVolta() {
      if (!this.linha || this.linha.id_linha == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.routeByFencesNoLine") },
        });
        return;
      }
      if (this.savingCercas || this.roteirizandoPorCercas) return;
      this.savingCercas = true;
      this.roteirizandoPorCercas = true;
      try {
        await this.carregarDados();

        const sentidosIda = new Set([1, 3, 4]);
        const sentidosVolta = new Set([2, 5, 6]);
        const mapaSentidos = { 1: 2, 3: 5, 4: 6, [-1]: 2 };
        const sortByOrdem = (a, b) =>
          Number(a.ordem_parada || 0) - Number(b.ordem_parada || 0);
        const isOperacional = (c) => {
          const t = Number(c && c.id_tipo_cerca);
          return (
            t === FENCE_TYPE.START ||
            t === FENCE_TYPE.STOP ||
            t === FENCE_TYPE.END
          );
        };

        const idaRotas = (this.rawRotas || []).filter((r) =>
          sentidosIda.has(Number(r.id_sentido))
        );
        if (!idaRotas.length) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.routeByFencesNoRoute") },
          });
          return;
        }
        const sentidoIda = Number(idaRotas[0].id_sentido) || 1;
        const sentidoVolta = mapaSentidos[sentidoIda] || 2;
        let idaMainRota = selectMainRouteForSentido(this.rawRotas, sentidoIda);
        if (!idaMainRota || idaMainRota.id_rota == null) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.routeByFencesNoRoute") },
          });
          return;
        }
        const detalheIda = await this.ensureRotaDetail(idaMainRota.id_rota, { force: true });
        if (detalheIda) {
          idaMainRota = { ...idaMainRota, ...detalheIda };
        }
        // Preferir empresa da linha; não propagar cliente (evita mismatch no insertRota).
        const idEmpresaLinha =
          this.linha && this.linha.id_empresa != null && this.linha.id_empresa !== ""
            ? this.linha.id_empresa
            : null;
        idaMainRota = {
          ...idaMainRota,
          id_empresa:
            idEmpresaLinha != null
              ? idEmpresaLinha
              : idaMainRota.id_empresa != null && idaMainRota.id_empresa !== ""
                ? idaMainRota.id_empresa
                : null,
          id_cliente: null,
        };
        if (idaMainRota.id_empresa == null || !idaMainRota.cod_rota || !idaMainRota.nome_rota) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.routeByFencesUpdateError") },
          });
          return;
        }

        const todas = this.cercasLinhaLocal || [];
        let cercasIda = todas
          .filter(
            (c) =>
              isOperacional(c) && sentidosIda.has(Number(c.id_sentido))
          )
          .slice()
          .sort(sortByOrdem);
        if (!cercasIda.length) {
          cercasIda = todas
            .filter(
              (c) => isOperacional(c) && Number(c.id_sentido) === -1
            )
            .slice()
            .sort(sortByOrdem);
        }
        if (!cercasIda.length) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.noOutboundFencesToMirror") },
          });
          return;
        }
        if (!cercasIda.some((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.END)) {
          EventBus.$emit("show-error", {
            error: { message: this.$t("crudLinhas.map.routeByFencesInsufficientStops") },
          });
          return;
        }

        const temposIda = cercasIda.map((c) => {
          const tempo = Number(c.tempo_chegada_minutos);
          return Number.isFinite(tempo) ? tempo : 0;
        });
        const tempoMaximo = Math.max(0, ...temposIda);
        const sentidoVoltaLabel = this.descricaoSentido(sentidoVolta) || "";
        // Ida: START → … → END. Volta: inverte (END primeiro, START por último).
        const cercasVolta = cercasIda
          .slice()
          .reverse()
          .map((c, index) => {
            const tempoOriginal = Number(c.tempo_chegada_minutos);
            const tempo = Number.isFinite(tempoOriginal) ? tempoOriginal : 0;
            return {
              ...c,
              id_sentido: sentidoVolta,
              descricao_sentido: sentidoVoltaLabel,
              ordem_parada: index + 1,
              tempo_chegada_minutos: tempoMaximo - tempo,
            };
          });

        const cercasMantidas = todas.filter((c) => {
          if (!isOperacional(c)) return true;
          return !sentidosVolta.has(Number(c.id_sentido));
        });

        const ordered = enrichCercasWithCoords(cercasVolta);
        const hasDisplacement = lineHasDisplacement(this.rawRotas, sentidoIda);
        const planResult = await planOrderedFencesRoute(ordered, {
          serviceTime: 0,
          sentido: sentidoVolta,
          hasDisplacement,
        });
        if (!planResult || planResult.error || !planResult.geojson) {
          const rateLimited = /429|RATE_LIMIT|rate.?limit/i.test(
            String((planResult && planResult.message) || "")
          );
          EventBus.$emit("show-error", {
            error: {
              message: rateLimited
                ? this.$t("crudLinhas.map.vroomRateLimit")
                : (planResult && planResult.message) ||
                  this.$t("crudLinhas.map.stopDragRecalcError"),
            },
          });
          return;
        }

        const idLinha = this.linha.id_linha;
        const persisted = await persistLineFenceSequence({
          idLinha,
          sentido: sentidoVolta,
          mainRota: idaMainRota,
          otherRotas: (this.rawRotas || []).slice(),
          orderedCercas: ordered,
          preservedExtraCercas: cercasMantidas,
          planGeojson: planResult.geojson,
          planDistance: planResult.distance,
          planShape: planResult.shape,
          etasByCerca: planResult.etasByCerca,
          durationSec: planResult.durationSec,
          replaceRoute: true,
        });
        if (!persisted || !persisted.ok) {
          EventBus.$emit("show-error", {
            error: {
              message:
                (persisted && persisted.message) ||
                this.$t("crudLinhas.map.routeByFencesPersistError"),
            },
          });
          return;
        }

        this.cercasLinhaLocal = cercasMantidas.concat(cercasVolta);
        await this.carregarDados();
        this.$emit("cercas-changed");
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.returnFencesGenerated"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.savingCercas = false;
        this.roteirizandoPorCercas = false;
      }
    },
    async saveCercasLinha() {
      const cercasAntes = this.cercasLinhaLocal.map((item) => ({ ...item }));
      const wasEditing = this.editingCerca;
      const cercaAnterior = wasEditing
        ? cercasAntes.find(
          (item) =>
            Number(item.id_cerca) === Number(this.editingCercaId) &&
            Number(item.id_sentido) === Number(this.editingCercaSentido)
        ) || null
        : null;
      const historicoDetalhe = wasEditing
        ? {
          origem: "CRUD_LINHAS",
          acao: "EDITAR_CERCA",
          cerca_anterior: cercaAnterior ? { ...cercaAnterior } : null,
          cerca_nova: this.buildCercaPayload(),
          cercas_antes: cercasAntes,
        }
        : null;
      // Só redesenha o traçado quando uma cerca NOVA está sendo associada — edições de
      // ordem/tipo/tempo de uma associação já existente não precisam recalcular a rota.
      const redesenhar = !wasEditing && !!this.itemCerca.id_cerca;
      if (this.itemCerca.id_cerca) {
        const status = await this.addCercaAssociacao();
        if (status === "blocked") return;
        if (status === "pending") {
          this.persistCercaAfterDialog = true;
          this.pendingHistoricoCercaDetalhe = historicoDetalhe;
          this.pendingCercaRedesenhar = redesenhar;
          return;
        }
      }
      await this.persistCercasLinha(historicoDetalhe, { redesenhar });
    },
    async persistHistoricoCerca(detalhe, cercasDepois) {
      if (!detalhe) return;
      let user = null;
      try {
        user = JSON.parse(sessionStorage.getItem("user"));
      } catch (e) {
        user = null;
      }
      const cercasAntes = Array.isArray(detalhe.cercas_antes) ? detalhe.cercas_antes : [];
      const destinationIds = [...cercasAntes, ...(cercasDepois || [])]
        .filter((c) => c && (Number(c.id_tipo_cerca) === 2 || Number(c.id_tipo_cerca) === 3))
        .map((c) => c.id_cerca);
      const cercaAtual = detalhe.acao === "EDITAR_CERCA"
        ? detalhe.cerca_nova || null
        : null;
      const result = await persistHistoricoRoteirizacaoCrudEvent({
        idSessaoOrigem: this.historicoSessaoOrigemId,
        idSessaoCrud: this.historicoSessaoId,
        idCercaDestinoCandidates: destinationIds,
        idUsuario: user && user.id_usuario,
        detalhe: {
          ...detalhe,
          id_linha: this.linha && this.linha.id_linha,
          cod_linha: this.linha && this.linha.cod_linha,
          nome_linha: this.linha && this.linha.nome_linha,
          cerca_atual: cercaAtual,
          cercas_depois: (cercasDepois || []).map((c) => ({ ...c })),
        },
      });
      if (!result || !result.ok) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudLinhas.messages.historySaveError"),
          tipo: "warning",
        });
      }
    },
    /** @returns {Promise<boolean>} true quando as cercas foram gravadas. */
    async persistCercasLinha(historicoDetalhe = null, { redesenhar = false, silentSuccess = false } = {}) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return false;
      if (hasTerminusOrderViolation(this.cercasLinhaLocal, this.terminusPinOptions)) {
        this.dialogTerminusOrder = true;
        return false;
      }
      this.savingCercas = true;
      try {
        await apiController.request(apiController.endpoints.deleteCercasbylinha, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (this.cercasLinhaLocal.length) {
          const rows = this.cercasLinhaLocal.map((c) => [
            idLinha,
            c.id_cerca,
            c.id_tipo_cerca,
            c.show_sinotico ? 1 : 0,
            c.tempo_chegada_minutos || 0,
            c.id_sentido,
            c.ordem_parada || 0,
          ]);
          await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
            data: rows,
            silent: true,
          });
        }
        const cercasDepois = this.cercasLinhaLocal.map((c) => ({ ...c }));
        await this.persistHistoricoCerca(historicoDetalhe, cercasDepois);
        // Nova cerca associada à linha: redesenha o traçado via /plan mantendo a ordem
        // atual (o /solve completo só roda no fluxo dedicado de criação de cerca).
        if (redesenhar) {
          const sentido = this.sentidoSelecionado;
          if (sentido != null && sentido !== "") {
            await this.redesenharTracadoMantendoOrdem(Number(sentido));
          }
        }
        await this.carregarDados();
        this.$emit("cercas-changed");
        if (!silentSuccess) {
          EventBus.$emit("show-success", {
            message: this.$t("crudLinhas.map.fencesSaved"),
          });
        }
        return true;
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
        return false;
      } finally {
        this.savingCercas = false;
      }
    },
    descricaoSentido(idSentido) {
      const s = (this.sentidos || []).find(
        (item) => Number(item.id_sentido) === Number(idSentido)
      );
      return s ? s.descricao_sentido : null;
    },
    openMoveFenceDrawer() {
      const idOrigem = this.linha && this.linha.id_linha;
      if (idOrigem == null) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.routeByFencesNoLine") },
        });
        return;
      }
      if (this.linhasSelecionadasNoMapa.length < 2) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.moveFenceNeedTwoLinesOnMap") },
        });
        return;
      }
      this.moveFenceStep = 1;
      this.moveFenceSource = null;
      this.moveFenceSourceLinha = idOrigem;
      this.moveFenceTargetLinha = null;
      this.moveFenceTargetLabel = null;
      this.moveFenceSourceRows = [];
      this.moveFenceSourceRotas = [];
      this.moveFenceDestRows = [];
      this.moveFenceDestRotas = [];
      this.reorderFencesMode = false;
      this.drawerMoveFenceOpen = true;
      this.$nextTick(() => this.loadMoveFenceSourceData(idOrigem));
    },
    closeMoveFenceDrawer() {
      if (this.movendoCerca) return;
      this.drawerMoveFenceOpen = false;
      this.moveFenceStep = 1;
    },
    async selectMoveFenceOriginLinha(idLinha) {
      if (Number(this.moveFenceSourceLinha) === Number(idLinha)) return;
      this.moveFenceSourceLinha = idLinha;
      this.moveFenceSource = null;
      if (Number(this.moveFenceTargetLinha) === Number(idLinha)) {
        this.moveFenceTargetLinha = null;
        this.moveFenceTargetLabel = null;
        this.moveFenceDestRows = [];
        this.moveFenceDestRotas = [];
      }
      await this.loadMoveFenceSourceData(idLinha);
    },
    selectMoveFenceCerca(cerca) {
      this.moveFenceSource = cerca ? { ...cerca } : null;
      if (cerca) {
        this.focusCercaGeometry(cerca);
      }
    },
    isMoveFenceSourceSelected(cerca) {
      const source = this.moveFenceSource;
      if (!source || !cerca) return false;
      return (
        Number(source.id_cerca) === Number(cerca.id_cerca) &&
        Number(source.id_sentido) === Number(cerca.id_sentido) &&
        Number(source.id_tipo_cerca) === Number(cerca.id_tipo_cerca)
      );
    },
    async selectMoveFenceDestLinha(idLinha, label = null) {
      if (idLinha != null && Number(idLinha) === Number(this.moveFenceSourceLinha)) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.moveFenceSameLine") },
        });
        return;
      }
      this.moveFenceTargetLinha = idLinha;
      this.moveFenceTargetLabel = label;
      if (idLinha != null) {
        this.focusLinhaAssociadaOnMap(idLinha);
      }
      await this.loadMoveFenceDestData(idLinha);
    },
    moveFenceGoBack() {
      if (this.moveFenceStep > 1) this.moveFenceStep -= 1;
    },
    moveFenceGoNext() {
      if (!this.canAdvanceMoveFenceStep) return;
      if (this.moveFenceStep < 3) this.moveFenceStep += 1;
    },
    async loadMoveFenceSourceData(idLinha) {
      if (idLinha == null || idLinha === "") {
        this.moveFenceSourceRows = [];
        this.moveFenceSourceRotas = [];
        return;
      }
      this.moveFenceLoadingSource = true;
      try {
        const idNum = Number(idLinha);
        if (
          Number.isFinite(idNum) &&
          this.linha &&
          Number(this.linha.id_linha) === idNum
        ) {
          this.moveFenceSourceRows = (this.cercasLinhaLocal || []).slice();
          this.moveFenceSourceRotas = (this.rawRotas || []).slice();
          return;
        }
        const [cercasRes, rotasRes] = await Promise.all([
          apiController.request(apiController.endpoints.getCercasporlinha, {
            data: [idLinha],
            silent: true,
          }),
          apiController.request(apiController.endpoints.getRotasporlinha, {
            data: [idLinha],
            silent: true,
          }),
        ]);
        this.moveFenceSourceRows = Array.isArray(cercasRes.data) ? cercasRes.data : [];
        this.moveFenceSourceRotas = Array.isArray(rotasRes.data) ? rotasRes.data : [];
      } catch (e) {
        this.moveFenceSourceRows = [];
        this.moveFenceSourceRotas = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.moveFenceLoadingSource = false;
      }
    },
    async loadMoveFenceDestData(idLinha) {
      this.moveFenceDestRows = [];
      this.moveFenceDestRotas = [];
      if (idLinha == null || idLinha === "") return;
      try {
        const [cercasRes, rotasRes] = await Promise.all([
          apiController.request(apiController.endpoints.getCercasporlinha, {
            data: [idLinha],
            silent: true,
          }),
          apiController.request(apiController.endpoints.getRotasporlinha, {
            data: [idLinha],
            silent: true,
          }),
        ]);
        this.moveFenceDestRows = Array.isArray(cercasRes.data) ? cercasRes.data : [];
        this.moveFenceDestRotas = Array.isArray(rotasRes.data) ? rotasRes.data : [];
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    /**
     * Move a cerca da linha atual para a linha destino.
     *
     * Não há endpoint de delete por vínculo individual em linhas_cercas: o
     * padrão é apagar o conjunto da linha e reinserir. Escreve o DESTINO
     * primeiro de propósito — uma falha no meio deixa a cerca nas duas linhas
     * (recuperável repetindo), nunca em nenhuma.
     */
    async confirmMoveFence() {
      const idOrigem =
        this.moveFenceSourceLinha != null && this.moveFenceSourceLinha !== ""
          ? this.moveFenceSourceLinha
          : this.linha && this.linha.id_linha;
      const source = this.moveFenceSource;
      const idDest = this.moveFenceTargetLinha;
      if (!idOrigem || !source || idDest == null || idDest === "") {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.moveFenceRequired") },
        });
        return;
      }
      if (Number(idDest) === Number(idOrigem)) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.moveFenceSameLine") },
        });
        return;
      }
      if (isPinnedTerminusFence(source, {
        pinStart: lineHasDisplacement(this.moveFenceSourceRotas, source.id_sentido),
      })) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.moveFenceTerminusBlocked") },
        });
        return;
      }

      const mesmoVinculo = (a, b) =>
        Number(a.id_cerca) === Number(b.id_cerca) &&
        Number(a.id_sentido) === Number(b.id_sentido) &&
        Number(a.id_tipo_cerca) === Number(b.id_tipo_cerca);

      if (
        (this.moveFenceDestRows || []).some((c) =>
          mesmoVinculo(c, { ...source, id_tipo_cerca: FENCE_TYPE.STOP })
        )
      ) {
        EventBus.$emit("show-error", {
          error: { message: this.$t("crudLinhas.map.fenceAlreadyAssociated") },
        });
        return;
      }

      const cercasAntes = (this.moveFenceSourceRows || []).map((c) => ({ ...c }));
      const origemNext = (this.moveFenceSourceRows || []).filter((c) => !mesmoVinculo(c, source));
      const destPinOpts = {
        pinStart: lineHasDisplacement(this.moveFenceDestRotas, source.id_sentido),
      };
      // Não usar max(ordem)+1: isso coloca a cerca depois do ponto final e dispara
      // dialogTerminusOrder. Insere antes do terminal pinado; o /solve reordena depois.
      const { list: destNext, inserted: destino } = insertFencePreservingTerminusOrder(
        this.moveFenceDestRows,
        source,
        destPinOpts
      );

      if (
        hasTerminusOrderViolation(origemNext, {
          pinStart: lineHasDisplacement(this.moveFenceSourceRotas, source.id_sentido),
        }) ||
        hasTerminusOrderViolation(destNext, destPinOpts)
      ) {
        this.dialogTerminusOrder = true;
        return;
      }

      const toRow = (c, idLinha) => [
        idLinha,
        c.id_cerca,
        c.id_tipo_cerca,
        c.show_sinotico ? 1 : 0,
        c.tempo_chegada_minutos || 0,
        c.id_sentido,
        c.ordem_parada || 0,
      ];
      const regravarLinha = async (idLinha, rows) => {
        await apiController.request(apiController.endpoints.deleteCercasbylinha, {
          data: { id_linha: idLinha },
          silent: true,
        });
        if (rows.length) {
          await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
            data: rows.map((c) => toRow(c, idLinha)),
            silent: true,
          });
        }
      };

      this.movendoCerca = true;
      try {
        await regravarLinha(idDest, destNext);
        await regravarLinha(idOrigem, origemNext);

        if (
          this.linha &&
          Number(this.linha.id_linha) === Number(idOrigem)
        ) {
          this.cercasLinhaLocal = origemNext;
        }
        await this.persistHistoricoCerca(
          {
            origem: "CRUD_LINHAS",
            acao: "MOVER_CERCA_ENTRE_LINHAS",
            id_linha_destino: idDest,
            cerca_anterior: { ...source },
            cerca_nova: { ...destino },
            cercas_antes: cercasAntes,
          },
          origemNext
        );

        await roteirizarLinhaPorCercas({ idLinha: idOrigem, sentido: source.id_sentido });
        await roteirizarLinhaPorCercas({ idLinha: idDest, sentido: destino.id_sentido });

        this.closeMoveFenceDrawer();
        await this.carregarDados();
        await this.syncAdditionalLinhas();
        this.$emit("cercas-changed");
        this.$emit("rotas-changed");
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.map.moveFenceSuccess"),
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.movendoCerca = false;
      }
    },
    onPassageiroMarkerClick(pax) {
      this.$emit("passenger-click", pax);
    },
  },
};
</script>

<style scoped>
.linha-map-editor {
  height: 100%;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
}
.linha-map-editor__toolbar {
  max-width: 100%;
  margin: 0 !important;
}
.linha-map-editor__toolbar >>> .v-input--checkbox .v-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.linha-map-editor__body {
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  position: relative;
}
.linha-map-editor__drawer {
  z-index: 1200;
}
.linha-map-editor__drawer--list >>> .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.linha-map-editor__drawer-inner {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.linha-map-editor__drawer-toolbar {
  flex-shrink: 0;
}
.linha-map-editor__drawer-pager {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.linha-map-editor__drawer-list {
  overflow-y: auto;
  min-height: 0;
}
.linha-map-editor__cercas-reorder-table {
  background: transparent;
}
.linha-map-editor__drawer-manage {
  flex-shrink: 0;
  border-top: 1px solid #e0e0e0;
  max-height: 45%;
  overflow-y: auto;
}
.linha-map-editor__drawer--move-fence >>> .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.linha-map-editor__move-fence-stepper {
  min-height: 0;
  overflow: hidden;
  background: transparent !important;
}
.linha-map-editor__move-fence-stepper >>> .v-stepper__header {
  box-shadow: none;
}
.linha-map-editor__move-fence-stepper-items {
  overflow-y: auto;
  min-height: 0;
}
.linha-map-editor__move-fence-list .v-list-item {
  cursor: pointer;
  border-radius: 4px;
}
.linha-map-editor__move-fence-list .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04);
}
.linha-map-editor__drawer-footer {
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}
.linha-map-editor__map {
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  position: relative;
}
.linha-map-editor__map--picking >>> .leaflet-container {
  cursor: crosshair;
}
.linha-map-editor__side-panel {
  width: 280px;
  max-width: 40%;
  flex: 0 0 auto;
  align-self: stretch;
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
}
.linha-map-editor__fence-form-anchor {
  scroll-margin-top: 72px;
}
.linha-map-editor__preview-editbar {
  position: absolute;
  z-index: 1100;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 6px 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  max-width: calc(100% - 24px);
  gap: 2px;
}
.linha-map-editor__fence-format-select {
  max-width: 200px;
  min-width: 140px;
  flex: 0 1 200px;
}
.linha-map-editor__fence-format-select >>> .v-input__slot {
  min-height: 28px !important;
}
.linha-map-editor__fence-format-select >>> .v-input__control {
  min-height: 28px;
}
.linha-map-editor__fence-format-panel {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff8e1;
}
.linha-map-editor__fence-item--selected {
  background: rgba(255, 193, 7, 0.18);
}
.linha-map-editor__preview-dialog-card {
  height: 100%;
}
.linha-map-editor__preview-dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
}
.linha-map-editor__preview-dialog-map {
  flex: 1 1 480px;
  min-width: 320px;
  min-height: 320px;
}
.linha-map-editor__direction-fences {
  max-height: 190px;
  overflow-y: auto;
}
.linha-map-editor__preview-cards {
  gap: 8px;
}
.linha-map-editor__preview-card {
  flex: 1 1 250px;
  min-width: 0;
}
.linha-map-editor__preview-dialog-side {
  flex: 0 0 560px;
  max-width: 100%;
  overflow-y: auto;
  border-left: 1px solid #e0e0e0;
}
.linha-map-editor__preview-summary >>> td {
  height: 24px;
  padding: 0 8px;
}
.linha-map-editor__preview-table >>> th,
.linha-map-editor__preview-table >>> td {
  height: 26px;
  padding: 0 6px;
}
.linha-map-editor__preview-row--changed {
  background-color: #fff8e1;
}
.linha-map-editor__legend {
  display: inline-block;
  width: 18px;
  height: 0;
  vertical-align: middle;
  margin-right: 6px;
}
.linha-map-editor__legend--atual {
  border-top: 3px dashed #1565c0;
}
.linha-map-editor__legend--nova {
  border-top: 4px solid #2e7d32;
}
</style>

<style>
.text-overlay {
  background: #fff;
  padding: 0 0.5em;
  border: 1px solid #aaa;
  border-radius: 0.1em;
}

.linha-map-cerca-pin,
.linha-map-pax-pin {
  background: transparent;
  border: none;
}
</style>
